import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { fmtMonto } from 'shared/format';
import {
  FiGrid,
  FiShoppingCart,
  FiList,
  FiClock,
  FiSearch,
  FiPlus,
  FiMinus,
  FiTrash2,
  FiX,
  FiCamera,
  FiMapPin,
  FiCreditCard,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiPackage,
  FiEye,
  FiImage,
  FiEdit2,
  FiXCircle,
  FiSave,
  FiRefreshCw,
  FiRotateCw,
  FiUser,
  FiHash,
  FiPhone,
  FiDollarSign,
  FiCalendar,
  FiAlertCircle,
  FiChevronRight,
  FiArrowRight,
  FiCoffee,
  FiCheckCircle,
  FiAlertTriangle,
  FiSend,
  FiZap,
  FiLock,
  FiDroplet,
  FiActivity,
  FiHome,
  FiHeart,
  FiShoppingBag,
  FiLayers,
  FiAward,
  FiStar,
  FiTool,
  FiFeather,
  FiShield,
  FiSmartphone,
  FiBook,
  FiGift,
  FiTrendingUp,
  FiSun,
  FiClipboard,
  FiPrinter,
} from 'react-icons/fi';
import api from '../shared/api';
import { getImagenUrl, getSchemaName } from '../shared/api';
import { empresaUrl } from '../shared/config';
import socket from '../shared/socket';
import { cacheGet, cacheSet, invalidate } from '../shared/cache';
import { useResponsive } from '../shared/hooks';
import { BottomSheet, CartPill, EmptyState, Badge } from '../shared/components/ui';
import HistorialFiltros from '../components/Trabajador/HistorialFiltros';
import TicketImprimir from '../components/Tienda/TicketImprimir';
import { useAuth } from '../shared/context/AuthContext';
import { useNotificaciones } from '../shared/context/NotificacionesContext';
import { useDarkMode } from '../shared/context/DarkModeContext';
import { toDataURL } from 'qrcode';
import BotonEscanear from '../shared/components/BotonEscanear';

const API_URL = process.env.REACT_APP_API_URL || '';

function usePTDColors() {
  const { COL: G, darkMode, toggleDarkMode } = useDarkMode();
  const COL = {
    ...G,
    orange: darkMode ? '#fb923c' : '#ff6d00',
    tagPurpleBg: '#ede9fe',
    tagPurpleText: '#7c3aed',
    tagOrangeBg: '#fff7ed',
    tagOrangeText: '#c2410c',
    canvasDot: darkMode ? '#475569' : '#c8cad0',
  };
  return { COL, darkMode, toggleDarkMode };
}

const formasOptions = [
  { id: 'cuadrado', label: 'Cuadrada' },
  { id: 'redonda', label: 'Redonda' },
  { id: 'rectangular', label: 'Rectang.' },
];

// Paleta de colores para personalizar cada mesa del salon.
const MESA_PALETTE_COLORES = [
  '#2563eb', '#059669', '#d97706', '#7c3aed', '#0891b2',
  '#db2777', '#ea580c', '#4f46e5', '#16a34a', '#dc2626',
  '#0d9488', '#9333ea', '#ca8a04', '#475569', '#0ea5e9',
];

function ShapeIcon({ forma, size, color }) {
  const s = size || 24;
  const c = color || '#94a3b8';
  if (forma === 'redonda') {
    return (
      <svg width={s} height={s} viewBox="0 0 40 40">
        <rect x={4} y={4} width={32} height={32} rx={16} fill="none" stroke={c} strokeWidth={2.5} />
        <circle cx={20} cy={20} r={3} fill={c} opacity={0.3} />
      </svg>
    );
  }
  if (forma === 'rectangular') {
    return (
      <svg width={s} height={s} viewBox="0 0 40 40">
        <rect x={3} y={6} width={34} height={28} rx={4} fill="none" stroke={c} strokeWidth={2.5} />
        <circle cx={20} cy={20} r={3} fill={c} opacity={0.3} />
      </svg>
    );
  }
  return (
    <svg width={s} height={s} viewBox="0 0 40 40">
      <rect x={5} y={5} width={30} height={30} rx={4} fill="none" stroke={c} strokeWidth={2.5} />
      <circle cx={20} cy={20} r={3} fill={c} opacity={0.3} />
    </svg>
  );
}

// ─── Icono + color por departamento (keyword match, con fallback) ───
const DEPT_ICONS = [
  { kw: ['bebida', 'refresco', 'jugo', 'gaseosa', 'agua', 'licor', 'cerveza', 'vino', 'malta', 'cafe', 'tinto', 'coctel', 'beb'], icon: FiDroplet, color: '#0ea5e9' },
  { kw: ['hamburguesa', 'comida', 'plato', 'comida rapida', 'almuerzo', 'cena', 'parrilla', 'pizza', 'perro', 'perra', 'frito', 'frita', 'sopa', 'pollo', 'carne', 'pescado', 'marisco', 'pasta'], icon: FiActivity, color: '#ef4444' },
  { kw: ['postre', 'dulce', 'helado', 'torta', 'pastel', 'chocolate', 'galleta', 'brownie', 'fl', 'flan'], icon: FiGift, color: '#ec4899' },
  { kw: ['ensalada', 'vegetal', 'verdura', 'fruta', 'saludable', 'fit', 'ligero'], icon: FiFeather, color: '#22c55e' },
  { kw: ['desayuno', 'arepa', 'empanada', 'cachapa', 'tostada', 'huevo'], icon: FiSun, color: '#f59e0b' },
  { kw: ['snack', 'pasaboca', 'papa', 'tequeno', 'picada', 'entrada', 'acomp'], icon: FiShoppingBag, color: '#a855f7' },
  { kw: ['salsa', 'aderezo', 'adicional', 'extra', 'topping'], icon: FiLayers, color: '#64748b' },
  { kw: ['reposteria', 'pan', 'panaderia', 'pasteleria', 'harina'], icon: FiStar, color: '#d97706' },
  { kw: ['combos', 'combo', 'promocion', 'oferta', 'pack'], icon: FiAward, color: '#7c3aed' },
  { kw: ['hogar', 'limpieza', 'aseo', 'detergente', 'jabon'], icon: FiHome, color: '#14b8a6' },
  { kw: ['belleza', 'cosmetico', 'higiene', 'personal', 'shampoo', 'crema'], icon: FiHeart, color: '#f43f5e' },
  { kw: ['farmacia', 'medicina', 'salud', 'medicamento'], icon: FiShield, color: '#0891b2' },
  { kw: ['electronica', 'tecnologia', 'celular', 'accesorio', 'cargador'], icon: FiSmartphone, color: '#2563eb' },
  { kw: ['ropa', 'vestir', 'calzado', 'textil', 'moda'], icon: FiTrendingUp, color: '#db2777' },
  { kw: ['ferreteria', 'herramienta', 'construccion', 'reparacion'], icon: FiTool, color: '#78716c' },
  { kw: ['oficina', 'papeleria', 'escolar', 'libro', 'utiles'], icon: FiBook, color: '#0284c7' },
];

const DEPT_COLORS = [
  '#ef4444', '#f59e0b', '#22c55e', '#0ea5e9', '#7c3aed',
  '#ec4899', '#14b8a6', '#a855f7', '#d97706', '#2563eb',
];

function getDeptMeta(nombre) {
  const n = (nombre || '').toLowerCase();
  for (const d of DEPT_ICONS) {
    if (d.kw.some((k) => n.includes(k))) return { icon: d.icon, color: d.color };
  }
  let h = 0;
  for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0;
  return { icon: FiPackage, color: DEPT_COLORS[h % DEPT_COLORS.length] };
}

const fmt = (n) => {
  if (n == null || isNaN(n)) return '$0.00';
  const v = parseFloat(n);
  return '$' + fmtMonto(v);
};

// Navegación por teclado dentro de modales (personalización, departamentos,
// cobro). Las flechas mueven el foco entre botones/campos, Enter/Espacio activan
// (comportamiento nativo de <button>), Escape cierra el modal. Los campos de texto
// (input/select/textarea) quedan excluidos para no robar el foco mientras se escribe.
function useRovingModal(containerRef, onEscape, active) {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isField = ['input', 'textarea', 'select'].includes(tag);
      if (e.key === 'Escape' && !isField) {
        e.preventDefault();
        e.stopPropagation();
        onEscapeRef.current?.();
        return;
      }
      if (isField) return;
      if (e.key === 'Tab') return;
      const dir =
        e.key === 'ArrowDown' || e.key === 'ArrowRight'
          ? 1
          : e.key === 'ArrowUp' || e.key === 'ArrowLeft'
            ? -1
            : 0;
      if (!dir) return;
      e.preventDefault();
      e.stopPropagation();
      const items = Array.from(
        containerRef.current?.querySelectorAll?.(
          'button, input:not([type="hidden"]), select, textarea'
        ) || []
      ).filter((el) => !el.disabled && el.offsetParent !== null);
      if (!items.length) return;
      let idx = items.indexOf(e.target);
      if (idx < 0) idx = 0;
      const next = items[(idx + dir + items.length) % items.length];
      next?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, containerRef]);
}

// Leyenda de atajos de teclado del POS (module-scope, estable).
const TECLAS = [
  ['← → ↑ ↓', 'Mover selección'],
  ['Enter', 'Agregar / aplicar'],
  ['D', 'Departamentos'],
  ['F o /', 'Buscar'],
  ['C', 'Ir al carrito'],
  ['+ / -', 'Cantidad ±1'],
  ['Número + Enter', 'Cantidad directa'],
  ['Supr', 'Eliminar del carrito'],
  ['P', 'Cobrar'],
  ['← → en ventanas', 'Navegar opciones'],
  ['Enter en ventana', 'Seleccionar opción'],
  ['Esc', 'Cerrar / volver'],
  ['?', 'Ver atajos'],
];
function TeclasLista({ COL }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {TECLAS.map(([tecla, desc]) => (
        <div key={tecla} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
          <span
            style={{
              fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: COL.pri2,
              background: COL.blueBg, border: `1px solid ${COL.border}`,
              padding: '2px 8px', borderRadius: 6, minWidth: 64, textAlign: 'center',
            }}
          >
            {tecla}
          </span>
          <span style={{ color: COL.text }}>{desc}</span>
        </div>
      ))}
    </div>
  );
}

const timeAgo = (d) => {
  if (!d) return '';
  const min = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (min < 1) return 'Ahora';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const formatearFecha = (f, full) => {
  if (!f) return '-';
  const d = new Date(f);
  return full
    ? d.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
};

const MONEDAS_BS = ['VES', 'VEF', 'BS', 'BSF', 'BSS'];
const tasaBsDe = (pd) => {
  // Tasa Bs/USD congelada al cobrar. Fuentes (en orden):
  //   1. Columna `tasa_cambio` del pedido (nueva, se congela al cobrar).
  //   2. Fallback para historicos: la tasa del pago en Bs (congelada en `pagos`).
  const t = parseFloat(pd?.tasa_cambio);
  if (t > 0) return t;
  for (const p of (Array.isArray(pd?.pagos) ? pd.pagos : [])) {
    if (MONEDAS_BS.includes(String(p?.moneda_codigo || '').toUpperCase())) {
      const tt = parseFloat(p?.tasa);
      if (tt > 0) return tt;
    }
  }
  return 0;
};

// ═══════════ COMPONENTE PRINCIPAL ═══════════
export default function PedidosTienda() {
  const { isMobile } = useResponsive();
  const { isAdmin, tp } = useAuth();
  const { COL, darkMode } = usePTDColors();
  const [config, setConfig] = useState(null);
  const [vista, setVista] = useState(null);
  const [sede, setSede] = useState('');
  const [sedes, setSedes] = useState([]);
  const [cargarPedidoDesdeEspera, setCargarPedidoDesdeEspera] = useState(null);
  const [abrirPagoDirecto, setAbrirPagoDirecto] = useState(null);
  const [mesaActiva, setMesaActiva] = useState(null);
  const [pedidoAbierto, setPedidoAbierto] = useState(null);
  const [ticket, setTicket] = useState(null);

  const abrirTicket = (recibo, modo, autoPrint = false) =>
    setTicket({ recibo, modo, autoPrint, key: Date.now() });

  useEffect(() => {
    api
      .get('/pedidos/tienda/config')
      .then((r) => setConfig(r.data?.data || {}))
      .catch((e) => console.error('Error cargando config tienda:', e));
    const cachedSedes = cacheGet('sedes:listar');
    if (cachedSedes) {
      setSedes(cachedSedes);
      if (cachedSedes.length) setSede(cachedSedes[0].codigo);
    }
    api
      .get('/sedes/listar')
      .then((r) => {
        const d = r.data?.data || [];
        setSedes(d);
        cacheSet('sedes:listar', d, 0);
        if (d.length && !cachedSedes) setSede(d[0].codigo);
      })
      .catch((e) => console.error('Error cargando sedes:', e));
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = darkMode ? COL.bg : '#f8fafc';
    document.body.style.color = darkMode ? COL.text : '#1e293b';
  }, [darkMode, COL]);

  const tieneHistorialRef = useRef(false);
  tieneHistorialRef.current = isAdmin() || tp('pedidos_tienda.historial_ver');

  useEffect(() => {
    try {
      const raw = localStorage.getItem('agorax_pedido_abrir');
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data && data.vista === 'tienda' && data.id && tieneHistorialRef.current) {
        setVista('historial');
      }
    } catch {}
  }, []);

  if (!config || !sede) return <SkeletonLoader />;

  const p = config?.permisos || {};
  const tieneRestaurante = config?.tiene_restaurante;
  const admin = isAdmin();
  const has = (perm) => admin || !!p[perm];

  const todosLosTabs = [];
  if (tieneRestaurante && (admin || tp('pedidos_tienda.salon_ver')))
    todosLosTabs.push({ id: 'salon', icon: <FiGrid size={18} />, label: 'Salon' });
  if (admin || tp('pedidos_tienda.pedido_ver'))
    todosLosTabs.push({ id: 'pedido', icon: <FiShoppingCart size={18} />, label: 'Pedido' });
  if (admin || tp('pedidos_tienda.espera_ver'))
    todosLosTabs.push({ id: 'espera', icon: <FiList size={18} />, label: 'En Espera' });
  if (admin || tp('pedidos_tienda.historial_ver'))
    todosLosTabs.push({ id: 'historial', icon: <FiClock size={18} />, label: 'Historial' });

  if (!vista && todosLosTabs.length > 0) setVista(todosLosTabs[0].id);
  if (todosLosTabs.length === 0)
    return (
      <div style={{ padding: 40, textAlign: 'center', color: COL.muted }}>
        Sin accesos disponibles
      </div>
    );
  if (!vista) return null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: COL.bg }}>
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <VistaRenderer
          vista={vista}
          setVista={setVista}
          sede={sede}
          config={config}
          p={p}
          has={has}
          tieneRestaurante={tieneRestaurante}
          isMobile={isMobile}
          cargarPedidoDesdeEspera={cargarPedidoDesdeEspera}
          setCargarPedidoDesdeEspera={setCargarPedidoDesdeEspera}
          abrirPagoDirecto={abrirPagoDirecto}
          setAbrirPagoDirecto={setAbrirPagoDirecto}
          mesaActiva={mesaActiva}
          setMesaActiva={setMesaActiva}
          pedidoAbierto={pedidoAbierto}
          setPedidoAbierto={setPedidoAbierto}
          abrirTicket={abrirTicket}
        />
      </div>
      <TabBar
        tabs={todosLosTabs}
        vista={vista}
        setVista={setVista}
        isMobile={isMobile}
        sede={sede}
        sedes={sedes}
        setSede={setSede}
      />
      {ticket && (
        <TicketImprimir
          key={ticket.key}
          recibo={ticket.recibo}
          modo={ticket.modo}
          autoPrint={ticket.autoPrint}
          onClose={() => setTicket(null)}
        />
      )}
    </div>
  );
}

function TabBar({ tabs, vista, setVista, isMobile, sede, sedes, setSede }) {
  const { COL } = usePTDColors();
  if (isMobile) {
    return (
      <div
        style={{
          display: 'flex',
          background: COL.white,
          borderTop: `1px solid ${COL.border}`,
          flexShrink: 0,
          boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}
      >
        {tabs.map((t) => {
          const active = vista === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setVista(t.id)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                padding: '10px 4px 8px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: active ? COL.pri2 : COL.faint,
                position: 'relative',
                transition: 'color 0.2s',
              }}
            >
              {active && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '25%',
                    right: '25%',
                    height: 3,
                    borderRadius: '0 0 3px 3px',
                    background: COL.pri2,
                  }}
                />
              )}
              {t.icon}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: active ? 700 : 500,
                  letterSpacing: active ? 0 : undefined,
                }}
              >
                {t.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        background: COL.white,
        borderTop: `1px solid ${COL.border}`,
        flexShrink: 0,
        padding: '0 16px',
      }}
    >
      <div style={{ display: 'flex', gap: 4, flex: 1 }}>
        {tabs.map((t) => {
          const active = vista === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setVista(t.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '14px 18px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                color: active ? COL.pri2 : COL.faint,
                fontFamily: 'inherit',
                borderBottom: active ? `2px solid ${COL.pri2}` : '2px solid transparent',
                background: 'transparent',
                transition: 'all 0.15s',
              }}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
      </div>
      {sedes.length > 1 && (
        <select
          value={sede}
          onChange={(e) => setSede(e.target.value)}
          style={{
            padding: '7px 12px',
            borderRadius: 10,
            border: `1.5px solid ${COL.border}`,
            fontSize: 12,
            fontWeight: 500,
            background: COL.bg,
            color: COL.text,
            fontFamily: 'inherit',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          {sedes.map((s) => (
            <option key={s.codigo} value={s.codigo}>
              {s.nombre}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// ═══════════ LOADING ═══════════
function ComboNotaInput({ onAgregar }) {
  const { COL } = usePTDColors();
  const [valor, setValor] = useState('');
  const agregar = () => {
    const nom = valor.trim();
    if (!nom) return;
    onAgregar(nom);
    setValor('');
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }}
        placeholder="Nota (ej: sin cebolla)"
        maxLength={80}
        style={{
          flex: 1,
          padding: '7px 10px',
          borderRadius: 10,
          border: `1.5px solid ${COL.border}`,
          fontSize: 12,
          fontFamily: 'inherit',
          color: COL.text,
          outline: 'none',
          background: COL.white,
        }}
      />
      <button
        onClick={agregar}
        disabled={!valor.trim()}
        style={{
          padding: '0 12px',
          borderRadius: 10,
          border: 'none',
          background: COL.pri2,
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          cursor: valor.trim() ? 'pointer' : 'default',
          opacity: valor.trim() ? 1 : 0.5,
          fontFamily: 'inherit',
        }}
      >
        Agregar
      </button>
    </div>
  );
}

function SkeletonLoader() {
  const { COL } = usePTDColors();
  return (
    <div
      style={{
        padding: 40,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: 200,
          height: 14,
          borderRadius: 7,
          background: COL.border,
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div
        style={{
          width: 280,
          height: 10,
          borderRadius: 5,
          background: COL.bg2,
          animation: 'pulse 1.5s ease-in-out infinite 0.2s',
        }}
      />
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 20px',
        textAlign: 'center',
      }}
    >
      <FiAlertCircle size={40} style={{ opacity: 0.3, marginBottom: 12, color: '#ea4335' }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 4 }}>
        Error al cargar
      </div>
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 16, maxWidth: 280 }}>
        {message || 'No se pudieron cargar los datos.'}
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: '9px 22px',
            borderRadius: 10,
            border: 'none',
            background: '#2952CC',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <FiRefreshCw size={14} /> Reintentar
        </button>
      )}
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, inputLabel, inputPlaceholder }) {
  const [valor, setValor] = useState('');
  const requiereInput = !!inputLabel || !!inputPlaceholder;
  const confirmar = () => {
    if (requiereInput && !valor.trim()) return;
    onConfirm(requiereInput ? valor.trim() : valor);
  };
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(3px)',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: 340,
          maxWidth: '90vw',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ padding: '24px 22px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 6 }}>
            {title || 'Confirmar'}
          </div>
          {message && (
            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.4 }}>{message}</div>
          )}
        </div>
        {requiereInput && (
          <div style={{ padding: '0 22px 16px' }}>
            {inputLabel && (
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {inputLabel}
              </div>
            )}
            <textarea
              autoFocus
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder={inputPlaceholder || 'Escribe aquí...'}
              rows={2}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                padding: '8px 10px',
                fontSize: 13,
                fontFamily: 'inherit',
                color: '#1F2937',
                resize: 'vertical',
                outline: 'none',
              }}
            />
          </div>
        )}
        <div style={{ display: 'flex', borderTop: '1px solid #e8eaef' }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              background: 'transparent',
              color: '#6B7280',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={confirmar}
            disabled={requiereInput && !valor.trim()}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderLeft: '1px solid #e8eaef',
              background: 'transparent',
              color: '#ea4335',
              cursor: requiereInput && !valor.trim() ? 'not-allowed' : 'pointer',
              opacity: requiereInput && !valor.trim() ? 0.5 : 1,
              fontSize: 14,
              fontWeight: 700,
              fontFamily: 'inherit',
            }}
          >
            {confirmLabel || 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VistaRenderer(props) {
  switch (props.vista) {
    case 'salon':
      return <SalonMesas {...props} />;
    case 'pedido':
      return <NuevoPedido {...props} />;
    case 'espera':
      return <EnEspera {...props} />;
    case 'historial':
      return <HistorialPedidos {...props} />;
    default:
      return null;
  }
}

// ═══════════ SALON ═══════════
function QRMesaModal({ mesa, onClose, isMobile }) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const [qrUrl, setQrUrl] = useState(null);
  const slug = localStorage.getItem('agorax_empresa_slug') || '';

  const menuUrl = `${empresaUrl(slug)}/mesa/${mesa.token || ''}`;

  useEffect(() => {
    let activo = true;
    if (!mesa.token) {
      setQrUrl(null);
      return;
    }
    toDataURL(menuUrl, { width: 420, margin: 1, color: { dark: '#0B1A4A', light: '#FFFFFF' } })
      .then((url) => {
        if (activo) setQrUrl(url);
      })
      .catch(() => {
        if (activo) setQrUrl(null);
      });
    return () => {
      activo = false;
    };
  }, [menuUrl, mesa.token]);

  const imprimir = () => {
    const w = window.open('', '_blank', 'width=480,height=640');
    if (!w) {
      window.print();
      return;
    }
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>QR Mesa ${mesa.nombre || mesa.codigo}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:32px}
      h2{margin:0 0 4px}h3{margin:0 0 20px;color:#555;font-weight:400}
      img{width:320px;height:320px}p{margin-top:16px;color:#888;font-size:12px;word-break:break-all}</style>
      </head><body>
      <h2>Mesa ${mesa.nombre || mesa.codigo}</h2>
      <h3>Escanea para ver el men\u00fa y pedir</h3>
      ${qrUrl ? `<img src="${qrUrl}" alt="QR"/>` : '<p>No se pudo generar el c\u00f3digo QR</p>'}
      <p>${menuUrl}</p>
      <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const descargar = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = `qr-mesa-${mesa.codigo || mesa.id}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const content = (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 800, color: COL.text }}>
          QR Mesa {mesa.nombre || mesa.codigo}
        </div>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: COL.bg,
            borderRadius: 10,
            cursor: 'pointer',
            padding: 8,
            color: COL.muted,
          }}
        >
          <FiX size={18} />
        </button>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}
      >
        {qrUrl ? (
          <img
            src={qrUrl}
            alt="QR de la mesa"
            style={{ width: 240, height: 240, borderRadius: 12, border: `1px solid ${COL.border}` }}
          />
        ) : (
          <div
            style={{
              width: 240,
              height: 240,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COL.muted,
              background: COL.bg,
              borderRadius: 12,
              fontSize: 13,
            }}
          >
            {mesa.token ? 'Generando...' : 'No hay token para esta mesa'}
          </div>
        )}
        <div style={{ fontSize: 12, color: COL.muted, textAlign: 'center', wordBreak: 'break-all' }}>
          {menuUrl}
        </div>
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button onClick={imprimir} disabled={!qrUrl} style={{ ...S.btnFull(COL.pri2), flex: 1 }}>
            <FiEye size={16} /> Imprimir
          </button>
          <button onClick={descargar} disabled={!qrUrl} style={S.btnSm(COL.pri2)}>
            Descargar
          </button>
        </div>
      </div>
    </div>
  );

  if (isMobile)
    return (
      <BottomSheet open={true} onClose={onClose} title={`QR Mesa ${mesa.nombre || mesa.codigo}`} height="auto">
        {content}
      </BottomSheet>
    );
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: COL.overlay,
        zIndex: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(3px)',
      }}
    >
      <div
        style={{
          background: COL.surface,
          borderRadius: 20,
          width: 400,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 28,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {content}
      </div>
    </div>
  );
}

function SalonMesas({ sede, has, isMobile, setVista, setMesaActiva, setPedidoAbierto }) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canvasZoom, setCanvasZoom] = useState(isMobile ? 0.65 : 1);
  const [mesaSel, setMesaSel] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [rotatingId, setRotatingId] = useState(null);
  const [showCrear, setShowCrear] = useState(false);
  const [editingMesa, setEditingMesa] = useState(null);
  const [qrMesa, setQrMesa] = useState(null);
  const [error, setError] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const canvasRef = useRef(null);
  const dragMoved = useRef(false);
  const dragRef = useRef({ id: null, ofsX: 0, ofsY: 0 });
  const rotateRef = useRef({ id: null, cx: 0, cy: 0, startAngle: 0, startRot: 0 });
  const mesasRef = useRef([]);
  const zoomRef = useRef(canvasZoom);

  useEffect(() => {
    mesasRef.current = mesas;
  }, [mesas]);
  useEffect(() => {
    zoomRef.current = canvasZoom;
  }, [canvasZoom]);

  const cargar = useCallback((silencioso = false) => {
    if (!silencioso) {
      setLoading(true);
    }
    setError(false);
    const cacheKey = `mesas:${sede}`;
    api
      .get(`/mesas/?sede=${sede}`)
      .then((r) => {
        const d = r.data?.data || [];
        cacheSet(cacheKey, d);
        setMesas(d);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }, [sede]);
  useEffect(() => {
    // Reaccion inmediata: pinta el salon desde cache y revalida en background.
    const cacheKey = `mesas:${sede}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      setMesas(cached);
      setLoading(false);
    }
    cargar(cached ? true : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sede]);

  // Auto-recarga ante cambios: pedido creado/cobrado/anulado (cambia estado y
  // contador de la mesa) y operaciones de mesas hechas desde otra sesion.
  // Merge optimista: actualiza la mesa afectada al instante y revalida en
  // background (sin skeleton) para no parpadear el salon.
  const reloadTimerRef = useRef(null);
  useEffect(() => {
    const handler = (payload) => {
      const mesaId = payload?.mesa_id;
      const estado = payload?.estado;
      if (mesaId != null) {
        setMesas((prev) =>
          prev.map((x) => {
            if (x.id !== mesaId) return x;
            const upd = { ...x };
            if (estado === 'cobrado' || estado === 'anulado' || estado === 'creado') {
              // Recuenta pedidos_activos: sin datos exactos, revalida pronto.
              upd._dirty = true;
            }
            if (estado === 'cobrado' || estado === 'anulado') {
              upd.pedidos_activos = Math.max(0, (x.pedidos_activos || 0) - 1);
              if (upd.pedidos_activos === 0 && x.estado === 'ocupada') upd.estado = 'sucia';
            } else if (estado === 'creado') {
              upd.pedidos_activos = (x.pedidos_activos || 0) + 1;
              upd.estado = 'ocupada';
            }
            return upd;
          })
        );
      }
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = setTimeout(() => cargar(true), 400);
    };
    socket.on('pedido_actualizado', handler);
    socket.on('mesa_actualizada', () => {
      invalidate(`mesas:${sede}`);
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = setTimeout(() => cargar(true), 400);
    });
    return () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      socket.off('pedido_actualizado', handler);
      socket.off('mesa_actualizada');
    };
  }, [cargar, sede]);

  const cambiarEstado = async (m, estado) => {
    // Optimista: actualiza la mesa al instante (sin recargar ni skeleton).
    setMesas((prev) => prev.map((x) => (x.id === m.id ? { ...x, estado } : x)));
    setMesaSel(null);
    try {
      await api.put(`/mesas/${m.id}/estado`, { estado });
    } catch (e) {
      cargar(true);
    }
  };
  const guardarPosicion = async (id, x, y) => {
    try {
      await api.put(`/mesas/${id}`, { pos_x: x, pos_y: y });
    } catch (_) {}
  };
  const guardarRotacion = async (id, rot) => {
    try {
      await api.put(`/mesas/${id}`, { rotacion: rot });
    } catch (_) {}
  };
  const _startRotate = (m, clientX, clientY, el) => {
    const mesaEl = el && el.closest ? el.closest('[data-mesa]') : null;
    const rect = mesaEl ? mesaEl.getBoundingClientRect() : null;
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const startAngle = Math.atan2(clientY - cy, clientX - cx);
    rotateRef.current = { id: m.id, cx, cy, startAngle, startRot: Number(m.rotacion) || 0 };
    setRotatingId(m.id);
  };
  const eliminarMesa = async (id) => {
    setConfirm({
      title: 'Eliminar mesa',
      message: 'Se perdera la configuracion de esta mesa.',
      onConfirm: async () => {
        setConfirm(null);
        setMesas((prev) => prev.filter((x) => x.id !== id));
        setMesaSel(null);
        invalidate(`mesas:${sede}`);
        try {
          await api.delete(`/mesas/${id}`);
        } catch (e) {
          cargar(true);
        }
      },
    });
  };

  const onMouseDownMesa = useCallback(
    (e, m) => {
      if (!has('salon_gestionar')) return;
      e.preventDefault();
      e.stopPropagation();
      dragMoved.current = false;
      _startDrag(m, e.clientX, e.clientY);
    },
    [has]
  );

  const onTouchStartMesa = useCallback(
    (e, m) => {
      if (!has('salon_gestionar')) return;
      if (e.touches.length !== 1) return;
      dragMoved.current = false;
      _startDrag(m, e.touches[0].clientX, e.touches[0].clientY);
    },
    [has]
  );

  const _startDrag = (m, clientX, clientY) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const z = zoomRef.current;
    const mx = (clientX - rect.left) / z;
    const my = (clientY - rect.top) / z;
    dragRef.current = { id: m.id, ofsX: mx - (m.pos_x || 80), ofsY: my - (m.pos_y || 80) };
    setDraggingId(m.id);
  };

  useEffect(() => {
    if (!draggingId) return;
    const onMove = (clientX, clientY) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const z = zoomRef.current;
      const mx = (clientX - rect.left) / z;
      const my = (clientY - rect.top) / z;
      const dr = dragRef.current;
      const nx = Math.round(Math.max(0, mx - dr.ofsX) / 10) * 10;
      const ny = Math.round(Math.max(0, my - dr.ofsY) / 10) * 10;
      dragMoved.current = true;
      setMesas((prev) =>
        prev.map((m) => (m.id === draggingId ? { ...m, pos_x: nx, pos_y: ny } : m))
      );
    };
    const onMouseMove = (e) => onMove(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      if (e.touches.length === 1) onMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    const onUp = () => {
      if (dragMoved.current) {
        const mesa = mesasRef.current.find((m) => m.id === draggingId);
        if (mesa) guardarPosicion(mesa.id, mesa.pos_x || 80, mesa.pos_y || 80);
      }
      setDraggingId(null);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId]); // eslint-disable-line

  useEffect(() => {
    if (!rotatingId) return;
    const onMove = (clientX, clientY) => {
      const rr = rotateRef.current;
      const angle = Math.atan2(clientY - rr.cy, clientX - rr.cx);
      let deg = rr.startRot + ((angle - rr.startAngle) * 180) / Math.PI;
      deg = Math.round((((deg % 360) + 360) % 360) / 5) * 5;
      setMesas((prev) => prev.map((x) => (x.id === rotatingId ? { ...x, rotacion: deg } : x)));
    };
    const onMouseMove = (e) => onMove(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      if (e.touches.length === 1) onMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onUp = () => {
      const mesa = mesasRef.current.find((m) => m.id === rotatingId);
      if (mesa) guardarRotacion(mesa.id, Number(mesa.rotacion) || 0);
      setRotatingId(null);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [rotatingId]); // eslint-disable-line

  const counts = {
    libre: mesas.filter((m) => m.estado === 'libre').length,
    ocupada: mesas.filter((m) => m.estado === 'ocupada').length,
    sucia: mesas.filter((m) => m.estado === 'sucia').length,
  };
  const colorMap = { libre: COL.green, ocupada: COL.red, sucia: COL.amber };

  if (loading)
    return (
      <div style={{ padding: 60, display: 'flex', justifyContent: 'center' }}>
        <SkeletonLoader />
      </div>
    );
  if (error) return <ErrorBanner message="No se pudieron cargar las mesas." onRetry={cargar} />;
  if (mesas.length === 0 && !has('salon_gestionar'))
    return (
      <EmptyState
        icon={FiGrid}
        title="No hay mesas"
        description="Solicita al administrador que configure las mesas"
      />
    );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div
        style={{
          padding: '10px 16px',
          background: COL.white,
          borderBottom: `1px solid ${COL.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: COL.text }}>
            {mesas.length} Mesas
          </span>
          <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
            {Object.entries(counts).map(([k, v]) => (
              <span
                key={k}
                style={{ display: 'flex', alignItems: 'center', gap: 5, color: COL.muted }}
              >
                <span style={{ width: 9, height: 9, borderRadius: 3, background: colorMap[k] }} />
                {k.charAt(0).toUpperCase() + k.slice(1)} ({v})
              </span>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {has('salon_gestionar') && (
            <button
              onClick={() => setShowCrear(true)}
              style={{
                ...S.zoomBtn,
                color: COL.pri2,
                border: `1.5px solid ${COL.pri2}30`,
                background: COL.blueBg,
              }}
              title="Agregar mesa"
            >
              <FiPlus size={15} />
            </button>
          )}
          <button
            onClick={() => setCanvasZoom((z) => Math.max(0.4, +(z - 0.15).toFixed(2)))}
            style={S.zoomBtn}
          >
            −
          </button>
          <span
            style={{
              fontSize: 11,
              color: COL.muted,
              fontWeight: 600,
              minWidth: 32,
              textAlign: 'center',
            }}
          >
            {Math.round(canvasZoom * 100)}%
          </span>
          <button
            onClick={() => setCanvasZoom((z) => Math.min(2, +(z + 0.15).toFixed(2)))}
            style={S.zoomBtn}
          >
            +
          </button>
          <button onClick={cargar} style={{ ...S.zoomBtn, marginLeft: 4 }} title="Refrescar">
            <FiRefreshCw size={14} />
          </button>
        </div>
      </div>

      {mesas.length === 0 && has('salon_gestionar') && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            color: COL.muted,
            background: '#e8eaee',
          }}
        >
          <FiGrid size={48} style={{ opacity: 0.25 }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: COL.text }}>
              Sin mesas configuradas
            </div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              Agrega mesas para comenzar a gestionar pedidos
            </div>
          </div>
          <button
            onClick={() => setShowCrear(true)}
            style={{ ...S.btnFull(COL.pri2), width: 'auto', paddingLeft: 24, paddingRight: 24 }}
          >
            <FiPlus size={18} /> Agregar Primera Mesa
          </button>
        </div>
      )}

      {mesas.length > 0 && (
        <div style={{ flex: 1, overflow: 'auto', background: COL.canvasBg, position: 'relative' }}>
          <div
            ref={canvasRef}
            style={{
              position: 'relative',
              width: 2000,
              height: 1200,
              backgroundImage: `radial-gradient(circle, ${COL.canvasDot} 0.6px, transparent 0.6px)`,
              backgroundSize: '20px 20px',
              transform: `scale(${canvasZoom})`,
              transformOrigin: '0 0',
            }}
          >
            {mesas.map((m, i) => {
              const c = m.color || colorMap[m.estado] || COL.muted;
              const hasPos = m.pos_x !== 0 || m.pos_y !== 0;
              const col = i % 8,
                row = Math.floor(i / 8);
              const x = hasPos ? m.pos_x : 80 + col * 130;
              const y = hasPos ? m.pos_y : 80 + row * 120;
              const scale = m.tamano != null ? Number(m.tamano) || 1 : 1;
              const baseW = m.forma === 'rectangular' ? 120 : 92;
              const baseH = m.forma === 'rectangular' ? 70 : 92;
              const w = Math.round(baseW * scale);
              const h = Math.round(baseH * scale);
              const rot = m.rotacion != null ? Number(m.rotacion) || 0 : 0;
              const radius = m.forma === 'redonda' ? '50%' : m.forma === 'rectangular' ? 16 : 12;
              const isDragging = draggingId === m.id;
              const isRotating = rotatingId === m.id;
              const isSelected = mesaSel && mesaSel.id === m.id;
              return (
                <div
                  key={m.id}
                  data-mesa={m.id}
                  onMouseDown={(e) => onMouseDownMesa(e, m)}
                  onTouchStart={(e) => onTouchStartMesa(e, m)}
                  onClick={() => {
                    if (!dragMoved.current) setMesaSel(m);
                    dragMoved.current = false;
                  }}
                  style={{
                    position: 'absolute',
                    left: x,
                    top: y,
                    width: w,
                    height: h,
                    borderRadius: radius,
                    background: `linear-gradient(135deg, ${c}ee, ${c})`,
                    boxShadow: isDragging ? `0 12px 32px ${c}60` : `0 4px 14px ${c}30`,
                    cursor: has('salon_gestionar') ? 'grab' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 13,
                    transform: `rotate(${rot}deg)`,
                    transition: isDragging || isRotating ? 'none' : 'transform 0.15s, box-shadow 0.15s',
                    userSelect: 'none',
                    zIndex: isDragging || isRotating ? 100 : 1,
                    opacity: isDragging || isRotating ? 0.85 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!draggingId) {
                      e.currentTarget.style.transform = `rotate(${rot}deg) scale(1.06)`;
                      e.currentTarget.style.boxShadow = `0 8px 22px ${c}50`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!draggingId) {
                      e.currentTarget.style.transform = `rotate(${rot}deg) scale(1)`;
                      e.currentTarget.style.boxShadow = `0 4px 14px ${c}30`;
                    }
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 800 }}>{m.nombre || m.codigo}</span>
                  <span style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>{m.capacidad}p</span>
                  {m.pedidos_activos > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: COL.white,
                        color: COL.red,
                        fontSize: 11,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                      }}
                    >
                      {m.pedidos_activos}
                    </div>
                  )}
                  {has('salon_gestionar') && !draggingId && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: -4,
                        right: -2,
                        fontSize: 9,
                        opacity: 0.5,
                      }}
                      title="Arrastrar para mover"
                    >
                      ⋮⋮
                    </div>
                  )}
                  {isSelected && has('salon_gestionar') && !isDragging && (
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        _startRotate(m, e.clientX, e.clientY, e.currentTarget);
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (e.touches.length === 1)
                          _startRotate(m, e.touches[0].clientX, e.touches[0].clientY, e.currentTarget);
                      }}
                      title="Girar mesa"
                      style={{
                        position: 'absolute',
                        top: -26,
                        left: '50%',
                        width: 24,
                        height: 24,
                        marginLeft: -12,
                        borderRadius: '50%',
                        background: COL.white,
                        color: COL.pri2,
                        border: `1.5px solid ${COL.pri2}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'grab',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                      }}
                    >
                      <FiRotateCw size={13} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {has('salon_gestionar') && !isMobile && (
            <div
              style={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                fontSize: 11,
                color: COL.muted,
                background: 'rgba(255,255,255,0.9)',
                padding: '4px 10px',
                borderRadius: 8,
              }}
            >
              Arrastra las mesas para moverlas
            </div>
          )}
        </div>
      )}

      {mesaSel &&
        (isMobile ? (
          <BottomSheet
            open={true}
            onClose={() => setMesaSel(null)}
            title={`Mesa ${mesaSel.nombre || mesaSel.codigo}`}
            height="auto"
          >
            <MesaActions
              mesa={mesaSel}
              onEstado={cambiarEstado}
              onEdit={setEditingMesa}
              onDelete={eliminarMesa}
              onQR={setQrMesa}
              has={has}
              setVista={setVista}
              setMesaActiva={setMesaActiva}
              setPedidoAbierto={setPedidoAbierto}
              cerrar={() => setMesaSel(null)}
            />
          </BottomSheet>
        ) : (
          <div
            onClick={(e) => e.target === e.currentTarget && setMesaSel(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 500,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <div
              style={{
                width: 360,
                background: COL.white,
                boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
                padding: 24,
                overflowY: 'auto',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 20,
                }}
              >
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: COL.text }}>
                    Mesa {mesaSel.nombre || mesaSel.codigo}
                  </div>
                  <div style={{ fontSize: 13, color: COL.muted, marginTop: 2 }}>
                    Capacidad: {mesaSel.capacidad} personas · {mesaSel.forma || 'cuadrado'}
                  </div>
                </div>
                <button
                  onClick={() => setMesaSel(null)}
                  style={{
                    border: 'none',
                    background: COL.bg,
                    borderRadius: 10,
                    cursor: 'pointer',
                    padding: 8,
                    color: COL.muted,
                  }}
                >
                  <FiX size={18} />
                </button>
              </div>
              <MesaActions
                mesa={mesaSel}
                onEstado={cambiarEstado}
                onEdit={setEditingMesa}
                onDelete={eliminarMesa}
                onQR={setQrMesa}
                has={has}
                setVista={setVista}
                setMesaActiva={setMesaActiva}
                setPedidoAbierto={setPedidoAbierto}
                cerrar={() => setMesaSel(null)}
              />
            </div>
          </div>
        ))}

      {showCrear && (
        <CrearMesaModal
          sede={sede}
          onClose={() => setShowCrear(false)}
          onCreated={() => {
            setShowCrear(false);
            cargar();
          }}
          isMobile={isMobile}
        />
      )}

      {editingMesa && (
        <EditarMesaModal
          mesa={editingMesa}
          onClose={() => setEditingMesa(null)}
          onUpdated={() => {
            setEditingMesa(null);
            setMesaSel(null);
            cargar();
          }}
          isMobile={isMobile}
        />
      )}
      {qrMesa && (
        <QRMesaModal
          mesa={qrMesa}
          onClose={() => setQrMesa(null)}
          isMobile={isMobile}
        />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function MesaActions({ mesa, onEstado, onEdit, onDelete, has, setVista, setMesaActiva, setPedidoAbierto, onQR }) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const estadoColor = { libre: COL.green, ocupada: COL.red, sucia: COL.amber };
  const c = estadoColor[mesa.estado] || COL.muted;
  const [pedidosAbiertos, setPedidosAbiertos] = useState([]);
  const [cargandoAbiertos, setCargandoAbiertos] = useState(false);

  useEffect(() => {
    if (mesa.estado !== 'ocupada') {
      setPedidosAbiertos([]);
      return;
    }
    setCargandoAbiertos(true);
    api
      .get(`/pedidos/tienda/mesa/${mesa.id}/activos`)
      .then((r) => setPedidosAbiertos(r.data?.data || []))
      .catch(() => setPedidosAbiertos([]))
      .finally(() => setCargandoAbiertos(false));
  }, [mesa.id, mesa.estado]);

  const iniciarNuevoPedido = () => {
    setPedidoAbierto(null);
    setMesaActiva(mesa);
    setVista('pedido');
  };

  const abrirPedido = (pd) => {
    setMesaActiva(mesa);
    setPedidoAbierto({ id: pd.id, mesa });
    setVista('pedido');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div style={{ width: 12, height: 12, borderRadius: 4, background: c, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: COL.text }}>
          {mesa.estado?.toUpperCase()}
        </span>
        {mesa.pedidos_activos > 0 && (
          <Badge label={`${mesa.pedidos_activos} pedidos`} variant="warning" />
        )}
      </div>

      {mesa.estado === 'sucia' && has('salon_gestionar') && (
        <button onClick={() => onEstado(mesa, 'libre')} style={S.btnFull(COL.green)}>
          <FiCheck size={18} /> Marcar como Limpia
        </button>
      )}
      {mesa.estado === 'libre' && has('pedido_ver') && (
        <button onClick={iniciarNuevoPedido} style={S.btnFull(COL.pri2)}>
          <FiShoppingCart size={18} /> Iniciar Pedido
        </button>
      )}
      {mesa.estado === 'ocupada' && has('pedido_ver') && (
        <>
          <button onClick={iniciarNuevoPedido} style={S.btnFull(COL.pri2)}>
            <FiPlus size={18} /> Nuevo Pedido
          </button>

          {cargandoAbiertos && (
            <div style={{ fontSize: 12, color: COL.muted, padding: '4px 2px' }}>
              Cargando pedidos...
            </div>
          )}

          {!cargandoAbiertos && pedidosAbiertos.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: COL.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  marginTop: 4,
                }}
              >
                Pedidos abiertos
              </div>
              {pedidosAbiertos.map((pd) => (
                <button
                  key={pd.id}
                  onClick={() => abrirPedido(pd)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${COL.border}`,
                    background: COL.bg,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <FiPackage size={15} color={COL.amber} />
                  <span style={{ fontWeight: 700, color: COL.text, fontSize: 13 }}>
                    #{pd.ticket_numero || pd.id}
                  </span>
                  {pd.cliente_nombre && (
                    <span style={{ fontSize: 12, color: COL.muted, fontWeight: 600 }}>
                      {pd.cliente_nombre}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: COL.muted }}>
                    {pd.en_espera ? 'En espera' : pd.estado}
                  </span>
                  <FiChevronRight size={14} color={COL.faint} />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {has('salon_gestionar') && (
        <>
          <div style={{ height: 1, background: COL.border, margin: '4px 0' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => onQR && onQR(mesa)} style={S.btnSm(COL.pri2)}>
              <FiHash size={13} /> QR
            </button>
            <button onClick={() => onEdit(mesa)} style={S.btnSm(COL.pri2)}>
              <FiEdit2 size={13} /> Editar
            </button>
            <button onClick={() => onDelete(mesa.id)} style={S.btnSm(COL.red)}>
              <FiTrash2 size={13} /> Eliminar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ColorPickerMesa({ color, onChange, COL, S }) {
  const [custom, setCustom] = useState(color || '');
  useEffect(() => {
    setCustom(color || '');
  }, [color]);
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={S.formLabel}>Color de la mesa</label>
      <div style={{ fontSize: 11, color: COL.muted, margin: '4px 0 8px' }}>
        Personaliza el color. Si no eliges ninguno, se usa el color por estado (libre/ocupada/sucia).
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          onClick={() => onChange(null)}
          title="Sin color personalizado"
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            border: `2px solid ${color == null ? COL.pri2 : COL.border}`,
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COL.muted,
            fontFamily: 'inherit',
          }}
        >
          <FiX size={14} />
        </button>
        {MESA_PALETTE_COLORES.map((c) => {
          const sel = color === c;
          return (
            <button
              key={c}
              onClick={() => onChange(c)}
              title={c}
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: c,
                border: sel ? `3px solid ${COL.text}` : '1px solid rgba(0,0,0,0.12)',
                cursor: 'pointer',
                boxShadow: sel ? '0 0 0 2px ' + c : 'none',
              }}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(custom) ? custom : '#2563eb'}
          onChange={(e) => {
            setCustom(e.target.value);
            onChange(e.target.value);
          }}
          style={{ width: 34, height: 34, border: 'none', padding: 0, background: 'none', cursor: 'pointer' }}
        />
        <span style={{ fontSize: 11, color: COL.muted }}>Color personalizado (selector)</span>
      </div>
    </div>
  );
}

function CrearMesaModal({ sede, onClose, onCreated, isMobile }) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const [nombre, setNombre] = useState('');
  const [forma, setForma] = useState('cuadrado');
  const [color, setColor] = useState(null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const crear = async () => {
    setGuardando(true);
    setError('');
    const posX = Math.floor(Math.random() * 500 + 100);
    const posY = Math.floor(Math.random() * 400 + 100);
    try {
      const r = await api.post('/mesas/', {
        sede_codigo: sede,
        nombre: nombre.trim() || undefined,
        forma,
        pos_x: posX,
        pos_y: posY,
        color: color || undefined,
      });
      if (r.data.status === 'error') {
        setError(r.data.message);
        setGuardando(false);
        return;
      }
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al crear');
    } finally {
      setGuardando(false);
    }
  };

  const content = (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 800, color: COL.text }}>Nueva Mesa</div>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: COL.bg,
            borderRadius: 10,
            cursor: 'pointer',
            padding: 8,
            color: COL.muted,
          }}
        >
          <FiX size={18} />
        </button>
      </div>
      {error && (
        <div
          style={{
            padding: 10,
            borderRadius: 10,
            background: COL.redBg,
            color: COL.red,
            fontSize: 13,
            marginBottom: 14,
            fontWeight: 500,
          }}
        >
          <FiAlertCircle size={14} style={{ marginRight: 6 }} />
          {error}
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <label style={S.formLabel}>Nombre</label>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej: Terraza 1"
          autoFocus
          style={S.formInput}
        />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={S.formLabel}>Forma</label>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {formasOptions.map((f) => {
            const sel = forma === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setForma(f.id)}
                title={f.label}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  padding: '14px 6px 10px',
                  borderRadius: 14,
                  border: `1.5px solid ${sel ? COL.pri2 : COL.border}`,
                  background: sel ? COL.blueBg : COL.white,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                <ShapeIcon forma={f.id} size={32} color={sel ? COL.pri2 : COL.muted} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: sel ? 700 : 500,
                    color: sel ? COL.pri2 : COL.muted,
                  }}
                >
                  {f.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <ColorPickerMesa color={color} onChange={setColor} COL={COL} S={S} />
      <button onClick={crear} disabled={guardando} style={S.btnFull(COL.pri2)}>
        {guardando ? (
          'Creando...'
        ) : (
          <>
            <FiPlus size={18} /> Crear Mesa
          </>
        )}
      </button>
    </div>
  );

  if (isMobile)
    return (
      <BottomSheet open={true} onClose={onClose} title="Nueva Mesa" height="auto">
        {content}
      </BottomSheet>
    );
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: COL.overlay,
        zIndex: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(3px)',
      }}
    >
      <div
        style={{
          background: COL.surface,
          borderRadius: 20,
          width: 420,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 28,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {content}
      </div>
    </div>
  );
}

function EditarMesaModal({ mesa, onClose, onUpdated, isMobile }) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const [nombre, setNombre] = useState(mesa.nombre || mesa.codigo || '');
  const [forma, setForma] = useState(mesa.forma || 'cuadrado');
  const [rotacion, setRotacion] = useState(mesa.rotacion != null ? Number(mesa.rotacion) : 0);
  const [tamano, setTamano] = useState(mesa.tamano != null ? Number(mesa.tamano) : 1);
  const [color, setColor] = useState(mesa.color || null);
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/mesas/${mesa.id}`, { nombre: nombre.trim(), forma, rotacion, tamano, color });
      onUpdated();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGuardando(false);
    }
  };

  const content = (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 800, color: COL.text }}>Editar Mesa</div>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: COL.bg,
            borderRadius: 10,
            cursor: 'pointer',
            padding: 8,
            color: COL.muted,
          }}
        >
          <FiX size={18} />
        </button>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={S.formLabel}>Nombre</label>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          autoFocus
          style={S.formInput}
        />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={S.formLabel}>Forma</label>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {formasOptions.map((f) => {
            const sel = forma === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setForma(f.id)}
                title={f.label}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  padding: '14px 6px 10px',
                  borderRadius: 14,
                  border: `1.5px solid ${sel ? COL.pri2 : COL.border}`,
                  background: sel ? COL.blueBg : COL.white,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                <ShapeIcon forma={f.id} size={32} color={sel ? COL.pri2 : COL.muted} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: sel ? 700 : 500,
                    color: sel ? COL.pri2 : COL.muted,
                  }}
                >
                  {f.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={S.formLabel}>Rotación: {rotacion}°</label>
        <input
          type="range"
          min={0}
          max={359}
          step={5}
          value={rotacion}
          onChange={(e) => setRotacion(Number(e.target.value))}
          style={{ width: '100%', marginTop: 4, accentColor: COL.pri2 }}
        />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={S.formLabel}>Tamaño: {Math.round(tamano * 100)}%</label>
        <input
          type="range"
          min={0.5}
          max={2}
          step={0.1}
          value={tamano}
          onChange={(e) => setTamano(Number(e.target.value))}
          style={{ width: '100%', marginTop: 4, accentColor: COL.pri2 }}
        />
      </div>
      <ColorPickerMesa color={color} onChange={setColor} COL={COL} S={S} />
      <button onClick={guardar} disabled={guardando} style={S.btnFull(COL.pri2)}>
        {guardando ? (
          'Guardando...'
        ) : (
          <>
            <FiCheck size={18} /> Guardar Cambios
          </>
        )}
      </button>
    </div>
  );

  if (isMobile)
    return (
      <BottomSheet open={true} onClose={onClose} title="Editar Mesa" height="auto">
        {content}
      </BottomSheet>
    );
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: COL.overlay,
        zIndex: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(3px)',
      }}
    >
      <div
        style={{
          background: COL.surface,
          borderRadius: 20,
          width: 420,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 28,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {content}
      </div>
    </div>
  );
}

// ═══════════ NUEVO PEDIDO ═══════════
function NuevoPedido({
  sede,
  has,
  config,
  isMobile,
  setVista,
  cargarPedidoDesdeEspera,
  setCargarPedidoDesdeEspera,
  abrirPagoDirecto,
  setAbrirPagoDirecto,
  mesaActiva: mesaActivaProp,
  pedidoAbierto,
  setPedidoAbierto,
  abrirTicket,
}) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const { agregarNotificacion } = useNotificaciones();
  const toast = (mensaje, tipo = 'info') =>
    agregarNotificacion({ titulo: tipo === 'error' ? 'Error' : 'Pedido Tienda', mensaje, tipo });
  const [carrito, setCarrito] = useState([]);
  const [productos, setProductos] = useState([]);
  const [combosTienda, setCombosTienda] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [departamentos, setDepartamentos] = useState([]);
  const [deptoFiltro, setDeptoFiltro] = useState('');
  const [grupoFiltro, setGrupoFiltro] = useState('');
  const [mostrarSubgrupos, setMostrarSubgrupos] = useState(false);
  const [vistaCatalogo, setVistaCatalogo] = useState('grid');
  const [showDeptoModal, setShowDeptoModal] = useState(false);
  const [loadingProd, setLoadingProd] = useState(true);
  const [errorProd, setErrorProd] = useState(false);
  const [showCarrito, setShowCarrito] = useState(!isMobile);
  const [showPago, setShowPago] = useState(false);
  const [showModalCaja, setShowModalCaja] = useState(false);
  const [pagoPaso, setPagoPaso] = useState(0);
  const [pagosLista, setPagosLista] = useState([]);
  const [cobrarImpuesto, setCobrarImpuesto] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [mesaActiva, setMesaActiva] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [pedidoEditandoId, setPedidoEditandoId] = useState(null);
  const [showEtiquetasModal, setShowEtiquetasModal] = useState(null);
  const [etiquetasSeleccionadas, setEtiquetasSeleccionadas] = useState([]);
  const [etiquetasEditandoIdx, setEtiquetasEditandoIdx] = useState(null);
  const [etiquetaManualInput, setEtiquetaManualInput] = useState('');
  const [comboDetalle, setComboDetalle] = useState(null);
  const [comboSelEtiquetas, setComboSelEtiquetas] = useState({});
  const [comboEtiquetasManuales, setComboEtiquetasManuales] = useState({});
  const saveTimerRef = useRef(null);
  const searchRef = useRef(null);
  const carritoSnapshotRef = useRef(null);

  // ── Navegación por teclado (POS ágil) ──
  const [navZone, setNavZone] = useState('catalogo'); // 'catalogo' | 'carrito'
  const [navIndex, setNavIndex] = useState(0);
  const [qtyBuffer, setQtyBuffer] = useState('');
  const [showTeclas, setShowTeclas] = useState(false);
  const gridRef = useRef(null);
  const productCardRefs = useRef({}); // codigo -> nodo DOM
  const cartItemRefs = useRef({}); // indice -> nodo DOM

  // Navegación por teclado en modales + animación de búsqueda.
  const [searchFocused, setSearchFocused] = useState(false);
  const [deptSearch, setDeptSearch] = useState('');
  const etiquetasPanelRef = useRef(null);
  const deptosPanelRef = useRef(null);
  const etiquetasAgregarRef = useRef(null);
  const deptosPrimerRef = useRef(null);

  const cerrarEtiquetas = () => {
    setShowEtiquetasModal(null);
    setEtiquetasEditandoIdx(null);
  };
  const cerrarDeptos = () => {
    setShowDeptoModal(false);
    setDeptSearch('');
  };
  useRovingModal(etiquetasPanelRef, cerrarEtiquetas, !!showEtiquetasModal);
  useRovingModal(deptosPanelRef, cerrarDeptos, showDeptoModal);

  // Auto-enfoca la primera opción de personalización (o la nota) al abrir.
  useEffect(() => {
    if (!showEtiquetasModal) return;
    const t = setTimeout(() => {
      const panel = etiquetasPanelRef.current;
      const first = panel?.querySelector('[data-nav="chip"]') || panel?.querySelector('[data-nav="nota"]');
      first?.focus();
    }, 30);
    return () => clearTimeout(t);
  }, [showEtiquetasModal]);

  // Info del cliente del pedido (nombre/telefono/cedula, todos opcionales).
  const [clienteInfo, setClienteInfo] = useState({ nombre: '', telefono: '', cedula: '' });
  const clienteNombre = clienteInfo.nombre || '';
  const clienteCedula = clienteInfo.cedula || '';
  const clienteTelefono = clienteInfo.telefono || '';
  const setClienteNombre = (v) => setClienteInfo((p) => ({ ...p, nombre: v }));
  const setClienteCedula = (v) => setClienteInfo((p) => ({ ...p, cedula: v }));
  const setClienteTelefono = (v) => setClienteInfo((p) => ({ ...p, telefono: v }));
  const [showEditarCliente, setShowEditarCliente] = useState(false);
  const [showClienteGuardar, setShowClienteGuardar] = useState(false);

  useEffect(() => {
    if (mesaActivaProp) {
      setMesaActiva(mesaActivaProp);
    }
  }, [mesaActivaProp]);

  // Abrir un pedido existente de una mesa (no se crea uno nuevo; se edita ese #).
  useEffect(() => {
    if (pedidoAbierto) {
      setMesaActiva(pedidoAbierto.mesa);
      cargarPedidoPorId(pedidoAbierto.id);
      setPedidoAbierto(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoAbierto]);

  const cargarPedidoPorId = async (pid) => {
    try {
      const r = await api.get(`/pedidos/tienda/${pid}`);
      const pd = r.data?.data;
      if (pd) {
        setPedidoEditandoId(pd.id);
        setClienteInfo({
          nombre: pd.cliente_nombre || '',
          cedula: pd.cliente_cedula || '',
          telefono: pd.cliente_telefono || '',
        });
        const items = (pd.productos || []).map((it) => ({
          codigo: it.codigo,
          descripcion: it.nombre || it.codigo,
          precio: it.precio_unitario || 0,
          cantidad: it.cantidad || 1,
          costo: it.costo_unitario || 0,
          imagen_url: it.imagen_url || getImagenUrl(it.codigo) || '',
          etiquetas: it.etiquetas || [],
          combo_id: it.combo_id || null,
          componentes: it.componentes || null,
          combo_extra: it.combo_extra || 0,
          precio_manual: !!it.precio_manual,
        }));
        setCarrito(items);
      }
    } catch (e) {
      console.error('Error cargando pedido de mesa:', e);
    }
  };

  const doSaveChanges = useCallback(async (prods, pid) => {
    if (!pid) return;
    const items = prods.map((c) => ({
      codigo: c.codigo,
      nombre: c.descripcion,
      cantidad: c.cantidad,
      precio_unitario: parseFloat(c.precio) || 0,
      costo_unitario: c.costo || 0,
      etiquetas: (c.etiquetas || []).map((e) => ({
        id: e.id,
        nombre: e.nombre,
        precio: e.precio || 0,
      })),
      combo_id: c.combo_id || null,
      componentes: c.componentes || null,
      combo_extra: c.combo_extra || 0,
      precio_manual: !!c.precio_manual,
    }));
    try {
      await api.put(`/pedidos/tienda/${pid}/productos`, items);
    } catch (e) {
      console.error('Error auto-guardando pedido:', e);
    }
  }, []);

  const debouncedSave = (prods, pid) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => doSaveChanges(prods, pid), 500);
  };

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Carga TODOS los productos activos de la sede (paginando con cursor e incluyendo
  // agotados) para que el filtro client-side por grupos/subgrupos/departamentos/busqueda
  // vea el catalogo completo. Antes solo cargaba `limite=50` y el endpoint publico
  // ocultaba los productos con stock <= 0, dejando fuera parte del inventario.
  const cargarTodosProductos = useCallback(async (codigoSede) => {
    const all = [];
    let ultimoCodigo = null;
    let ultimaDesc = null;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let url = `/catalogo/productos?sede=${encodeURIComponent(codigoSede)}&limite=100&incluir_agotados=true`;
      if (ultimoCodigo) {
        url += `&ultimo_codigo=${encodeURIComponent(ultimoCodigo)}&ultima_descripcion=${encodeURIComponent(ultimaDesc || '')}`;
      }
      const r = await api.get(url);
      const data = r.data?.data || [];
      all.push(...data);
      if (!r.data?.has_more || data.length === 0) break;
      const nextCodigo = r.data.ultimo_codigo;
      // Guard de progreso: evita bucle infinito si el cursor no avanza.
      if (!nextCodigo || nextCodigo === ultimoCodigo) break;
      ultimoCodigo = nextCodigo;
      ultimaDesc = r.data.ultima_descripcion || null;
    }
    return all;
  }, []);

  useEffect(() => {
    const cacheKey = `catalogo:${sede}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      // Reaccion inmediata: catalogo visible sin skeleton, revalida en background.
      setProductos(cached.productos || []);
      setDepartamentos(cached.departamentos || []);
      setCombosTienda(cached.combos || []);
      setLoadingProd(false);
      setErrorProd(false);
    } else {
      setLoadingProd(true);
      setErrorProd(false);
    }
    let mounted = true;
    Promise.all([
      cargarTodosProductos(sede),
      api
        .get(`/catalogo/departamentos?sede=${sede}`)
        .then((r) => r.data?.data || []),
      api
        .get(`/catalogo/combos?schema_name=${encodeURIComponent(getSchemaName())}`)
        .then((r) => r.data?.data || [])
        .catch(() => []),
    ])
      .then(([productos, departamentos, combos]) => {
        if (!mounted) return;
        setProductos(productos);
        setDepartamentos(departamentos);
        setCombosTienda(combos);
        cacheSet(cacheKey, { productos, departamentos, combos });
        setLoadingProd(false);
      })
      .catch(() => {
        if (!mounted) return;
        setLoadingProd(false);
        setErrorProd(true);
      });
    return () => { mounted = false; };
  }, [sede, cargarTodosProductos]);

  // Invalida el catalogo en cache cuando llega un cambio de inventario (venta,
  // pedido, sync) para que la proxima apertura revalide en background.
  useEffect(() => {
    const onInv = (payload) => {
      const s = payload?.sede || payload?.sede_codigo;
      if (s) invalidate(`catalogo:${s}`);
    };
    socket.on('inventario_actualizado', onInv);
    return () => { socket.off('inventario_actualizado', onInv); };
  }, []);

  useEffect(() => {
    if (cargarPedidoDesdeEspera) {
      const items = (cargarPedidoDesdeEspera.productos || []).map((it) => ({
        ...it,
        codigo: it.codigo,
        descripcion: it.nombre || it.codigo,
        precio: it.precio_unitario || 0,
        cantidad: it.cantidad || 1,
        imagen_url: it.imagen_url || getImagenUrl(it.codigo) || '',
        etiquetas: it.etiquetas || [],
      }));
      setCarrito(items);
      setMesaActiva(
        cargarPedidoDesdeEspera.mesa_id
          ? { id: cargarPedidoDesdeEspera.mesa_id, nombre: cargarPedidoDesdeEspera.mesa_nombre }
          : null
      );
      setClienteInfo({
        nombre: cargarPedidoDesdeEspera.cliente_nombre || '',
        cedula: cargarPedidoDesdeEspera.cliente_cedula || '',
        telefono: cargarPedidoDesdeEspera.cliente_telefono || '',
      });
      setPedidoEditandoId(cargarPedidoDesdeEspera.id);
      carritoSnapshotRef.current = JSON.stringify(items);
      setCargarPedidoDesdeEspera(null);
    }
  }, [cargarPedidoDesdeEspera, setCargarPedidoDesdeEspera, setClienteInfo]);

  useEffect(() => {
    if (abrirPagoDirecto && carrito.length > 0) {
      setAbrirPagoDirecto(null);
      abrirPago();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abrirPagoDirecto, carrito]);

  const resultados = useMemo(() => {
    if (!busqueda && !deptoFiltro && !grupoFiltro) return productos;
    return productos.filter((p) => {
      if (grupoFiltro && p.grupo !== grupoFiltro) return false;
      if (deptoFiltro && p.departamento !== deptoFiltro) return false;
      if (busqueda) {
        const q = busqueda.toLowerCase();
        if (!p.descripcion?.toLowerCase().includes(q) && !p.codigo?.toLowerCase().includes(q))
          return false;
      }
      return true;
    });
  }, [productos, deptoFiltro, grupoFiltro, busqueda]);

  const gruposDepto = useMemo(() => {
    const map = new Map();
    departamentos.forEach((d) => {
      const codigo = d.grupo || '';
      const nombre = d.grupo_nombre || codigo || 'Sin categoría';
      const key = codigo || nombre;
      if (!map.has(key)) map.set(key, { codigo, nombre, depts: [] });
      map.get(key).depts.push(d);
    });
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        depts: [...g.depts].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es')),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [departamentos]);

  // Grupos/subgrupos filtrados por la búsqueda del modal de departamentos.
  // Si una búsqueda coincide con un subgrupo (pero no con el grupo), se conserva
  // el grupo con solo los subgrupos que coinciden, para poder llegar a ellos.
  const deptGroupsFiltrados = useMemo(() => {
    const q = (deptSearch || '').trim().toLowerCase();
    if (!q) return gruposDepto;
    return gruposDepto
      .map((g) => {
        const depts = g.depts.filter((d) => (d.nombre || '').toLowerCase().includes(q));
        const gMatch = (g.nombre || '').toLowerCase().includes(q);
        if (!gMatch && depts.length === 0) return null;
        return { ...g, depts };
      })
      .filter(Boolean);
  }, [gruposDepto, deptSearch]);

  // Revela subgrupos cuando hay búsqueda activa (además del toggle manual).
  const mostrarSubdeptos = mostrarSubgrupos || !!deptSearch.trim();

  const agregarAlCarrito = (prod) => {
    // Siempre abre el modal de personalización (notas + extras, si los hay),
    // de modo que cualquier producto pueda llevar una nota de personalización.
    const etiquetas = prod.etiquetas || [];
    setShowEtiquetasModal(prod);
    setEtiquetaManualInput('');
    setEtiquetasSeleccionadas(
      etiquetas
        .filter((e) => e.activa_por_defecto)
        .map((e) => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 }))
    );
  };

  const _etKey = (ets) =>
    (ets || [])
      .map((e) => e.id)
      .sort()
      .join(',');

  const _agregarAlCarritoDirecto = (prod, ets, componentes) => {
    setCarrito((prev) => {
      const esCombo = !!prod.combo_id;
      const idx = prev.findIndex((c) => {
        if (c.codigo !== prod.codigo) return false;
        if (esCombo) return _firmaCombo(c.componentes) === _firmaCombo(componentes);
        return _etKey(c.etiquetas) === _etKey(ets);
      });
      const next =
        idx >= 0
          ? prev.map((c, i) =>
              i === idx
                ? {
                    ...c,
                    cantidad: c.cantidad + 1,
                    etiquetas: esCombo ? c.etiquetas : ets,
                    componentes: esCombo ? componentes : c.componentes,
                    combo_extra: esCombo ? prod.combo_extra : c.combo_extra,
                  }
                : c
            )
          : [
              ...prev,
              {
                ...prod,
                cantidad: 1,
                etiquetas: esCombo ? [] : ets,
                componentes: esCombo ? componentes : undefined,
              },
            ];
      if (pedidoEditandoId) debouncedSave(next, pedidoEditandoId);
      return next;
    });
  };

  // Expande los componentes del combo en instancias individuales (cantidad > 1 => una por unidad).
  const instanciasCombo = (items) => {
    const out = [];
    (items || []).forEach((it) => {
      const cant = Math.max(1, parseInt(it.cantidad, 10) || 1);
      for (let idx = 0; idx < cant; idx++) {
        out.push({ ...it, _instancia: idx });
      }
    });
    return out;
  };

  const abrirComboDetalle = (combo) => {
    const sel = {};
    instanciasCombo(combo.items).forEach((it) => {
      sel[`${it.codigo_producto}#${it._instancia}`] = (it.etiquetas || []).map((e) => e.id);
    });
    setComboSelEtiquetas(sel);
    setComboEtiquetasManuales({});
    setComboDetalle(combo);
  };

  const cerrarComboDetalle = () => {
    setComboDetalle(null);
    setComboSelEtiquetas({});
    setComboEtiquetasManuales({});
  };

  const toggleEtiquetaCombo = (codigoProducto, instanciaIdx, et) => {
    const key = `${codigoProducto}#${instanciaIdx}`;
    setComboSelEtiquetas((prev) => {
      const actual = prev[key] || [];
      const ya = actual.includes(et.id);
      return {
        ...prev,
        [key]: ya ? actual.filter((id) => id !== et.id) : [...actual, et.id],
      };
    });
  };

  const agregarEtiquetaManualCombo = (key, nombre) => {
    const nom = (nombre || '').trim();
    if (!nom) return;
    setComboEtiquetasManuales((prev) => {
      const actual = prev[key] || [];
      if (actual.some((e) => e.nombre.toLowerCase() === nom.toLowerCase())) return prev;
      return {
        ...prev,
        [key]: [...actual, { id: 'manual_' + Date.now().toString(36), nombre: nom }],
      };
    });
  };

  const quitarEtiquetaManualCombo = (key, id) => {
    setComboEtiquetasManuales((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((e) => e.id !== id),
    }));
  };

  const _firmaCombo = (componentes) =>
    JSON.stringify(
      (componentes || []).map((x) => [
        x.codigo_producto,
        (x.etiquetas || []).map((e) => e.id).sort(),
      ])
    );

  const agregarComboPersonalizado = () => {
    if (!comboDetalle) return;
    let extra = 0;
    const componentes = instanciasCombo(comboDetalle.items).map((it) => {
      const key = `${it.codigo_producto}#${it._instancia}`;
      const incluidos = new Set((it.etiquetas || []).map((e) => e.id));
      const seleccionados = comboSelEtiquetas[key] || [];
      const etiquetas = (it.etiquetas_disponibles || [])
        .filter((e) => seleccionados.includes(e.id))
        .map((e) => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 }));
      (it.etiquetas_disponibles || []).forEach((e) => {
        if (!incluidos.has(e.id) && seleccionados.includes(e.id)) {
          extra += e.precio || 0;
        }
      });
      const manuales = (comboEtiquetasManuales[key] || []).map((e) => ({
        id: e.id, nombre: e.nombre, precio: 0,
      }));
      return {
        codigo_producto: it.codigo_producto,
        nombre: it.nombre,
        etiquetas: [...etiquetas, ...manuales],
      };
    });
    const prod = {
      codigo: `combo_${comboDetalle.id}`,
      combo_id: comboDetalle.id,
      descripcion: comboDetalle.nombre,
      precio: comboDetalle.precio || 0,
      costo: 0,
      imagen_url: comboDetalle.imagen_url || comboDetalle.thumb_url || '',
      etiquetas: [],
      componentes,
      combo_extra: extra,
    };
    _agregarAlCarritoDirecto(prod, [], componentes);
    cerrarComboDetalle();
  };

  const agregarComboTienda = (combo) => {
    const tieneComponentes = Array.isArray(combo.items) && combo.items.length > 0;
    if (tieneComponentes) {
      abrirComboDetalle(combo);
      return;
    }
    const prod = {
      codigo: `combo_${combo.id}`,
      combo_id: combo.id,
      descripcion: combo.nombre,
      precio: combo.precio || 0,
      costo: 0,
      imagen_url: combo.imagen_url || combo.thumb_url || '',
      etiquetas: [],
      componentes: [],
      combo_extra: 0,
    };
    _agregarAlCarritoDirecto(prod, [], []);
  };

  const showEtiquetasEditor = (cartItem, cartIdx) => {
    setShowEtiquetasModal({
      ...cartItem,
      etiquetas:
        (cartItem.etiquetas || []).length > 0
          ? cartItem.etiquetas
          : productos.find((p) => p.codigo === cartItem.codigo)?.etiquetas || [],
    });
    setEtiquetasSeleccionadas([
      ...(cartItem.etiquetas || []).map((e) => ({
        id: e.id,
        nombre: e.nombre,
        precio: e.precio || 0,
      })),
    ]);
    setEtiquetaManualInput('');
    setEtiquetasEditandoIdx(cartIdx);
  };
  const cambiarCantidad = (idx, delta) =>
    setCarrito((prev) => {
      const n = prev.map((c, i) => (i === idx ? { ...c, cantidad: c.cantidad + delta } : c));
      const next = n[idx].cantidad <= 0 ? n.filter((_, i) => i !== idx) : n;
      if (pedidoEditandoId) debouncedSave(next, pedidoEditandoId);
      return next;
    });
  const setCantidadDirecta = (idx, valor) => {
    const n = parseInt(valor, 10);
    if (isNaN(n) || n < 0) return;
    setCarrito((prev) => {
      const next = prev
        .map((c, i) => (i === idx ? { ...c, cantidad: n } : c))
        .filter((c) => c.cantidad > 0);
      if (pedidoEditandoId) debouncedSave(next, pedidoEditandoId);
      return next;
    });
  };
  const eliminarItem = (idx) =>
    setCarrito((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (pedidoEditandoId) debouncedSave(next, pedidoEditandoId);
      return next;
    });
  const cambiarPrecioManual = (idx, valor) =>
    setCarrito((prev) => {
      const next = prev.map((c, i) => (i === idx ? { ...c, precio: valor } : c));
      if (pedidoEditandoId) debouncedSave(next, pedidoEditandoId);
      return next;
    });
  // Solo números, con un máximo de 2 decimales. Devuelve el string ya saneado.
  const sanitizarPrecioManual = (valor) => {
    const limpio = String(valor || '')
      .replace(/[^\d.,]/g, '')
      .replace(/,/g, '.');
    const partes = limpio.split('.');
    const entera = (partes[0] || '').replace(/^0+(?=\d)/, '');
    if (partes.length > 1) {
      const decimal = partes.slice(1).join('').slice(0, 2);
      return `${entera || '0'}.${decimal}`;
    }
    return entera;
  };
  const subtotal = carrito.reduce((s, c) => {
    const base = c.cantidad * (parseFloat(c.precio) || 0);
    const etExtra = (c.etiquetas || []).reduce((t, e) => t + (e.precio || 0), 0) * c.cantidad;
    const comboExtra = (c.combo_extra || 0) * c.cantidad;
    return s + base + etExtra + comboExtra;
  }, 0);
  const impuestoTotal = carrito.reduce((s, c) => {
    const base = c.cantidad * (parseFloat(c.precio) || 0);
    const etExtra = (c.etiquetas || []).reduce((t, e) => t + (e.precio || 0), 0) * c.cantidad;
    const comboExtra = (c.combo_extra || 0) * c.cantidad;
    const tasa = c.impuesto || 0;
    return s + (base + etExtra + comboExtra) * (tasa / 100);
  }, 0);
  const total = subtotal + (cobrarImpuesto ? impuestoTotal : 0);
  const itemCount = carrito.reduce((s, c) => s + c.cantidad, 0);

  const abrirPago = async () => {
    if (!mesaActiva) {
      toast('Selecciona una mesa para el pedido', 'error');
      return;
    }
    // Abre el modal de inmediato para una sensacion de fluidez; los metodos de
    // pago y la validacion de caja se resuelven en background.
    setPagoPaso(0);
    setShowPago(true);

    const cacheKey = `metodos-pago:${sede}`;
    const cached = cacheGet(cacheKey);
    if (cached) setMetodosPago(cached);
    api
      .get(`/ventas/metodos-pago?sede=${sede}`)
      .then((r) => {
        const d = r.data?.data || [];
        cacheSet(cacheKey, d);
        setMetodosPago(d);
      })
      .catch((e) => console.error('Error cargando metodos de pago:', e));

    if (config?.cierre_caja_obligatorio) {
      try {
        const cr = await api.get('/cierre-caja/abierta');
        if (!cr.data?.data) {
          setShowPago(false);
          setShowModalCaja(true);
        }
      } catch (e) {
        setShowPago(false);
        setShowModalCaja(true);
      }
    }
  };

  // ════════════════════════════════════════════════════════════════
  //  Navegación por teclado (POS ágil)
  // ════════════════════════════════════════════════════════════════
  // Mantiene el índice dentro de rango cuando cambia la lista visible.
  useEffect(() => {
    const len = navZone === 'catalogo' ? resultados.length : carrito.length;
    if (len === 0) {
      setNavIndex(0);
    } else if (navIndex >= len) {
      setNavIndex(len - 1);
    }
  }, [resultados.length, carrito.length, navZone, navIndex]);

  // Auto-scroll al elemento seleccionado.
  useEffect(() => {
    const el =
      navZone === 'catalogo'
        ? productCardRefs.current[resultados[navIndex]?.codigo]
        : cartItemRefs.current[navIndex];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [navIndex, navZone, resultados, carrito]);

  const keyboardTecladoRef = useRef(null);
  keyboardTecladoRef.current = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return;
      const modalOpen =
        showEtiquetasModal ||
        showDeptoModal ||
        showPago ||
        showModalCaja ||
        showEditarCliente ||
        showClienteGuardar ||
        comboDetalle;
      if (modalOpen) return;

      const k = e.key;

      if (navZone === 'catalogo') {
        const len = resultados.length;
        if (len === 0) return;
        // Columnas de la grilla (vista grid) o 1 (vista lista).
        let cols = 1;
        if (vistaCatalogo === 'grid' && gridRef.current) {
          const width = gridRef.current.clientWidth;
          const item = Math.round(isMobile ? 140 : 180);
          const gap = 10;
          cols = Math.max(1, Math.floor((width + gap) / (item + gap)));
        }
        switch (k) {
          case 'ArrowRight':
            e.preventDefault();
            setNavIndex((i) => Math.min(len - 1, i + 1));
            break;
          case 'ArrowLeft':
            e.preventDefault();
            setNavIndex((i) => Math.max(0, i - 1));
            break;
          case 'ArrowDown':
            e.preventDefault();
            setNavIndex((i) => Math.min(len - 1, i + cols));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setNavIndex((i) => Math.max(0, i - cols));
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            if (resultados[navIndex]) agregarAlCarrito(resultados[navIndex]);
            break;
          case 'd':
          case 'D':
            e.preventDefault();
            setShowDeptoModal(true);
            break;
          case 'f':
          case 'F':
          case '/':
            e.preventDefault();
            setSearchFocused(true);
            searchRef.current?.focus();
            break;
          case 'c':
          case 'C':
            e.preventDefault();
            if (carrito.length) {
              setNavZone('carrito');
              setNavIndex(0);
              setQtyBuffer('');
            }
            break;
          case '?':
            e.preventDefault();
            setShowTeclas((v) => !v);
            break;
          default:
            break;
        }
        return;
      }

      // ── Zona carrito ──
      const len = carrito.length;
      if (len === 0) {
        setNavZone('catalogo');
        return;
      }
      switch (k) {
        case 'ArrowDown':
          e.preventDefault();
          setNavIndex((i) => Math.min(len - 1, i + 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setNavIndex((i) => Math.max(0, i - 1));
          break;
        case 'ArrowRight':
        case '+':
        case '=':
          e.preventDefault();
          cambiarCantidad(navIndex, 1);
          break;
        case 'ArrowLeft':
        case '-':
          e.preventDefault();
          cambiarCantidad(navIndex, -1);
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          eliminarItem(navIndex);
          break;
        case 'Enter':
          e.preventDefault();
          if (qtyBuffer !== '') {
            setCantidadDirecta(navIndex, qtyBuffer);
            setQtyBuffer('');
          }
          break;
        case 'Escape':
        case 'c':
        case 'C':
          e.preventDefault();
          setNavZone('catalogo');
          setQtyBuffer('');
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          abrirPago();
          break;
        case 'f':
        case 'F':
        case '/':
          e.preventDefault();
          setNavZone('catalogo');
          setQtyBuffer('');
          setSearchFocused(true);
          searchRef.current?.focus();
          break;
        default:
          if (/^[0-9]$/.test(k)) {
            e.preventDefault();
            setQtyBuffer((b) => (b + k).replace(/^0+(?=\d)/, '').slice(0, 4));
          }
          break;
      }
  };

  useEffect(() => {
    const onKey = (e) => keyboardTecladoRef.current?.(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const confirmarPago = async (vuelto = 0, propina = null, propinaMonto = 0, procesarConDiferencia = false) => {
    const items = carrito.map((c) => ({
      codigo: c.codigo,
      nombre: c.descripcion,
      cantidad: c.cantidad,
      precio_unitario: parseFloat(c.precio) || 0,
      costo_unitario: c.costo || 0,
      etiquetas: (c.etiquetas || []).map((e) => ({
        id: e.id,
        nombre: e.nombre,
        precio: e.precio || 0,
      })),
      combo_id: c.combo_id || null,
      componentes: c.componentes || null,
      combo_extra: c.combo_extra || 0,
      precio_manual: !!c.precio_manual,
    }));
    const abrirTicketEntrega = async (pedidoId) => {
      try {
        const r = await api.get(`/pedidos/tienda/${pedidoId}/recibo`);
        if (r.data?.data) abrirTicket(r.data.data, 'entrega', true);
      } catch (e) {
        console.error('Error generando ticket de entrega:', e);
      }
    };
    try {
      if (pedidoEditandoId) {
        // Solo re-guarda productos si el carrito cambio respecto al pedido cargado.
        if (carritoSnapshotRef.current !== JSON.stringify(carrito)) {
          await api.put(`/pedidos/tienda/${pedidoEditandoId}/productos`, items);
        }
        if (pagosLista.length > 0)
          await api.put(`/pedidos/tienda/${pedidoEditandoId}/cobrar`, {
            pagos: pagosLista,
            monto_recibido: total + propinaMonto + vuelto,
            vuelto,
            cobrar_impuesto: cobrarImpuesto,
            cliente_nombre: clienteNombre,
            cliente_cedula: clienteCedula,
            cliente_telefono: clienteTelefono,
            propina,
            procesar_con_diferencia: procesarConDiferencia,
          });
        setShowPago(false);
        setCarrito([]);
        setMesaActiva(null);
        setPagosLista([]);
        setCobrarImpuesto(false);
        setClienteInfo({ nombre: '', telefono: '', cedula: '' });
        setPedidoEditandoId(null);
        carritoSnapshotRef.current = null;
        if (pagosLista.length > 0) abrirTicketEntrega(pedidoEditandoId);
        toast(
          `Pedido #${pedidoEditandoId} cobrado` + (vuelto > 0 ? ` — Vuelto: ${fmt(vuelto)}` : ''),
          'success'
        );
      } else {
        const r = await api.post('/pedidos/tienda/crear', {
          items,
          sede_codigo: sede,
          mesa_id: mesaActiva?.id,
          cliente_nombre: clienteNombre,
          cliente_cedula: clienteCedula,
          cliente_telefono: clienteTelefono,
        });
        const pid = r.data?.data?.id;
        if (pid && pagosLista.length > 0)
          await api.put(`/pedidos/tienda/${pid}/cobrar`, {
            pagos: pagosLista,
            monto_recibido: total + propinaMonto + vuelto,
            vuelto,
            cobrar_impuesto: cobrarImpuesto,
            cliente_nombre: clienteNombre,
            cliente_cedula: clienteCedula,
            cliente_telefono: clienteTelefono,
            propina,
            procesar_con_diferencia: procesarConDiferencia,
          });
        setShowPago(false);
        setCarrito([]);
        setMesaActiva(null);
        setPagosLista([]);
        setCobrarImpuesto(false);
        setClienteInfo({ nombre: '', telefono: '', cedula: '' });
        carritoSnapshotRef.current = null;
        if (pid && pagosLista.length > 0) abrirTicketEntrega(pid);
        toast(
          `Pedido #${r.data?.data?.ticket_numero || pid} creado` +
            (vuelto > 0 ? ` — Vuelto: ${fmt(vuelto)}` : ''),
          'success'
        );
      }
    } catch (e) {
      toast(e.response?.data?.detail || 'Error al procesar el pedido', 'error');
    }
  };
  const guardarEnEspera = () => {
    if (!mesaActiva) {
      toast('Selecciona una mesa para guardar el pedido', 'error');
      return;
    }
    if (!pedidoEditandoId) {
      // Pedido nuevo: pedir datos del cliente antes de guardarlo en espera.
      setShowClienteGuardar(true);
      return;
    }
    confirmarGuardarEnEspera();
  };

  const confirmarGuardarEnEspera = async (cliente = null) => {
    const nombre = cliente ? cliente.nombre : clienteNombre;
    const telefono = cliente ? cliente.telefono : clienteTelefono;
    const cedula = cliente ? cliente.cedula : clienteCedula;
    setGuardando(true);
    const items = carrito.map((c) => ({
      codigo: c.codigo,
      nombre: c.descripcion,
      cantidad: c.cantidad,
      precio_unitario: parseFloat(c.precio) || 0,
      costo_unitario: c.costo || 0,
      etiquetas: (c.etiquetas || []).map((e) => ({
        id: e.id,
        nombre: e.nombre,
        precio: e.precio || 0,
      })),
      combo_id: c.combo_id || null,
      componentes: c.componentes || null,
      combo_extra: c.combo_extra || 0,
      precio_manual: !!c.precio_manual,
    }));
    try {
      if (pedidoEditandoId) {
        await api.put(`/pedidos/tienda/${pedidoEditandoId}/productos`, items);
        if (nombre || telefono || cedula) {
          await api.put(`/pedidos/tienda/${pedidoEditandoId}/cliente`, {
            cliente_nombre: nombre || null,
            cliente_telefono: telefono || null,
            cliente_cedula: cedula || null,
          });
        }
        toast('Pedido actualizado en espera', 'success');
      } else {
        await api.post('/pedidos/tienda/crear', {
          items,
          sede_codigo: sede,
          mesa_id: mesaActiva?.id,
          cliente_nombre: nombre,
          cliente_cedula: cedula,
          cliente_telefono: telefono,
          en_espera: true,
        });
        toast('Pedido guardado en espera', 'success');
      }
      setCarrito([]);
      setMesaActiva(null);
      setClienteInfo({ nombre: '', telefono: '', cedula: '' });
      setPedidoEditandoId(null);
      if (setVista && !pedidoEditandoId) setVista('espera');
    } catch (e) {
      toast(e.response?.data?.detail || 'Error al guardar', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const cerrarEdicionPedido = () => {
    setCarrito([]);
    setMesaActiva(null);
    setClienteInfo({ nombre: '', telefono: '', cedula: '' });
    setPedidoEditandoId(null);
    if (setVista) setVista('espera');
  };

  const ProductoCard = ({ p, index }) => {
    const enCarrito = carrito.find((c) => c.codigo === p.codigo);
    const etiquetasCount = (p.etiquetas || []).length;
    const etiquetasDefaultCount = (p.etiquetas || []).filter((e) => e.activa_por_defecto).length;
    const selected = navZone === 'catalogo' && navIndex === index;
    return (
      <div
        ref={(el) => {
          if (el) productCardRefs.current[p.codigo] = el;
        }}
        onClick={() => agregarAlCarrito(p)}
        style={{
          background: selected ? COL.blueBg : COL.white,
          borderRadius: 14,
          border: `2px solid ${selected ? COL.pri2 : enCarrito ? COL.pri2 : COL.border}`,
          cursor: 'pointer',
          boxShadow: selected
            ? `0 0 0 3px ${COL.pri2}30`
            : enCarrito
              ? `0 2px 12px ${COL.pri2}20`
              : '0 1px 3px rgba(0,0,0,0.04)',
          overflow: 'hidden',
          transition: 'all 0.15s',
          userSelect: 'none',
          position: 'relative',
        }}
      >
        {enCarrito && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              background: COL.pri2,
              color: '#fff',
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            {enCarrito.cantidad}
          </div>
        )}
        {etiquetasCount > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
              background: COL.tagPurpleBg,
              color: COL.tagPurpleText,
              padding: '3px 7px',
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <FiEdit2 size={10} /> {etiquetasDefaultCount || etiquetasCount}
          </div>
        )}
        {p.imagen_url ? (
          <img
            src={getImagenUrl(p.imagen_url)}
            alt=""
            style={{ width: '100%', height: isMobile ? 100 : 130, objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: isMobile ? 100 : 130,
              background: `linear-gradient(135deg, ${COL.bg}, ${COL.bg2})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FiPackage size={28} color={COL.faint} />
          </div>
        )}
        <div style={{ padding: '8px 11px 10px' }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: COL.text,
              lineHeight: 1.3,
              wordBreak: 'break-word',
              minHeight: 32,
            }}
          >
            {p.descripcion}
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: COL.pri2, marginTop: 3 }}>
            {fmt(p.precio)}
          </div>
          {p.tipo_producto !== 'servicio' && p.stock != null && p.stock < 5 && (
            <div style={{ fontSize: 10, color: COL.red, marginTop: 2, fontWeight: 500 }}>
              Quedan {p.stock}
            </div>
          )}
        </div>
      </div>
    );
  };

  const ProductoFila = ({ p, index }) => {
    const enCarrito = carrito.find((c) => c.codigo === p.codigo);
    const etiquetasCount = (p.etiquetas || []).length;
    const selected = navZone === 'catalogo' && navIndex === index;
    return (
      <div
        ref={(el) => {
          if (el) productCardRefs.current[p.codigo] = el;
        }}
        onClick={() => agregarAlCarrito(p)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 12px',
          borderRadius: 12,
          border: `2px solid ${selected ? COL.pri2 : enCarrito ? COL.pri2 : COL.border}`,
          background: selected ? COL.blueBg : enCarrito ? COL.blueBg : COL.white,
          cursor: 'pointer',
          boxShadow: selected
            ? `0 0 0 3px ${COL.pri2}30`
            : enCarrito
              ? `0 2px 10px ${COL.pri2}18`
              : '0 1px 2px rgba(0,0,0,0.03)',
          transition: 'all 0.12s',
          userSelect: 'none',
          position: 'relative',
        }}
      >
        {p.imagen_url ? (
          <img
            src={getImagenUrl(p.imagen_url)}
            alt=""
            style={{
              width: 46,
              height: 46,
              borderRadius: 10,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 10,
              background: COL.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FiPackage size={18} color={COL.faint} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: COL.text,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {p.descripcion}
          </div>
          {etiquetasCount > 0 && (
            <div
              style={{
                fontSize: 10,
                color: COL.tagPurpleText,
                marginTop: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <FiEdit2 size={10} /> Personalizable
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: COL.pri2 }}>
            {fmt(p.precio)}
          </div>
          {p.tipo_producto !== 'servicio' && p.stock != null && p.stock < 5 && (
            <div style={{ fontSize: 10, color: COL.red, fontWeight: 500 }}>Quedan {p.stock}</div>
          )}
        </div>
        {enCarrito && (
          <div
            style={{
              position: 'absolute',
              top: -7,
              right: -7,
              background: COL.pri2,
              color: '#fff',
              minWidth: 22,
              height: 22,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 800,
              padding: '0 4px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }}
          >
            {enCarrito.cantidad}
          </div>
        )}
      </div>
    );
  };

  const CartItem = ({ c, i }) => {
    const selected = navZone === 'carrito' && navIndex === i;
    return (
    <div
      ref={(el) => {
        if (el) cartItemRefs.current[i] = el;
      }}
      style={{
        padding: '10px 14px',
        borderBottom: `1px solid ${COL.border}`,
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        background: selected ? COL.blueBg : COL.white,
        boxShadow: selected ? `inset 3px 0 0 ${COL.pri2}` : 'none',
        transition: 'background 0.15s',
      }}
    >
      {c.imagen_url ? (
        <img
          src={getImagenUrl(c.imagen_url)}
          alt=""
          style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: COL.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FiPackage size={16} color={COL.faint} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            lineHeight: 1.3,
            wordBreak: 'break-word',
            color: COL.text,
          }}
        >
          {c.descripcion}
        </div>
        <div style={{ fontSize: 11, color: COL.muted, marginTop: 1 }}>
          {c.precio_manual ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
              <input
                type="text"
                inputMode="decimal"
                value={c.precio ?? ''}
                placeholder="0.00"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') e.target.blur();
                }}
                onChange={(e) => cambiarPrecioManual(i, sanitizarPrecioManual(e.target.value))}
                style={{
                  width: 100,
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: '1px solid #c9ced6',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  color: COL.text,
                  background: '#fff',
                }}
              />
              <span>c/u (manual)</span>
            </span>
          ) : (
            <>
              {fmt(c.precio)} c/u
              {c.combo_extra > 0 && (
                <span style={{ color: COL.tagOrangeText }}>
                  {' '}+ {fmt(c.combo_extra)} personalización
                </span>
              )}
            </>
          )}
        </div>
        {(c.etiquetas || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
            {c.etiquetas.map((et, ei) => (
              <span
                key={ei}
                onClick={(e) => {
                  e.stopPropagation();
                  showEtiquetasEditor(c, i);
                }}
                style={{
                  fontSize: 9,
                  fontWeight: 500,
                  background: COL.tagOrangeBg,
                  color: COL.tagOrangeText,
                  padding: '2px 5px',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                {et.nombre}
                {et.precio > 0 ? ` +${fmt(et.precio)}` : ''}
              </span>
            ))}
          </div>
        )}
        {c.combo_id && Array.isArray(c.componentes) && c.componentes.length > 0 && (
          <div style={{ marginTop: 4, paddingLeft: 2 }}>
            {c.componentes.map((comp, ci) => {
              const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
              return (
                <div
                  key={ci}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 5,
                    fontSize: 10,
                    color: COL.muted,
                    padding: '1px 0',
                    lineHeight: 1.35,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: '#f0b400',
                      flexShrink: 0,
                      marginTop: 4,
                    }}
                  />
                  <span style={{ wordBreak: 'break-word' }}>
                    {comp.nombre}
                    {compEtiq.length > 0 && (
                      <span style={{ color: COL.faint }}> ({compEtiq.map((e) => e.nombre).join(', ')})</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
        <button onClick={() => cambiarCantidad(i, -1)} style={S.qtyBtn}>
          <FiMinus size={12} />
        </button>
        {selected ? (
          <input
            type="text"
            inputMode="numeric"
            value={qtyBuffer}
            placeholder={String(c.cantidad)}
            onClick={(e) => e.stopPropagation()}
            onBlur={() => setQtyBuffer('')}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                if (qtyBuffer !== '') {
                  setCantidadDirecta(i, qtyBuffer);
                  setQtyBuffer('');
                }
              } else if (e.key === 'Escape') {
                setQtyBuffer('');
              }
            }}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
              setQtyBuffer(v);
            }}
            style={{
              width: 46,
              textAlign: 'center',
              fontWeight: 700,
              fontSize: 14,
              fontFamily: 'inherit',
              color: COL.text,
              border: `2px solid ${COL.pri2}`,
              borderRadius: 8,
              padding: '3px 2px',
              background: COL.white,
              outline: 'none',
            }}
          />
        ) : (
          <span
            style={{
              minWidth: 26,
              textAlign: 'center',
              fontWeight: 700,
              fontSize: 14,
              color: COL.text,
            }}
          >
            {c.cantidad}
          </span>
        )}
        <button onClick={() => cambiarCantidad(i, 1)} style={S.qtyBtn}>
          <FiPlus size={12} />
        </button>
      </div>
      <button
        onClick={() => eliminarItem(i)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: COL.faint,
          padding: 4,
          borderRadius: 8,
          transition: 'all 0.15s',
        }}
        title="Eliminar"
      >
        <FiTrash2 size={14} />
      </button>
    </div>
    );
  };

  const CartPanel = ({ compact }) => (
    <div
      style={{
        width: compact ? '100%' : 380,
        background: COL.white,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: compact ? 'none' : `1px solid ${COL.border}`,
        flexShrink: 0,
        height: '100%',
      }}
    >
      <div
        style={{
          padding: '14px 16px',
          background: pedidoEditandoId
            ? `linear-gradient(135deg, ${COL.amber}, ${COL.orange})`
            : `linear-gradient(135deg, ${COL.pri}, ${COL.pri2})`,
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 15 }}>
          <FiShoppingCart size={16} style={{ marginRight: 8 }} />
          {pedidoEditandoId ? `Editando #${pedidoEditandoId}` : 'Carrito'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              background: 'rgba(255,255,255,0.22)',
              padding: '4px 12px',
              borderRadius: 14,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {itemCount} items
          </span>
          {pedidoEditandoId && (
            <button
              onClick={cerrarEdicionPedido}
              title="Cerrar pedido"
              style={{
                border: 'none',
                background: 'rgba(255,255,255,0.22)',
                borderRadius: 8,
                cursor: 'pointer',
                padding: 6,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FiX size={18} />
            </button>
          )}
        </div>
      </div>
      {mesaActiva && (
        <div
          style={{
            padding: '8px 16px',
            background: COL.amberBg,
            borderBottom: `1px solid ${COL.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            color: COL.amber,
            fontWeight: 600,
          }}
        >
          <FiMapPin size={14} /> Mesa {mesaActiva.nombre || mesaActiva.codigo}
          {clienteNombre && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiUser size={13} /> {clienteNombre}
            </span>
          )}
          {pedidoEditandoId && (
            <button
              onClick={() => setShowEditarCliente(true)}
              title="Editar datos del cliente"
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                border: 'none',
                background: 'rgba(255,255,255,0.5)',
                color: COL.amber,
                padding: '3px 8px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 700,
                fontFamily: 'inherit',
              }}
            >
              <FiEdit2 size={12} /> Cliente
            </button>
          )}
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {carrito.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: COL.faint }}>
            <FiShoppingCart size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>Carrito vacio</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Selecciona productos del catalogo</div>
          </div>
        ) : (
          carrito.map((c, i) => (
            <Fragment key={c.codigo}>{CartItem({ c, i })}</Fragment>
          ))
        )}
      </div>
      <div style={{ padding: 14, borderTop: `2px solid ${COL.border}`, flexShrink: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 2,
          }}
        >
          <span style={{ fontSize: 12, color: COL.muted, fontWeight: 500 }}>Total</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: COL.text }}>{fmt(total)}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
          {has('pedido_cobrar') && (
            <button
              onClick={abrirPago}
              disabled={carrito.length === 0}
              style={S.btnPrimary(carrito.length === 0)}
            >
              <FiCreditCard size={18} /> {pedidoEditandoId ? 'Cobrar y Cerrar ' : 'Cobrar '}
              {fmt(total)}
            </button>
          )}
          {carrito.length > 0 && !pedidoEditandoId && has('pedido_espera') && (
            <button onClick={guardarEnEspera} disabled={guardando} style={S.btnOutline(COL.pri2)}>
              <FiSave size={15} /> {guardando ? 'Guardando...' : 'Guardar en Espera'}
            </button>
          )}
          {carrito.length > 0 && (
            <button
              onClick={() => {
                setCarrito([]);
                setClienteInfo({ nombre: '', telefono: '', cedula: '' });
                setPedidoEditandoId(null);
              }}
              style={{
                padding: 8,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: COL.faint,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 500,
                fontFamily: 'inherit',
              }}
            >
              <FiTrash2 size={12} style={{ marginRight: 4 }} /> Vaciar carrito
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <div
          style={{
            padding: '10px 14px',
            background: COL.white,
            borderBottom: `1px solid ${COL.border}`,
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <style>{`@keyframes agxSearchPop { 0%{transform:scale(1)} 45%{transform:scale(1.03)} 100%{transform:scale(1)} } @keyframes agxSearchRing { 0%{box-shadow:0 0 0 0 rgba(41,82,204,0.5)} 100%{box-shadow:0 0 0 5px rgba(41,82,204,0.12)} }`}</style>
            <FiSearch
              style={{
                position: 'absolute',
                left: 13,
                top: 12,
                color: searchFocused ? COL.pri2 : COL.faint,
                pointerEvents: 'none',
                zIndex: 1,
                transition: 'color 0.2s',
              }}
              size={17}
            />
            <input
              ref={searchRef}
              dir="ltr"
              autoComplete="off"
              spellCheck={false}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setBusqueda('');
                  setSearchFocused(false);
                  setNavZone('catalogo');
                  e.currentTarget.blur();
                }
              }}
              placeholder="Buscar producto..."
              style={{
                width: '100%',
                padding: '11px 40px 11px 42px',
                borderRadius: 14,
                border: `2px solid ${busqueda || searchFocused ? COL.pri2 : COL.border}`,
                fontSize: 14,
                outline: 'none',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                background: COL.bg,
                boxShadow: searchFocused ? '0 0 0 3px rgba(41,82,204,0.22)' : 'none',
                animation: searchFocused ? 'agxSearchPop 0.28s ease-out, agxSearchRing 0.5s ease-out' : 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
            />
            {busqueda && (
              <button
                onClick={() => {
                  setBusqueda('');
                  searchRef.current?.focus();
                }}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 12,
                  border: 'none',
                  background: COL.border,
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: COL.muted,
                }}
              >
                <FiX size={12} />
              </button>
            )}
            <BotonEscanear
              onScan={(codigo) => { setBusqueda(codigo); setSearchFocused(true); }}
              style={{ position: 'absolute', right: 40, top: 11, zIndex: 2 }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              background: COL.bg,
              borderRadius: 12,
              padding: 3,
              gap: 3,
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => setVistaCatalogo('grid')}
              title="Vista cuadricula"
              style={{
                width: 36,
                height: 34,
                borderRadius: 9,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: vistaCatalogo === 'grid' ? COL.white : 'transparent',
                color: vistaCatalogo === 'grid' ? COL.pri2 : COL.muted,
                boxShadow: vistaCatalogo === 'grid' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              <FiGrid size={16} />
            </button>
            <button
              onClick={() => setVistaCatalogo('list')}
              title="Vista lista"
              style={{
                width: 36,
                height: 34,
                borderRadius: 9,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: vistaCatalogo === 'list' ? COL.white : 'transparent',
                color: vistaCatalogo === 'list' ? COL.pri2 : COL.muted,
                boxShadow: vistaCatalogo === 'list' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              <FiList size={16} />
            </button>
          </div>
          <button
            onClick={() => setShowTeclas(true)}
            title="Atajos de teclado (?)"
            style={{
              width: 36, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: COL.bg, color: COL.muted, fontWeight: 700, fontSize: 14,
              flexShrink: 0, fontFamily: 'inherit',
            }}
          >
            ?
          </button>
        </div>
        <div
          style={{
            padding: '6px 14px',
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            background: COL.white,
            borderBottom: `1px solid ${COL.border}`,
            flexShrink: 0,
            overflowX: 'auto',
            scrollbarWidth: 'none',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 10,
              background: navZone === 'carrito' ? COL.amberBg : COL.blueBg,
              color: navZone === 'carrito' ? COL.amber : COL.pri2,
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              fontFamily: 'monospace',
            }}
            title="Selección actual (usa las flechas para moverte)"
          >
            {navZone === 'carrito'
              ? `Carrito ${carrito.length ? `${navIndex + 1}/${carrito.length}` : 'vacío'}`
              : resultados.length
                ? `Catálogo ${navIndex + 1}/${resultados.length}`
                : 'Catálogo'}
          </span>
          <button
            onClick={() => setShowDeptoModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 12,
              border: `1.5px solid ${COL.pri2}`,
              background: COL.priLight || COL.blueBg,
              color: COL.pri2,
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <FiGrid size={15} />
            {grupoFiltro
              ? `Grupo: ${gruposDepto.find((g) => g.codigo === grupoFiltro)?.nombre || grupoFiltro}`
              : deptoFiltro
                ? `Depto: ${deptoFiltro}`
                : 'Departamentos'}
            <FiChevronDown size={14} />
          </button>

          {(deptoFiltro || grupoFiltro) && (
            <button
              onClick={() => {
                setDeptoFiltro('');
                setGrupoFiltro('');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '8px 12px',
                borderRadius: 12,
                border: `1px solid ${COL.border}`,
                background: COL.surfaceAlt,
                color: COL.muted,
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <FiX size={13} /> Limpiar
            </button>
          )}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
          {!busqueda && !deptoFiltro && !grupoFiltro && combosTienda.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  color: COL.text,
                }}
              >
                <FiGrid size={14} /> Combos
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '170px'}, 1fr))`,
                  gap: 10,
                }}
              >
                {combosTienda.map((combo) => (
                  <div
                    key={combo.id}
                    onClick={() => agregarComboTienda(combo)}
                    style={{
                      borderRadius: 14,
                      border: `1.5px solid ${COL.border}`,
                      background: COL.white,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div
                      style={{
                        height: isMobile ? 90 : 110,
                        background: COL.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        flexShrink: 0,
                      }}
                    >
                      {(combo.imagen_url || combo.thumb_url) ? (
                        <img
                          src={`${API_URL}${combo.thumb_url || combo.imagen_url}`}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <FiPackage size={26} style={{ opacity: 0.35 }} />
                      )}
                      {combo.ahorro > 0 && (
                        <span
                          style={{
                            position: 'absolute',
                            top: 6,
                            left: 6,
                            fontSize: 10,
                            fontWeight: 700,
                            color: COL.green,
                            background: COL.greenBg || '#e6f4ea',
                            padding: '2px 7px',
                            borderRadius: 10,
                          }}
                        >
                          Ahorras {fmt(combo.ahorro)}
                        </span>
                      )}
                    </div>
                    <div style={{ padding: '9px 11px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: COL.text,
                          lineHeight: 1.3,
                          wordBreak: 'break-word',
                        }}
                      >
                        {combo.nombre}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: COL.pri2 }}>
                          {fmt(combo.precio || 0)}
                        </span>
                        {combo.precio_sin_descuento > 0 && combo.precio_sin_descuento > combo.precio && (
                          <span style={{ fontSize: 11, color: COL.muted, textDecoration: 'line-through' }}>
                            {fmt(combo.precio_sin_descuento)}
                          </span>
                        )}
                      </div>
                      {Array.isArray(combo.items) && combo.items.length > 0 && (
                        <div
                          style={{
                            fontSize: 10,
                            color: COL.muted,
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                          }}
                        >
                          {combo.items.map((it) => `${it.cantidad}× ${it.nombre}`).join(' · ')}
                        </div>
                      )}
                      <span
                        style={{
                          marginTop: 'auto',
                          fontSize: 10,
                          fontWeight: 700,
                          color: COL.pri2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          paddingTop: 4,
                        }}
                      >
                        <FiPlus size={12} /> Personalizar
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {loadingProd ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '180px'}, 1fr))`,
                gap: 10,
              }}
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    background: COL.white,
                    borderRadius: 14,
                    height: isMobile ? 160 : 200,
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <div
                    style={{
                      height: isMobile ? 100 : 130,
                      background: COL.bg,
                      borderRadius: '14px 14px 0 0',
                    }}
                  />
                  <div style={{ padding: 10 }}>
                    <div
                      style={{
                        height: 12,
                        background: COL.bg,
                        borderRadius: 6,
                        width: '70%',
                        marginBottom: 6,
                      }}
                    />
                    <div
                      style={{ height: 16, background: COL.bg, borderRadius: 8, width: '40%' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : errorProd ? (
            <ErrorBanner
              message="No se pudieron cargar los productos."
              onRetry={() => {
                setLoadingProd(true);
                setErrorProd(false);
                Promise.all([
                  cargarTodosProductos(sede).then((r) => setProductos(r)),
                  api
                    .get(`/catalogo/departamentos?sede=${sede}`)
                    .then((r) => setDepartamentos(r.data?.data || [])),
                ])
                  .finally(() => setLoadingProd(false))
                  .catch(() => setErrorProd(true));
              }}
            />
          ) : resultados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 50, color: COL.muted }}>
              <FiPackage size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {busqueda ? 'Sin resultados' : 'No hay productos'}
              </div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {busqueda
                  ? `No se encontro "${busqueda}"`
                  : 'Agrega productos desde el modulo de Inventario'}
              </div>
            </div>
          ) : vistaCatalogo === 'list' ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                paddingBottom: isMobile ? 0 : 20,
              }}
            >
              {resultados.map((p, idx) => (
                <Fragment key={p.codigo}>{ProductoFila({ p, index: idx })}</Fragment>
              ))}
            </div>
          ) : (
            <div
              ref={gridRef}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '180px'}, 1fr))`,
                gap: 10,
                paddingBottom: isMobile ? 0 : 20,
              }}
            >
              {resultados.map((p, idx) => (
                <Fragment key={p.codigo}>{ProductoCard({ p, index: idx })}</Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {isMobile ? (
        <>
          <CartPill itemCount={itemCount} total={fmt(total)} onClick={() => setShowCarrito(true)} />
          {showCarrito && (
            <BottomSheet
              open={true}
              onClose={() => setShowCarrito(false)}
              title="Carrito"
              height="70vh"
            >
              {CartPanel({ compact: true })}
            </BottomSheet>
          )}
        </>
      ) : (
        CartPanel({ compact: false })
      )}

      {showTeclas && (
        <div
          onClick={() => setShowTeclas(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1300,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COL.white, borderRadius: 16, padding: 20, maxWidth: 460, width: '100%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 800, fontSize: 16, color: COL.text }}>Atajos de teclado</span>
              <button onClick={() => setShowTeclas(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: COL.muted }}>
                <FiX size={18} />
              </button>
            </div>
            <TeclasLista COL={COL} />
            <button
              onClick={() => setShowTeclas(false)}
              style={{ marginTop: 14, width: '100%', padding: 10, borderRadius: 10, border: 'none', background: COL.pri2, color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {showEtiquetasModal && (
        <div
          onClick={(e) =>
            e.target === e.currentTarget &&
            (() => {
              setShowEtiquetasModal(null);
              setEtiquetasEditandoIdx(null);
            })()
          }
          style={{
            position: 'fixed',
            inset: 0,
            background: COL.overlay,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            ref={etiquetasPanelRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COL.surface,
              borderRadius: 20,
              width: isMobile ? '92%' : 420,
              maxHeight: '92vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{
                padding: '18px 22px',
                borderBottom: '1px solid #e8eaef',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#1F2937' }}>
                  {showEtiquetasModal.descripcion || showEtiquetasModal.nombre || ''}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  Personaliza tu pedido
                </div>
              </div>
              <button
                onClick={cerrarEtiquetas}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6B7280',
                  fontSize: 20,
                  padding: 4,
                }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: '16px 22px', maxHeight: '340px', overflowY: 'auto' }}>
              {(showEtiquetasModal.etiquetas || []).length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                    Opciones
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(showEtiquetasModal.etiquetas || []).map((et) => {
                      const sel = etiquetasSeleccionadas.find((e) => e.id === et.id);
                      return (
                        <button
                          key={et.id}
                          data-nav="chip"
                          onClick={() => {
                            setEtiquetasSeleccionadas((prev) => {
                              if (sel) return prev.filter((e) => e.id !== et.id);
                              return [
                                ...prev,
                                { id: et.id, nombre: et.nombre, precio: et.precio || 0 },
                              ];
                            });
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '8px 13px',
                            borderRadius: 999,
                            cursor: 'pointer',
                            border: sel ? '2px solid #2952CC' : '2px solid #e5e7eb',
                            background: sel ? '#2952CC' : COL.white,
                            color: sel ? '#fff' : '#1F2937',
                            fontSize: 13,
                            fontWeight: sel ? 700 : 500,
                            fontFamily: 'inherit',
                            transition: 'all 0.15s',
                          }}
                        >
                          {sel && <FiCheck size={13} />}
                          {et.nombre}
                          {et.precio > 0 && (
                            <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.9 }}>
                              +{fmt(et.precio)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  Nota personalizada
                </div>
                {(() => {
                  const manuales = etiquetasSeleccionadas.filter(
                    (e) => typeof e.id === 'string' && e.id.startsWith('manual_')
                  );
                  return manuales.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '10px' }}>
                      {manuales.map((e) => (
                        <span
                          key={e.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 11px',
                            borderRadius: 999,
                            background: '#fef7e0',
                            color: '#b06000',
                            fontSize: 13,
                            fontWeight: 600,
                            border: '1px solid #f3d78a',
                          }}
                        >
                          {e.nombre}
                          <button
                            onClick={() =>
                              setEtiquetasSeleccionadas((prev) => prev.filter((x) => x.id !== e.id))
                            }
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              color: '#b06000',
                              padding: 0,
                              display: 'flex',
                            }}
                          >
                            <FiX size={13} />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : null;
                })()}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    data-nav="nota"
                    value={etiquetaManualInput}
                    onChange={(e) => setEtiquetaManualInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const nom = etiquetaManualInput.trim();
                        if (!nom) return;
                        setEtiquetasSeleccionadas((prev) => [
                          ...prev,
                          { id: 'manual_' + Date.now().toString(36), nombre: nom, precio: 0 },
                        ]);
                        setEtiquetaManualInput('');
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        etiquetasAgregarRef.current?.focus();
                      }
                    }}
                    placeholder="Ej: sin cebolla, bien cocido..."
                    maxLength={80}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: '1.5px solid #e5e7eb',
                      fontSize: 14,
                      fontFamily: 'inherit',
                      color: '#1F2937',
                      outline: 'none',
                      background: COL.white,
                    }}
                  />
                  <button
                    onClick={() => {
                      const nom = etiquetaManualInput.trim();
                      if (!nom) return;
                      setEtiquetasSeleccionadas((prev) => [
                        ...prev,
                        { id: 'manual_' + Date.now().toString(36), nombre: nom, precio: 0 },
                      ]);
                      setEtiquetaManualInput('');
                    }}
                    disabled={!etiquetaManualInput.trim()}
                    style={{
                      padding: '0 16px',
                      borderRadius: 12,
                      border: 'none',
                      background: '#2952CC',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: etiquetaManualInput.trim() ? 'pointer' : 'default',
                      opacity: etiquetaManualInput.trim() ? 1 : 0.5,
                      fontFamily: 'inherit',
                    }}
                  >
                    Agregar
                  </button>
                </div>
              </div>
            </div>
            <div
              style={{
                padding: '14px 22px',
                borderTop: '1px solid #e8eaef',
                display: 'flex',
                gap: 10,
              }}
            >
              <button
                onClick={cerrarEtiquetas}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 14,
                  border: `2px solid ${COL.border}`,
                  background: COL.white,
                  color: COL.muted,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                ref={etiquetasAgregarRef}
                onClick={() => {
                  if (etiquetasEditandoIdx !== null) {
                    setCarrito((prev) => {
                      const n = [...prev];
                      n[etiquetasEditandoIdx] = {
                        ...n[etiquetasEditandoIdx],
                        etiquetas: etiquetasSeleccionadas,
                      };
                      if (pedidoEditandoId) debouncedSave(n, pedidoEditandoId);
                      return n;
                    });
                    setEtiquetasEditandoIdx(null);
                  } else {
                    _agregarAlCarritoDirecto(showEtiquetasModal, etiquetasSeleccionadas);
                  }
                  setShowEtiquetasModal(null);
                }}
                style={{
                  flex: 2,
                  padding: 12,
                  borderRadius: 14,
                  border: 'none',
                  background: '#2952CC',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                }}
              >
                {etiquetasEditandoIdx !== null ? 'Guardar' : 'Agregar'}
                {etiquetasSeleccionadas.length > 0 ? ` (${etiquetasSeleccionadas.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModalCaja && <ModalAbrirCaja onClose={() => setShowModalCaja(false)} onAbierta={() => { setShowModalCaja(false); abrirPago(); }} />}

      {showDeptoModal && (
        <div
          onClick={(e) => e.target === e.currentTarget && setShowDeptoModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: COL.overlay,
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
            padding: 16,
          }}
        >
          <div
            ref={deptosPanelRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COL.surface,
              borderRadius: 20,
              width: isMobile ? '100%' : 640,
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{
                padding: '18px 22px',
                borderBottom: `1px solid ${COL.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                background: COL.surface,
                zIndex: 1,
              }}
            >
              <div>
                <div style={{ fontWeight: 800, fontSize: 17, color: COL.text }}>
                  Departamentos
                </div>
                <div style={{ fontSize: 12, color: COL.muted, marginTop: 2 }}>
                  Toca o navega para filtrar el catálogo
                </div>
              </div>
              <button
                onClick={cerrarDeptos}
                style={{
                  background: COL.bg,
                  border: 'none',
                  cursor: 'pointer',
                  color: COL.muted,
                  fontSize: 20,
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: 16 }}>
              {/* Búsqueda de departamentos */}
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <FiSearch
                  style={{ position: 'absolute', left: 12, top: 12, color: COL.faint, pointerEvents: 'none' }}
                  size={15}
                />
                <input
                  autoFocus
                  value={deptSearch}
                  onChange={(e) => setDeptSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      if (deptSearch) {
                        setDeptSearch('');
                      } else {
                        cerrarDeptos();
                      }
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      deptosPrimerRef.current?.focus();
                    }
                  }}
                  placeholder="Escribe para filtrar departamentos..."
                  style={{
                    width: '100%',
                    padding: '11px 12px 11px 36px',
                    borderRadius: 12,
                    border: `2px solid ${deptSearch ? COL.pri2 : COL.border}`,
                    fontSize: 14,
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    background: COL.white,
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    boxShadow: deptSearch ? '0 0 0 3px rgba(41,82,204,0.18)' : 'none',
                  }}
                />
              </div>
              {/* Todos */}
              <button
                ref={deptosPrimerRef}
                onClick={() => {
                  setDeptoFiltro('');
                  setGrupoFiltro('');
                  setDeptSearch('');
                  setShowDeptoModal(false);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 16px',
                  borderRadius: 16,
                  border: `2px solid ${!deptoFiltro && !grupoFiltro ? COL.pri2 : COL.border}`,
                  background: !deptoFiltro && !grupoFiltro ? COL.priLight || COL.blueBg : COL.surfaceAlt,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: COL.pri2,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FiGrid size={24} />
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: COL.text }}>Todos</div>
                  <div style={{ fontSize: 12, color: COL.muted, marginTop: 1 }}>
                    {productos.length} productos
                  </div>
                </div>
                {!deptoFiltro && !grupoFiltro && <FiCheck size={20} color={COL.pri2} />}
              </button>

              {/* Toggle Ver subgrupos */}
              <button
                onClick={() => setMostrarSubgrupos((v) => !v)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: `1.5px solid ${COL.border}`,
                  background: COL.bg,
                  color: COL.pri2,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginBottom: 14,
                }}
              >
                {mostrarSubgrupos ? 'Ocultar subgrupos' : 'Ver subgrupos'}
                <FiChevronDown
                  size={14}
                  style={{ transform: mostrarSubgrupos ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                />
              </button>

              {/* Grid de grupos */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '170px'}, 1fr))`,
                  gap: 10,
                }}
              >
                {deptGroupsFiltrados.map((g) => {
                  const count = productos.filter((p) => p.grupo === g.codigo).length;
                  const meta = getDeptMeta(g.nombre);
                  const Icon = meta.icon;
                  const sel = grupoFiltro === g.codigo;
                  return (
                    <div
                      key={g.codigo || g.nombre}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        gridColumn: mostrarSubdeptos ? '1 / -1' : 'auto',
                      }}
                    >
                      <button
                        onClick={() => {
                          setDeptoFiltro('');
                          setGrupoFiltro(sel ? '' : g.codigo);
                          setDeptSearch('');
                          setShowDeptoModal(false);
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 10,
                          padding: '16px 10px 14px',
                          borderRadius: 16,
                          border: `2px solid ${sel ? meta.color : COL.border}`,
                          background: sel ? `${meta.color}14` : COL.surfaceAlt,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          transition: 'all 0.12s',
                        }}
                      >
                        <div
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 16,
                            background: `${meta.color}1f`,
                            color: meta.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon size={28} />
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: COL.text,
                            textAlign: 'center',
                            lineHeight: 1.25,
                            wordBreak: 'break-word',
                            minHeight: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {g.nombre}
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: sel ? meta.color : COL.muted,
                            background: sel ? `${meta.color}1f` : COL.bg,
                            padding: '2px 9px',
                            borderRadius: 999,
                          }}
                        >
                          {mostrarSubdeptos ? g.depts.length : count}
                        </span>
                      </button>

                      {mostrarSubdeptos &&
                        g.depts.map((d) => {
                          const dSel = deptoFiltro === d.nombre;
                          return (
                            <button
                              key={d.codigo}
                              onClick={() => {
                                setGrupoFiltro('');
                                setDeptoFiltro(dSel ? '' : d.nombre);
                                setDeptSearch('');
                                setShowDeptoModal(false);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '10px 14px',
                                borderRadius: 12,
                                border: `1.5px solid ${dSel ? COL.pri2 : COL.border}`,
                                background: dSel ? COL.priLight || COL.blueBg : COL.surfaceAlt,
                                color: dSel ? COL.pri2 : COL.text,
                                fontWeight: dSel ? 700 : 500,
                                fontSize: 13,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                textAlign: 'left',
                              }}
                            >
                              <span style={{ flex: 1 }}>{d.nombre}</span>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: dSel ? COL.pri2 : COL.muted,
                                  background: dSel ? `${COL.pri2}1f` : COL.bg,
                                  padding: '2px 9px',
                                  borderRadius: 999,
                                }}
                              >
                                {productos.filter((p) => p.departamento === d.nombre).length}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {comboDetalle && (
        <div
          onClick={(e) => e.target === e.currentTarget && cerrarComboDetalle()}
          style={{
            position: 'fixed',
            inset: 0,
            background: COL.overlay,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COL.surface,
              borderRadius: 20,
              width: isMobile ? '92%' : 460,
              maxHeight: '92vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{
                padding: '18px 22px',
                borderBottom: '1px solid #e8eaef',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#1F2937' }}>
                  {comboDetalle.nombre}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  Personaliza tu combo
                </div>
              </div>
              <button
                onClick={cerrarComboDetalle}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: 20, padding: 4 }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: '12px 22px', maxHeight: '52vh', overflowY: 'auto' }}>
              {instanciasCombo(comboDetalle.items).map((it) => {
                const disponibles = it.etiquetas_disponibles || [];
                const seleccionados = comboSelEtiquetas[`${it.codigo_producto}#${it._instancia}`] || [];
                const esMultiple = (parseInt(it.cantidad, 10) || 1) > 1;
                return (
                  <div
                    key={`${it.codigo_producto}#${it._instancia}`}
                    style={{
                      borderRadius: 12,
                      border: `1px solid ${COL.border}`,
                      background: COL.white,
                      padding: '12px 14px',
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
                        {esMultiple && (
                          <span style={{ color: COL.pri2, fontWeight: 700, marginRight: 6 }}>
                            #{it._instancia + 1}
                          </span>
                        )}
                        {it.nombre}
                      </div>
                      <div style={{ fontSize: 11, color: COL.muted, whiteSpace: 'nowrap' }}>Incluido</div>
                    </div>
                    {disponibles.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {disponibles.map((et) => {
                          const sel = seleccionados.includes(et.id);
                          if (et.incluida) {
                            return (
                              <span
                                key={et.id}
                                style={{
                                  padding: '5px 10px',
                                  borderRadius: '20px',
                                  border: `1px solid ${COL.border}`,
                                  background: COL.bg,
                                  color: COL.muted,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                <span style={{ color: COL.pri2, fontSize: 10 }}>✓</span>
                                {et.nombre} <span style={{ opacity: 0.75 }}>incluido</span>
                              </span>
                            );
                          }
                          return (
                            <button
                              key={et.id}
                              onClick={() => toggleEtiquetaCombo(it.codigo_producto, it._instancia, et)}
                              style={{
                                padding: '5px 10px',
                                borderRadius: '20px',
                                border: `1px solid ${sel ? COL.pri2 : COL.border}`,
                                background: sel ? COL.tagPurpleBg : COL.white,
                                color: sel ? COL.pri2 : COL.muted,
                                fontSize: 11,
                                fontWeight: sel ? 700 : 500,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                              }}
                            >
                              {et.nombre}
                              {et.precio > 0 ? ` (+${fmt(et.precio)})` : ''}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Nota manual del componente */}
                    {(() => {
                      const key = `${it.codigo_producto}#${it._instancia}`;
                      const manuales = comboEtiquetasManuales[key] || [];
                      return (
                        <div style={{ marginTop: 8 }}>
                          {manuales.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                              {manuales.map((e) => (
                                <span
                                  key={e.id}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '4px 9px',
                                    borderRadius: 999,
                                    background: '#fef7e0',
                                    color: '#b06000',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    border: '1px solid #f3d78a',
                                  }}
                                >
                                  {e.nombre}
                                  <button
                                    onClick={() => quitarEtiquetaManualCombo(key, e.id)}
                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#b06000', padding: 0, display: 'flex' }}
                                  >
                                    <FiX size={12} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <ComboNotaInput onAgregar={(nom) => agregarEtiquetaManualCombo(key, nom)} />
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
            <div
              style={{
                padding: '14px 22px',
                borderTop: '1px solid #e8eaef',
                display: 'flex',
                gap: 10,
                alignItems: 'center',
              }}
            >
              <button
                onClick={cerrarComboDetalle}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 14,
                  border: `2px solid ${COL.border}`,
                  background: COL.white,
                  color: COL.muted,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={agregarComboPersonalizado}
                style={{
                  flex: 2,
                  padding: 12,
                  borderRadius: 14,
                  border: 'none',
                  background: '#2952CC',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                }}
              >
                Agregar · {fmt(comboDetalle.precio || 0)}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPago && (
        <div
          onClick={(e) => e.target === e.currentTarget && setShowPago(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: COL.overlay,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              background: COL.surface,
              borderRadius: 20,
              width: isMobile ? '92%' : 460,
              maxHeight: '92vh',
              overflow: 'auto',
              padding: 28,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <PagoSteps
              pagoPaso={pagoPaso}
              setPagoPaso={setPagoPaso}
              total={total}
              subtotal={subtotal}
              impuesto={impuestoTotal}
              cobrarImpuesto={cobrarImpuesto}
              setCobrarImpuesto={setCobrarImpuesto}
              pagosLista={pagosLista}
              setPagosLista={setPagosLista}
              metodosPago={metodosPago}
              clienteNombre={clienteNombre}
              setClienteNombre={setClienteNombre}
              clienteCedula={clienteCedula}
              setClienteCedula={setClienteCedula}
              clienteTelefono={clienteTelefono}
              setClienteTelefono={setClienteTelefono}
              onConfirm={confirmarPago}
              onClose={() => setShowPago(false)}
            />
          </div>
        </div>
      )}

      {showEditarCliente && (
        <ClienteModal
          titulo="Datos del cliente"
          initial={{ nombre: clienteNombre, telefono: clienteTelefono, cedula: clienteCedula }}
          confirmLabel="Guardar"
          onClose={() => setShowEditarCliente(false)}
          onConfirm={async (info) => {
            setShowEditarCliente(false);
            setClienteInfo(info);
            if (pedidoEditandoId) {
              try {
                await api.put(`/pedidos/tienda/${pedidoEditandoId}/cliente`, {
                  cliente_nombre: info.nombre || null,
                  cliente_telefono: info.telefono || null,
                  cliente_cedula: info.cedula || null,
                });
                toast('Datos del cliente actualizados', 'success');
              } catch (e) {
                toast(e.response?.data?.detail || 'Error al actualizar el cliente', 'error');
              }
            }
          }}
        />
      )}

      {showClienteGuardar && (
        <ClienteModal
          titulo="Datos del cliente"
          initial={clienteInfo}
          confirmLabel="Guardar en espera"
          onClose={() => setShowClienteGuardar(false)}
          onConfirm={(info) => {
            setShowClienteGuardar(false);
            setClienteInfo(info);
            confirmarGuardarEnEspera(info);
          }}
        />
      )}
      <style>{`@keyframes pulse { 0%,100%{opacity:.7} 50%{opacity:.3} }`}</style>
    </div>
  );
}

// ═══════════ BÚSQUEDA DE CLIENTE REGISTRADO ═══════════

// Debounce de busqueda: lo mas corto posible para que se sienta instantanea sin
// disparar una peticion por cada tecla. Con los indices trigrama del backend, cada
// consulta responde en ms, asi que 90ms es suficiente para coalescer tecleos rapidos.
const CLIENTE_SEARCH_DEBOUNCE = 90;

// Hook: cuando el usuario tipea una cédula, busca por cédula (prioridad) y, si
// encuentra coincidencia EXACTA, carga los datos guardados en BD (nombre/teléfono).
function useClientePorCedula(cedula, onFound) {
  const timerRef = useRef(null);
  useEffect(() => {
    const c = (cedula || '').trim();
    if (c.length < 6) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      api
        .get('/pedidos/tienda/buscar-cliente', { params: { q: c } })
        .then((r) => {
          const lista = r.data?.data || [];
          const exacto = lista.find((x) => (x.cedula || '').toUpperCase() === c.toUpperCase());
          if (exacto) onFound(exacto);
        })
        .catch(() => {});
    }, CLIENTE_SEARCH_DEBOUNCE);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cedula]);
}

function ClienteBusqueda({ onSelect, COL, S }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const termino = (q || '').trim();
    if (termino.length < 2) {
      setRes([]);
      setAbierto(false);
      return;
    }
    timerRef.current = setTimeout(() => {
      api
        .get('/pedidos/tienda/buscar-cliente', { params: { q: termino } })
        .then((r) => {
          setRes(r.data?.data || []);
          setAbierto(true);
        })
        .catch(() => setRes([]));
    }, CLIENTE_SEARCH_DEBOUNCE);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  return (
    <div style={{ position: 'relative', marginBottom: 14 }}>
      <label style={S.formLabel}>
        <FiSearch size={14} /> Buscar cliente registrado
      </label>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Nombre, cédula o teléfono"
        style={S.formInput}
      />
      {abierto && res.length > 0 && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            zIndex: 20,
            background: COL.surface,
            border: `1px solid ${COL.border}`,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
          }}
        >
          {res.map((c) => (
            <button
              key={c.id}
              onClick={() => {
                onSelect(c);
                setAbierto(false);
                setQ('');
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                border: 'none',
                borderBottom: `1px solid ${COL.line}`,
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <div style={{ fontWeight: 700, color: COL.text }}>{c.nombre || 'Sin nombre'}</div>
              <div style={{ fontSize: 11, color: COL.muted }}>
                {[c.cedula, c.telefono].filter(Boolean).join(' · ') || 'Sin datos'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════ MODAL DE CLIENTE ═══════════
function ClienteModal({ titulo, initial, confirmLabel = 'Continuar', onConfirm, onClose }) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const [nombre, setNombre] = useState(initial?.nombre || '');
  const [telefono, setTelefono] = useState(initial?.telefono || '');
  const [cedula, setCedula] = useState(initial?.cedula || '');
  const [clienteId, setClienteId] = useState(initial?.id || null);

  const seleccionarCliente = (c) => {
    setNombre(c.nombre || '');
    setTelefono(c.telefono || '');
    setCedula(c.cedula || '');
    setClienteId(c.id || null);
  };

  useClientePorCedula(cedula, (c) => {
    setNombre(c.nombre || '');
    setTelefono(c.telefono || '');
    setClienteId(c.id || null);
  });

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: COL.overlay,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        style={{
          background: COL.surface,
          borderRadius: 20,
          width: 460,
          maxWidth: '92%',
          maxHeight: '92vh',
          overflow: 'auto',
          padding: 28,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}
        >
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: COL.text }}>{titulo}</div>
            <div style={{ fontSize: 13, color: COL.muted, marginTop: 2 }}>Todos los campos son opcionales</div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: COL.bg,
              borderRadius: 12,
              cursor: 'pointer',
              padding: 10,
              color: COL.muted,
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        <ClienteBusqueda onSelect={seleccionarCliente} COL={COL} S={S} />

        <div style={{ marginBottom: 14 }}>
          <label style={S.formLabel}>
            <FiUser size={14} /> Nombre del cliente
          </label>
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Opcional"
            autoFocus
            style={S.formInput}
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={S.formLabel}>
            <FiPhone size={14} /> Telefono
          </label>
          <input
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            placeholder="Opcional"
            style={S.formInput}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={S.formLabel}>
            <FiHash size={14} /> Cedula / RIF
          </label>
          <input
            value={cedula}
            onChange={(e) => setCedula(e.target.value)}
            placeholder="Opcional"
            style={S.formInput}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={S.btnOutline(COL.muted)}>
            Cancelar
          </button>
          <button
            onClick={() =>
              onConfirm({
                id: clienteId,
                nombre: nombre.trim(),
                telefono: telefono.trim(),
                cedula: cedula.trim(),
              })
            }
            style={{ ...S.btnFull(COL.pri2), flex: 2 }}
          >
            {confirmLabel} <FiChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════ PAGO STEPS ═══════════
function PagoSteps({
  pagoPaso,
  setPagoPaso,
  total,
  subtotal,
  impuesto,
  cobrarImpuesto,
  setCobrarImpuesto,
  pagosLista,
  setPagosLista,
  metodosPago,
  clienteNombre,
  setClienteNombre,
  clienteCedula,
  setClienteCedula,
  clienteTelefono,
  setClienteTelefono,
  onConfirm,
  onClose,
}) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const [metodoActual, setMetodoActual] = useState(null);
  const [montoActual, setMontoActual] = useState('');
  const [propinaUsuarios, setPropinaUsuarios] = useState([]);
  const [propinaUsuario, setPropinaUsuario] = useState('');
  const [propinaModo, setPropinaModo] = useState(null);
  const [propinaPct, setPropinaPct] = useState(15);
  const [propinaMontoManual, setPropinaMontoManual] = useState('');
  const [confirmarDiferencia, setConfirmarDiferencia] = useState(false);

  // Navegación por teclado en el modal de cobro (flechas mueven el foco, Enter
  // activa, Escape cierra). El contenedor envuelve todo el contenido.
  const pagoPanelRef = useRef(null);
  const metodosRef = useRef(null);
  useRovingModal(pagoPanelRef, onClose, true);

  // Al pasar al paso Pago, enfoca el primer método para seguir sin ratón.
  useEffect(() => {
    if (pagoPaso !== 1) return;
    const t = setTimeout(() => {
      const m = metodosRef.current?.querySelector('button');
      if (m) m.focus();
    }, 30);
    return () => clearTimeout(t);
  }, [pagoPaso]);

  useEffect(() => {
    api.get('/propinas/destinatarios')
      .then((r) => r.data?.status === 'success' && setPropinaUsuarios(r.data.data || []))
      .catch(() => {});
  }, []);
  const monedaPrincipal = useMemo(() => {
    return (
      (metodosPago || []).find((m) => (m.tasa || 1) >= 0.95 && (m.tasa || 1) <= 1.05) ||
      (metodosPago || [])[0]
    );
  }, [metodosPago]);

  useClientePorCedula(clienteCedula, (c) => {
    if (!(c.nombre || '').trim() && !clienteNombre) return;
    setClienteNombre((prev) => prev || c.nombre || '');
    setClienteTelefono((prev) => prev || c.telefono || '');
  });

  const totalPagado = pagosLista.reduce((s, p) => s + (p.monto_equiv || p.monto || 0), 0);

  // ── Propina: monto resuelto en moneda principal. ──
  const propinaMonto = useMemo(() => {
    if (!propinaUsuario || !propinaModo) return 0;
    if (propinaModo === 'cambio') return Math.max(0, totalPagado - total);
    if (propinaModo === 'porcentaje') return Math.round(total * propinaPct) / 100;
    const v = parseFloat(propinaMontoManual || '0');
    return isNaN(v) || v <= 0 ? 0 : v;
  }, [propinaUsuario, propinaModo, propinaPct, propinaMontoManual, total, totalPagado]);

  const totalAPagar = total + propinaMonto;
  const restante = Math.max(0, totalAPagar - totalPagado);
  const vuelto = Math.max(0, totalPagado - totalAPagar);
  const faltanComprobantes = pagosLista.some(
    (p) => p.requiere_comprobante && !(p.comprobante_url || '').trim(),
  );

  const elegirMetodo = (m) => {
    setMetodoActual((prev) => (prev?.id === m.id ? null : m));
    setMontoActual('');
  };
  const pagarTodo = () => {
    if (!metodoActual || restante <= 0) return;
    const tasa = metodoActual.tasa || 1;
    const esPrincipal =
      metodoActual.moneda_codigo === monedaPrincipal?.moneda_codigo ||
      (tasa >= 0.95 && tasa <= 1.05);
    setMontoActual(esPrincipal ? String(restante) : String(Math.ceil(restante * tasa * 100) / 100));
  };

  const agregarPago = () => {
    if (!metodoActual || !montoActual || parseFloat(montoActual) <= 0) return;
    const montoFloat = parseFloat(montoActual);
    const tasa = metodoActual.tasa || 1;
    const esPrincipal =
      metodoActual.moneda_codigo === monedaPrincipal?.moneda_codigo ||
      (tasa >= 0.95 && tasa <= 1.05);
    const montoEquiv = esPrincipal ? montoFloat : Math.round((montoFloat / tasa) * 100) / 100;
    setPagosLista((prev) => [
      ...prev,
      {
        metodo: metodoActual.nombre,
        metodo_id: metodoActual.id,
        moneda_codigo: metodoActual.moneda_codigo,
        moneda_simbolo: metodoActual.moneda_simbolo || '$',
        tasa: tasa,
        monto: montoFloat,
        monto_equiv: montoEquiv,
        es_efectivo: (metodoActual.nombre || '').toLowerCase().startsWith('efectivo'),
        requiere_comprobante: !!(metodoActual.datos_negocio && metodoActual.datos_negocio.requiere_comprobante),
      },
    ]);
    setMontoActual('');
    setMetodoActual(null);
  };

  const simboloActual = metodoActual?.moneda_simbolo || '$';
  const tasaActual = metodoActual?.tasa || 1;
  const esMonedaPrincipal =
    metodoActual &&
    (metodoActual.moneda_codigo === monedaPrincipal?.moneda_codigo ||
      (tasaActual >= 0.95 && tasaActual <= 1.05));
  const equivActual =
    montoActual && !esMonedaPrincipal && tasaActual > 1.05
      ? `${monedaPrincipal?.moneda_simbolo || '$'}${fmtMonto(parseFloat(montoActual || 0) / tasaActual)}`
      : null;
  const equivRestante =
    restante > 0 && monedaPrincipal && tasaActual > 1.05
      ? `${simboloActual}${fmtMonto(restante * tasaActual)}`
      : null;

  const _confirmar = () => {
    if (restante > 0 && !confirmarDiferencia) {
      setConfirmarDiferencia(true);
      return;
    }
    setConfirmarDiferencia(false);
    let propina = null;
    if (propinaMonto > 0 && propinaUsuario) {
      propina = {
        usuario_cedula: propinaUsuario,
        origen_tipo: propinaModo,
        valor: propinaModo === 'porcentaje' ? propinaPct : (propinaModo === 'monto' ? propinaMonto : 0),
        moneda_codigo: monedaPrincipal?.moneda_codigo || 'USD',
        tasa: 1,
      };
    }
    onConfirm(vuelto, propina, propinaMonto, restante > 0);
  };

  return (
    <div ref={pagoPanelRef}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: COL.text }}>Cobrar Pedido</div>
          <div style={{ fontSize: 13, color: COL.muted, marginTop: 2 }}>{fmt(total)}</div>
        </div>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: COL.bg,
            borderRadius: 12,
            cursor: 'pointer',
            padding: 10,
            color: COL.muted,
          }}
        >
          <FiX size={18} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['Cliente', 'Pago'].map((label, i) => {
          const active = pagoPaso >= i;
          const current = pagoPaso === i;
          return (
            <div key={i} style={{ flex: 1 }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  marginBottom: 8,
                  background: active ? COL.pri2 : COL.border,
                }}
              />
              <div style={{ textAlign: 'center' }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: current ? 700 : 500,
                    color: current ? COL.pri2 : COL.faint,
                  }}
                >
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {pagoPaso === 0 && (
        <>
          <ClienteBusqueda
            onSelect={(c) => {
              setClienteNombre(c.nombre || '');
              setClienteCedula(c.cedula || '');
              setClienteTelefono(c.telefono || '');
            }}
            COL={COL}
            S={S}
          />
          <div style={{ marginBottom: 14 }}>
            <label style={S.formLabel}>
              <FiUser size={14} /> Nombre del cliente
            </label>
            <input
              value={clienteNombre}
              onChange={(e) => setClienteNombre(e.target.value)}
              placeholder="Opcional"
              autoFocus
              style={S.formInput}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={S.formLabel}>
              <FiPhone size={14} /> Telefono
            </label>
            <input
              value={clienteTelefono}
              onChange={(e) => setClienteTelefono(e.target.value)}
              placeholder="Opcional"
              style={S.formInput}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={S.formLabel}>
              <FiHash size={14} /> Cedula / RIF
            </label>
            <input
              value={clienteCedula}
              onChange={(e) => setClienteCedula(e.target.value)}
              placeholder="Opcional"
              style={S.formInput}
            />
          </div>
          <button onClick={() => setPagoPaso(1)} style={S.btnFull(COL.pri2)}>
            Continuar al Pago <FiChevronRight size={18} />
          </button>
        </>
      )}

      {pagoPaso === 1 && (
        <>
          <div style={{ background: COL.bg, padding: 16, borderRadius: 14, marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: COL.muted, fontSize: 13 }}>Total a cobrar</span>
              <span style={{ fontWeight: 800, fontSize: 24, color: COL.text }}>{fmt(total)}</span>
            </div>
            {impuesto > 0 && (
              <button
                onClick={() => setCobrarImpuesto((v) => !v)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: `1.5px solid ${cobrarImpuesto ? COL.pri2 : COL.border}`,
                  background: cobrarImpuesto ? `${COL.pri2}12` : COL.white,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
                    Cobrar IVA ({fmt(impuesto)})
                  </div>
                  <div style={{ fontSize: 11, color: COL.muted }}>
                    Subtotal {fmt(subtotal)} + {fmt(impuesto)} de IVA
                  </div>
                </div>
                <span
                  style={{
                    width: 40,
                    height: 22,
                    borderRadius: 999,
                    background: cobrarImpuesto ? COL.pri2 : COL.skeleton || COL.border,
                    position: 'relative',
                    flexShrink: 0,
                    transition: 'background 0.15s',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: cobrarImpuesto ? 20 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                      transition: 'left 0.15s',
                    }}
                  />
                </span>
              </button>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: COL.faint,
              }}
            >
              <span>
                {monedaPrincipal?.moneda_simbolo || '$'} {monedaPrincipal?.moneda_codigo || ''}
              </span>
            </div>
            {totalPagado > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COL.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: COL.muted, fontSize: 13 }}>Pagado</span>
                  <span style={{ fontWeight: 700, fontSize: 16, color: COL.green }}>
                    {fmt(totalPagado)}
                  </span>
                </div>
                {restante > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ color: COL.muted, fontSize: 13 }}>Restante</span>
                    <span style={{ fontWeight: 700, fontSize: 16, color: COL.red }}>
                      {fmt(restante)}
                    </span>
                  </div>
                )}
                {restante <= 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: '8px 12px',
                      borderRadius: 10,
                      background: 'var(--agx-green-bg)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <FiCheckCircle size={16} color={COL.green} />
                    <span
                      style={{ fontSize: 13, fontWeight: 600, color: 'var(--agx-success-text)' }}
                    >
                      Pago completo
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment method buttons */}
          <div style={{ fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 10 }}>
            Metodo de pago
          </div>
          <div ref={metodosRef} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {metodosPago.map((m) => {
              const sel = metodoActual?.id === m.id;
              const simb = m.moneda_simbolo || '$';
              return (
                <button
                  key={m.id || m.codigo}
                  onClick={() => elegirMetodo(m)}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: `1.5px solid ${sel ? COL.pri2 : COL.border}`,
                    background: sel ? COL.blueBg : COL.card,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontFamily: 'inherit',
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 14, color: COL.text }}>{m.nombre}</span>
                  <span
                    style={{
                      fontSize: 11,
                      color: COL.faint,
                      fontWeight: 600,
                      background: COL.bg,
                      padding: '2px 8px',
                      borderRadius: 6,
                    }}
                  >
                    {simb}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Selected method: amount input + pay-all */}
          {metodoActual && (
            <div
              style={{
                marginBottom: 16,
                padding: 14,
                borderRadius: 14,
                background: COL.blueBg || COL.bg,
                border: `1px solid ${COL.pri2}20`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
                  {metodoActual.nombre}{' '}
                  <span style={{ fontWeight: 400, color: COL.faint, fontSize: 12 }}>
                    ({simboloActual})
                  </span>
                </span>
                {restante > 0 && (
                  <button
                    onClick={pagarTodo}
                    style={{
                      border: 'none',
                      background: COL.pri2 + '15',
                      color: COL.pri2,
                      borderRadius: 8,
                      padding: '4px 10px',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 700,
                      fontFamily: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <FiZap size={12} /> Pagar todo
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: 12,
                      color: COL.faint,
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {simboloActual}
                  </span>
                  <input
                    type="number"
                    value={montoActual}
                    onChange={(e) => setMontoActual(e.target.value)}
                    placeholder="0.00"
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '11px 12px 11px 34px',
                      borderRadius: 12,
                      border: `1.5px solid ${COL.border}`,
                      fontSize: 15,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      outline: 'none',
                      background: '#fff',
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (parseFloat(montoActual || 0) > 0) agregarPago();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={agregarPago}
                  disabled={!montoActual || parseFloat(montoActual) <= 0}
                  style={{
                    padding: '11px 18px',
                    borderRadius: 12,
                    border: 'none',
                    background:
                      !montoActual || parseFloat(montoActual) <= 0 ? COL.border : COL.pri2,
                    color: '#fff',
                    fontWeight: 700,
                    cursor: !montoActual || parseFloat(montoActual) <= 0 ? 'default' : 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Agregar
                </button>
              </div>
              {equivActual && (
                <div
                  style={{
                    fontSize: 11,
                    color: COL.faint,
                    marginTop: 5,
                    paddingLeft: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>= {equivActual}</span>
                  {restante > 0 && <span style={{ color: COL.pri2 }}>Quedan: {fmt(restante)}</span>}
                </div>
              )}
              {!equivActual && restante > 0 && (
                <div
                  style={{
                    fontSize: 11,
                    color: COL.faint,
                    marginTop: 5,
                    paddingLeft: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>Quedan: {fmt(restante)}</span>
                  {equivRestante && <span>= {equivRestante}</span>}
                </div>
      )}
    </div>
          )}

          {/* Applied payments list */}
          {pagosLista.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 8 }}>
                Pagos aplicados ({pagosLista.length})
              </div>
              {pagosLista.map((p, i) => {
                const esPrincipal = Math.abs((p.monto_equiv || p.monto) - p.monto) < 0.01;
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: COL.bg,
                      marginBottom: 5,
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: p.es_efectivo ? 'var(--agx-green-bg)' : 'var(--agx-blue-bg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {p.es_efectivo ? (
                        <FiDollarSign size={16} color={COL.green} />
                      ) : (
                        <FiCreditCard size={14} color={COL.pri2} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COL.text }}>
                        {p.metodo}
                      </div>
                      <div style={{ fontSize: 11, color: COL.faint }}>
                        {p.moneda_simbolo}
                        {fmtMonto(p.monto)}
                        {!esPrincipal && p.tasa > 1.05 && <span> ≈ {fmt(p.monto_equiv)}</span>}
                      </div>
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 14, color: COL.text, flexShrink: 0 }}>
                      {fmt(p.monto_equiv || p.monto)}
                    </span>
                    <button
                      onClick={() => {
                        setPagosLista((prev) => prev.filter((_, j) => j !== i));
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: COL.faint,
                        padding: 4,
                        flexShrink: 0,
                      }}
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Comprobantes */}
          {pagosLista.some((p) => p.requiere_comprobante) && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 8 }}>
                Comprobantes de pago
              </div>
              {pagosLista.map((p, i) =>
                p.requiere_comprobante ? (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: 10, background: COL.bg, marginBottom: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: COL.text }}>{p.metodo}</span>
                    <PagoComprobante
                      pago={p}
                      onUploaded={(url) => {
                        setPagosLista((prev) =>
                          prev.map((pp, j) => (j === i ? { ...pp, comprobante_url: url } : pp)),
                        );
                      }}
                    />
                  </div>
                ) : null,
              )}
            </div>
          )}

          {/* Vuelto summary */}
          {restante <= 0 && totalPagado > 0 && vuelto > 0 && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 12,
                background: 'var(--agx-amber-bg)',
                border: '1px solid var(--agx-amber)',
                marginBottom: 14,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--agx-warning-text)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <FiAlertCircle size={14} /> Vuelto
              </span>
              <span style={{ fontWeight: 800, fontSize: 16, color: 'var(--agx-warning-text)' }}>
                {fmt(vuelto)}
              </span>
            </div>
          )}

          {/* Propina (minimalista) */}
          <div
            style={{
              marginBottom: 14,
              padding: '12px 14px',
              borderRadius: 12,
              background: COL.bg,
              border: `1px solid ${COL.border}`,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: COL.text, marginBottom: 10 }}>
              Propina
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {[
                { id: null, label: 'Sin propina' },
                { id: 'porcentaje', label: '10%' },
                { id: 'porcentaje15', label: '15%' },
                { id: 'porcentaje20', label: '20%' },
                { id: 'monto', label: 'Otro' },
                { id: 'cambio', label: 'Dejar cambio' },
              ].map((m) => {
                const activo =
                  (m.id === null && !propinaModo) ||
                  (m.id === 'porcentaje' && propinaModo === 'porcentaje' && propinaPct === 10) ||
                  (m.id === 'porcentaje15' && propinaModo === 'porcentaje' && propinaPct === 15) ||
                  (m.id === 'porcentaje20' && propinaModo === 'porcentaje' && propinaPct === 20) ||
                  (m.id === 'monto' && propinaModo === 'monto') ||
                  (m.id === 'cambio' && propinaModo === 'cambio');
                return (
                  <button
                    key={m.id || 'sin'}
                    type="button"
                    onClick={() => {
                      if (m.id === null) setPropinaModo(null);
                      else if (m.id === 'porcentaje') { setPropinaModo('porcentaje'); setPropinaPct(10); }
                      else if (m.id === 'porcentaje15') { setPropinaModo('porcentaje'); setPropinaPct(15); }
                      else if (m.id === 'porcentaje20') { setPropinaModo('porcentaje'); setPropinaPct(20); }
                      else setPropinaModo(m.id);
                    }}
                    style={{
                      padding: '7px 12px',
                      borderRadius: 999,
                      border: `1px solid ${activo ? COL.pri : COL.border}`,
                      background: activo ? COL.pri2 : COL.white,
                      color: activo ? '#fff' : COL.text,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>

            {propinaModo && (
              <>
                {propinaModo === 'cambio' ? (
                  <div style={{ fontSize: 12, color: COL.muted }}>
                    {totalPagado - total > 0
                      ? `El cambio de ${fmt(totalPagado - total)} se deja como propina.`
                      : 'Paga de más para poder dejar el cambio como propina.'}
                  </div>
                ) : propinaModo === 'monto' ? (
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    placeholder="Monto de propina"
                    value={propinaMontoManual}
                    onChange={(e) => setPropinaMontoManual(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: 10,
                      border: `1px solid ${COL.border}`,
                      background: COL.white,
                      color: COL.text,
                      fontSize: 13,
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                  />
                ) : null}

                <select
                  value={propinaUsuario}
                  onChange={(e) => setPropinaUsuario(e.target.value)}
                  style={{
                    width: '100%',
                    marginTop: 8,
                    padding: '9px 12px',
                    borderRadius: 10,
                    border: `1px solid ${propinaUsuario ? COL.border : '#e11d48'}`,
                    background: COL.white,
                    color: COL.text,
                    fontSize: 13,
                    fontFamily: 'inherit',
                  }}
                >
                  <option value="">¿Quién recibe la propina?</option>
                  {propinaUsuarios.map((u) => (
                    <option key={u.cedula} value={u.cedula}>
                      {u.nombre}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          {/* Resumen del total con propina */}
          {propinaMonto > 0 && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 12,
                background: COL.white,
                border: `1px solid ${COL.border}`,
                marginBottom: 14,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: COL.muted, marginBottom: 4 }}>
                <span>Subtotal</span><span>{fmt(total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: COL.green, marginBottom: 4 }}>
                <span>Propina</span><span>{fmt(propinaMonto)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 800, color: COL.text }}>
                <span>Total a pagar</span><span>{fmt(totalAPagar)}</span>
              </div>
            </div>
          )}

          <button
            onClick={_confirmar}
            disabled={faltanComprobantes || (propinaModo && !propinaUsuario)}
            style={S.btnPrimary(faltanComprobantes || (propinaModo && !propinaUsuario))}
          >
            <FiCreditCard size={18} />
            {propinaModo && !propinaUsuario
              ? 'Elige quién recibe la propina'
              : faltanComprobantes
                ? 'Adjunta el comprobante de pago'
                : restante > 0
                  ? `Cobrar — Falta ${fmt(restante)}`
                  : vuelto > 0
                    ? `Cobrar — Vuelto: ${fmt(vuelto)}`
                    : `Confirmar Cobro de ${fmt(totalAPagar)}`}
          </button>

          <button
            onClick={() => setPagoPaso(0)}
            style={{
              marginTop: 8,
              width: '100%',
              padding: 10,
              borderRadius: 10,
              border: 'none',
              background: 'transparent',
              color: COL.faint,
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          >
            Volver
          </button>
        </>
      )}

      {confirmarDiferencia && (
        <div
          onClick={() => setConfirmarDiferencia(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COL.white,
              borderRadius: 18,
              width: '100%',
              maxWidth: 400,
              padding: 24,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FiAlertTriangle size={22} color="#ea580c" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: COL.text }}>Falta por pagar</div>
                <div style={{ fontSize: 12, color: COL.muted }}>El monto pagado es inferior al total</div>
              </div>
            </div>
            <div style={{ padding: '14px 16px', borderRadius: 12, background: '#fff7ed', border: '1px solid #fed7aa', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: COL.muted, marginBottom: 4 }}>
                <span>Total a pagar</span><span>{fmt(totalAPagar)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: COL.muted, marginBottom: 4 }}>
                <span>Monto pagado</span><span>{fmt(totalPagado)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 800, color: '#c2410c' }}>
                <span>Diferencia (falta)</span><span>-{fmt(restante)}</span>
              </div>
            </div>
            <p style={{ fontSize: 12, color: COL.muted, lineHeight: 1.5, margin: '0 0 16px' }}>
              ¿Deseas procesar el pedido con esta diferencia negativa? Quedará registrado como falta de pago en el cierre de caja.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmarDiferencia(false)}
                style={{
                  flex: 1, padding: 12, borderRadius: 12, border: `1px solid ${COL.border}`,
                  background: COL.white, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COL.muted, fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={_confirmar}
                style={{
                  flex: 1.5, padding: 12, borderRadius: 12, border: 'none',
                  background: 'linear-gradient(135deg, #ea580c, #c2410c)', color: '#fff',
                  cursor: 'pointer', fontSize: 13, fontWeight: 800, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <FiCreditCard size={15} /> Procesar con diferencia
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════ EN ESPERA ═══════════
// Paleta de acentos por mesa (color estable derivado del id de mesa).
const MESA_COLORS = ['#2563eb', '#059669', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#ea580c', '#4f46e5'];
const colorMesa = (key) => {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return MESA_COLORS[h % MESA_COLORS.length];
};

const ESTADO_GRUPOS = [
  {
    key: 'pendiente',
    label: 'Pendientes',
    states: ['creado'],
    icon: FiClock,
    color: '#92400e',
    bg: '#fef3c7',
  },
  {
    key: 'cocina',
    label: 'En cocina',
    states: ['comanda_enviada', 'armado_cocina'],
    icon: FiCoffee,
    color: 'var(--agx-success-text)',
    bg: '#dcfce7',
  },
  {
    key: 'armando',
    label: 'Armando',
    states: ['armado'],
    icon: FiPackage,
    color: '#c2410c',
    bg: '#fff7ed',
  },
  {
    key: 'listo',
    label: 'Listos para cobrar',
    states: ['listo', 'servido'],
    icon: FiCheckCircle,
    color: '#1d4ed8',
    bg: 'var(--agx-blue-bg)',
  },
];

function EnEspera({
  sede,
  has,
  setVista,
  setCargarPedidoDesdeEspera,
  setAbrirPagoDirecto,
  tieneRestaurante,
  abrirTicket,
}) {
  const { COL } = usePTDColors();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const { agregarNotificacion } = useNotificaciones();

  // Seleccion multiple para cobro combinado.
  const [seleccionados, setSeleccionados] = useState({});
  const [showPagoLote, setShowPagoLote] = useState(false);
  const [pagoPaso, setPagoPaso] = useState(0);
  const [pagosLista, setPagosLista] = useState([]);
  const [cobrarImpuesto, setCobrarImpuesto] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [loteCliente, setLoteCliente] = useState({ nombre: '', cedula: '', telefono: '' });
  const [cobrandoLote, setCobrandoLote] = useState(false);

  // Traspaso de pedido de una mesa a otra.
  const [traspasarPedido, setTraspasarPedido] = useState(null);
  const [mesasDestino, setMesasDestino] = useState([]);
  const [traspasando, setTraspasando] = useState(false);

  const selIds = Object.keys(seleccionados)
    .map(Number)
    .filter((id) => pedidos.some((p) => p.id === id));
  const subtotalLote = selIds.reduce(
    (s, id) => s + (parseFloat(pedidos.find((p) => p.id === id)?.total) || 0),
    0
  );
  const impuestoLote = selIds.reduce((s, id) => {
    const pd = pedidos.find((p) => p.id === id);
    return (
      s +
      (Array.isArray(pd?.productos) ? pd.productos : []).reduce((acc, it) => {
        const base = (it.cantidad || 1) * (it.precio_unitario || 0);
        const etExtra = (it.etiquetas || []).reduce((t, e) => t + (e.precio || 0), 0);
        const comboExtra = it.combo_extra || 0;
        const tasa = it.impuesto || 0;
        return acc + (base + etExtra * (it.cantidad || 1) + comboExtra * (it.cantidad || 1)) * (tasa / 100);
      }, 0)
    );
  }, 0);
  const totalLote = subtotalLote + (cobrarImpuesto ? impuestoLote : 0);

  const toggleSeleccion = (pd) => {
    if (pd.pagado) return; // ya pagado: no entra al cobro combinado
    setSeleccionados((prev) => {
      const next = { ...prev };
      if (next[pd.id]) delete next[pd.id];
      else next[pd.id] = true;
      return next;
    });
  };
  const seleccionarMesa = (grupo) => {
    const cobrables = grupo.items.filter((pd) => !pd.pagado);
    const todosSel = cobrables.length > 0 && cobrables.every((pd) => seleccionados[pd.id]);
    setSeleccionados((prev) => {
      const next = { ...prev };
      cobrables.forEach((pd) => {
        if (todosSel) delete next[pd.id];
        else next[pd.id] = true;
      });
      return next;
    });
  };
  const limpiarSeleccion = () => setSeleccionados({});

  const abrirPagoLote = async () => {
    if (selIds.length === 0) return;
    setPagoPaso(0);
    setPagosLista([]);
    setLoteCliente({ nombre: '', cedula: '', telefono: '' });
    setShowPagoLote(true);
    const cacheKey = `metodos-pago:${sede}`;
    const cached = cacheGet(cacheKey);
    if (cached) setMetodosPago(cached);
    api
      .get(`/ventas/metodos-pago?sede=${sede}`)
      .then((r) => {
        const d = r.data?.data || [];
        cacheSet(cacheKey, d);
        setMetodosPago(d);
      })
      .catch((e) => console.error('Error cargando metodos de pago:', e));
  };

  const confirmarPagoLote = async (vuelto = 0, propina = null, propinaMonto = 0, procesarConDiferencia = false) => {
    if (selIds.length === 0 || cobrandoLote) return;
    setCobrandoLote(true);
    try {
      const r = await api.post('/pedidos/tienda/cobrar-lote', {
        pedido_ids: selIds,
        pagos: pagosLista,
        monto_recibido: totalLote + propinaMonto + vuelto,
        vuelto,
        cobrar_impuesto: cobrarImpuesto,
        cliente_nombre: loteCliente.nombre || null,
        cliente_cedula: loteCliente.cedula || null,
        cliente_telefono: loteCliente.telefono || null,
        propina,
        procesar_con_diferencia: procesarConDiferencia,
      });
      setShowPagoLote(false);
      limpiarSeleccion();
      setCobrarImpuesto(false);
      agregarNotificacion({
        titulo: selIds.length > 1 ? 'Cobro combinado' : 'Pedido cobrado',
        mensaje:
          selIds.length > 1
            ? `${selIds.length} pedidos cobrados como una sola venta`
            : 'Pedido cobrado correctamente',
        tipo: 'success',
      });
      // Ticket de entrega automatico del cobro combinado.
      const grupo = r.data?.data?.grupo_cobro;
      try {
        const params = grupo
          ? `grupo_cobro=${encodeURIComponent(grupo)}`
          : `pedido_ids=${selIds.join(',')}`;
        const rr = await api.get(`/pedidos/tienda/recibo?${params}`);
        if (rr.data?.data) abrirTicket(rr.data.data, 'entrega', true);
      } catch (e) {
        console.error('Error generando ticket de entrega combinado:', e);
      }
      cargar(true);
    } catch (e) {
      agregarNotificacion({
        titulo: 'Error',
        mensaje: e?.response?.data?.detail || 'No se pudo cobrar el lote',
        tipo: 'error',
      });
    } finally {
      setCobrandoLote(false);
    }
  };

  const imprimirCuenta = async () => {
    if (selIds.length === 0) return;
    try {
      const r = await api.get(`/pedidos/tienda/recibo?pedido_ids=${selIds.join(',')}`);
      if (r.data?.data) abrirTicket(r.data.data, 'cobro', false);
    } catch (e) {
      agregarNotificacion({
        titulo: 'Error',
        mensaje: e?.response?.data?.detail || 'No se pudo generar el ticket de cobro',
        tipo: 'error',
      });
    }
  };

  const cargar = useCallback((silencioso = false) => {
    if (!silencioso) setLoading(true);
    setError(false);
    api
      .get(`/pedidos/tienda/en-espera?sede=${sede}`)
      .then((r) => {
        const data = r.data?.data || [];
        setPedidos(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }, [sede]);
  useEffect(() => {
    cargar();
  }, [cargar]);

  // Auto-recarga con debounce ante cambios (crear/cobrar/anular) + merge
  // optimista: actualiza la fila afectada al instante y revalida en background.
  const reloadTimerRef = useRef(null);
  useEffect(() => {
    const handler = (payload) => {
      const pid = payload?.pedido_id;
      const estado = payload?.estado;
      if (pid != null) {
        setPedidos((prev) => {
          if (estado === 'cobrado' || estado === 'anulado') {
            return prev.filter((pd) => pd.id !== pid);
          }
          let changed = false;
          const next = prev.map((pd) => {
            if (pd.id !== pid) return pd;
            const upd = { ...pd };
            if (estado) { upd.estado = estado; changed = true; }
            if (payload?.en_espera !== undefined) { upd.en_espera = payload.en_espera; changed = true; }
            if (payload?.prioridad !== undefined) { upd.prioridad = payload.prioridad; changed = true; }
            if (payload?.mesa_id !== undefined) { upd.mesa_id = payload.mesa_id; changed = true; }
            if (payload?.ticket_numero !== undefined) { upd.ticket_numero = payload.ticket_numero; changed = true; }
            return upd;
          });
          return changed ? next : prev;
        });
      }
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = setTimeout(() => cargar(true), 400);
    };
    socket.on('pedido_actualizado', handler);
    return () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      socket.off('pedido_actualizado', handler);
    };
  }, [cargar]);

  const eliminarPedido = (id) => {
    setConfirm({
      title: 'Eliminar pedido',
      message: 'Este pedido en espera se eliminara permanentemente.',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.delete(`/pedidos/tienda/${id}/espera`);
        } catch (e) {
          agregarNotificacion({
            titulo: 'Error',
            mensaje: e?.response?.data?.detail || 'No se pudo eliminar el pedido',
            tipo: 'error',
          });
        }
      },
    });
  };
  const togglePrioridad = async (pd) => {
    try {
      await api.put(`/pedidos/tienda/${pd.id}/prioridad`, { prioridad: !pd.prioridad });
    } catch (e) {
      agregarNotificacion({
        titulo: 'Error',
        mensaje: e?.response?.data?.detail || 'No se pudo actualizar la prioridad',
        tipo: 'error',
      });
    }
  };
  const modificarPedido = (pd) => {
    setCargarPedidoDesdeEspera(pd);
    setVista('pedido');
  };
  const abrirTraspasar = async (pd) => {
    setTraspasarPedido(pd);
    setMesasDestino([]);
    try {
      const r = await api.get(`/mesas/?sede=${sede}`);
      const mesas = (r.data?.data || []).filter((m) => m.id !== pd.mesa_id);
      setMesasDestino(mesas);
    } catch (e) {
      agregarNotificacion({
        titulo: 'Error',
        mensaje: 'No se pudieron cargar las mesas',
        tipo: 'error',
      });
      setTraspasarPedido(null);
    }
  };
  const confirmarTraspasar = async (mesaDestinoId) => {
    if (!traspasarPedido || traspasando) return;
    setTraspasando(true);
    try {
      await api.put(`/pedidos/tienda/${traspasarPedido.id}/mesa`, { mesa_id: mesaDestinoId });
      setTraspasarPedido(null);
      agregarNotificacion({
        titulo: 'Pedido traspasado',
        mensaje: `El pedido #${traspasarPedido.ticket_numero || traspasarPedido.id} se movio de mesa`,
        tipo: 'success',
      });
      cargar(true);
    } catch (e) {
      agregarNotificacion({
        titulo: 'Error',
        mensaje: e?.response?.data?.detail || 'No se pudo traspasar el pedido',
        tipo: 'error',
      });
    } finally {
      setTraspasando(false);
    }
  };
  const completarPedido = (pd) => {
    setCargarPedidoDesdeEspera(pd);
    setVista('pedido');
    setAbrirPagoDirecto(pd);
  };

  const SLA_MIN = 30;
  const minutosDesde = (f) => {
    if (!f) return 0;
    return Math.floor((Date.now() - new Date(f).getTime()) / 60000);
  };

  const getGrupo = (estado) =>
    ESTADO_GRUPOS.find((g) => g.states.includes(estado)) || ESTADO_GRUPOS[0];

  // Agrupa por mesa (los pedidos sin mesa van a "Para llevar").
  const gruposMesa = useMemo(() => {
    const map = {};
    pedidos.forEach((pd) => {
      const key = pd.mesa_id != null ? `m${pd.mesa_id}` : '__sinmesa';
      if (!map[key]) {
        map[key] = {
          key,
          label: pd.mesa_id != null ? `Mesa ${pd.mesa_nombre || pd.mesa_codigo || pd.mesa_id}` : 'Para llevar',
          mesa_id: pd.mesa_id,
          color: pd.mesa_id != null ? colorMesa(key) : '#64748b',
          items: [],
        };
      }
      map[key].items.push(pd);
    });
    const grupos = Object.values(map);
    grupos.sort((a, b) => (a.key === '__sinmesa' ? 1 : b.key === '__sinmesa' ? -1 : a.key.localeCompare(b.key)));
    grupos.forEach((g) => {
      g.total = g.items.reduce((s, pd) => s + (parseFloat(pd.total) || 0), 0);
      g.pagados = g.items.filter((pd) => pd.pagado).length;
      g.items.sort((a, b) => (b.prioridad ? 1 : 0) - (a.prioridad ? 1 : 0) || (a.ticket_numero || a.id) - (b.ticket_numero || b.id));
    });
    return grupos;
  }, [pedidos]);

  if (loading)
    return (
      <div style={{ padding: 60, display: 'flex', justifyContent: 'center' }}>
        <SkeletonLoader />
      </div>
    );
  if (error)
    return <ErrorBanner message="No se pudieron cargar los pedidos en espera." onRetry={() => cargar()} />;

  if (pedidos.length === 0)
    return (
      <EmptyState
        icon={FiList}
        title="Sin pedidos en espera"
        description="Los pedidos guardados desde la pantalla de Pedido apareceran aqui"
        action={
          has('pedido_ver')
            ? { label: 'Crear pedido', onClick: () => setVista('pedido') }
            : undefined
        }
      />
    );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div
        style={{
          padding: '12px 16px',
          background: COL.white,
          borderBottom: `1px solid ${COL.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: COL.text }}>
            {pedidos.length} en espera
          </span>
          <span style={{ fontSize: 11, color: COL.faint }}>
            {gruposMesa.length} {gruposMesa.length === 1 ? 'mesa' : 'mesas'}
          </span>
        </div>
        <button
          onClick={cargar}
          style={{
            border: 'none',
            background: COL.bg,
            borderRadius: 8,
            cursor: 'pointer',
            padding: 7,
            color: COL.muted,
          }}
          title="Refrescar"
        >
          <FiRefreshCw size={15} />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {gruposMesa.map((grupo) => (
            <div key={grupo.key}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                  padding: '10px 14px',
                  borderRadius: 12,
                  background: grupo.color,
                  color: '#fff',
                  boxShadow: `0 4px 14px ${grupo.color}44`,
                }}
              >
                <button
                  onClick={() => seleccionarMesa(grupo)}
                  title="Seleccionar toda la mesa"
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    border: '2px solid rgba(255,255,255,0.8)',
                    background:
                      grupo.items.some((pd) => !pd.pagado) &&
                      grupo.items.filter((pd) => !pd.pagado).every((pd) => seleccionados[pd.id])
                        ? '#fff'
                        : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  {grupo.items.some((pd) => !pd.pagado) &&
                    grupo.items.filter((pd) => !pd.pagado).every((pd) => seleccionados[pd.id]) && (
                      <FiCheck size={16} color={grupo.color} />
                    )}
                </button>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {grupo.mesa_id != null ? <FiHome size={16} /> : <FiShoppingBag size={16} />}
                </div>
                <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '0.2px' }}>
                  {grupo.label}
                </span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    fontWeight: 700,
                    background: 'rgba(255,255,255,0.22)',
                    padding: '2px 10px',
                    borderRadius: 999,
                  }}
                >
                  {grupo.items.length} {grupo.items.length === 1 ? 'pedido' : 'pedidos'}
                </span>
                {grupo.total > 0 && (
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 800,
                      background: 'rgba(255,255,255,0.92)',
                      color: grupo.color,
                      padding: '2px 10px',
                      borderRadius: 999,
                    }}
                  >
                    {fmt(grupo.total)}
                  </span>
                )}
                {grupo.pagados > 0 && grupo.pagados < grupo.items.length && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      background: 'rgba(255,255,255,0.92)',
                      color: grupo.color,
                      padding: '2px 8px',
                      borderRadius: 999,
                    }}
                  >
                    {grupo.pagados} pagado
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {grupo.items.map((pd) => {
                  const open = expanded === pd.id;
                  const productosRaw = Array.isArray(pd.productos) ? pd.productos : [];
                  const productos = productosRaw.map((it) => ({
                    ...it,
                    imagen_url: it.imagen_url || getImagenUrl(it.codigo) || '',
                  }));
                  const count = productos.reduce((s, i) => s + (i.cantidad || 1), 0);
                  const minsCreado = minutosDesde(pd.fechapedido);
                  const minsActualizado = minutosDesde(pd.fechaactualizacion);
                  const slaAlert = minsCreado > SLA_MIN;
                  const eg = getGrupo(pd.estado);
                  return (
                    <div
                      key={pd.id}
                      style={{
                        background: COL.white,
                        borderRadius: 14,
                        border: `1px solid ${open ? COL.pri2 : slaAlert ? '#fca5a5' : COL.border}`,
                        borderLeft: `4px solid ${open ? COL.pri2 : slaAlert ? '#ef4444' : eg.color}`,
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                        boxShadow: open
                          ? `0 2px 12px ${COL.pri2}15`
                          : slaAlert
                            ? '0 1px 4px rgba(239,68,68,0.15)'
                            : '0 1px 2px rgba(0,0,0,0.03)',
                      }}
                    >
                      <div
                        onClick={() => setExpanded(open ? null : pd.id)}
                        style={{
                          padding: '14px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              marginBottom: 4,
                              flexWrap: 'wrap',
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSeleccion(pd);
                              }}
                              disabled={pd.pagado}
                              title={pd.pagado ? 'Ya pagado' : 'Seleccionar para cobro combinado'}
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: 7,
                                border: `2px solid ${pd.pagado ? COL.faint : seleccionados[pd.id] ? COL.pri2 : COL.border}`,
                                background: seleccionados[pd.id] ? COL.pri2 : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: pd.pagado ? 'default' : 'pointer',
                                flexShrink: 0,
                                padding: 0,
                              }}
                            >
                              {seleccionados[pd.id] && <FiCheck size={15} color="#fff" />}
                            </button>
                            <span style={{ fontWeight: 800, fontSize: 15, color: COL.pri }}>
                              #{pd.ticket_numero || pd.id}
                            </span>
                            {pd.prioridad && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color: '#b45309',
                                  background: '#fef3c7',
                                  padding: '3px 9px',
                                  borderRadius: 6,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                <FiStar size={11} color="#f59e0b" fill="#f59e0b" /> Prioridad
                              </span>
                            )}
                            {pd.pagado && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color: COL.pri2,
                                  background: COL.blueBg,
                                  padding: '3px 9px',
                                  borderRadius: 6,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                <FiCheckCircle size={11} /> Pagado
                              </span>
                            )}
                            {pd.cliente_nombre && (
                              <span
                                style={{
                                  fontSize: 14,
                                  fontWeight: 800,
                                  color: COL.pri,
                                  background: COL.priLight || '#eaf1ff',
                                  padding: '3px 10px',
                                  borderRadius: 8,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 5,
                                }}
                              >
                                <FiUser size={14} /> {pd.cliente_nombre}
                              </span>
                            )}
                            {pd.mesa_nombre && pd.mesa_id == null && (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: COL.amber,
                                  background: COL.amberBg,
                                  padding: '2px 8px',
                                  borderRadius: 6,
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                <FiMapPin size={10} /> {pd.mesa_nombre}
                              </span>
                            )}
                            {(() => {
                              const EgIcon = eg.icon;
                              return (
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: eg.color,
                                    background: eg.bg,
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3,
                                  }}
                                >
                                  <EgIcon size={10} />{' '}
                                  {eg.label.replace('Listos para cobrar', 'Listo')}
                                </span>
                              );
                            })()}
                            {slaAlert && (
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: '#dc2626',
                                  background: '#fee2e2',
                                  padding: '2px 7px',
                                  borderRadius: 6,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3,
                                }}
                              >
                                <FiAlertTriangle size={9} /> {minsCreado}min
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: 14,
                              fontSize: 12,
                              color: COL.muted,
                              flexWrap: 'wrap',
                              alignItems: 'center',
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <FiPackage size={12} /> {count} items
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <FiCalendar size={12} /> {timeAgo(pd.fechapedido)}
                            </span>
                            {minsActualizado > 5 && pd.estado !== 'creado' && (
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  color: minsActualizado > 15 ? COL.red : COL.amber,
                                }}
                              >
                                <FiClock size={12} /> {minsActualizado}min en estado
                              </span>
                            )}
                            {pd.cliente_telefono && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <FiPhone size={12} /> {pd.cliente_telefono}
                              </span>
                            )}
                          </div>
                          {pd.notas_asesor && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                marginTop: 8,
                                padding: '6px 10px',
                                borderRadius: 8,
                                background: COL.tagOrangeBg,
                                color: COL.tagOrangeText,
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              <FiEdit2 size={12} style={{ flexShrink: 0 }} />
                              <span style={{ wordBreak: 'break-word' }}>{pd.notas_asesor}</span>
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            flexShrink: 0,
                            marginLeft: 12,
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePrioridad(pd);
                            }}
                            title={pd.prioridad ? 'Quitar prioridad' : 'Marcar como prioridad'}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: 4,
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <FiStar
                              size={20}
                              color={pd.prioridad ? '#f59e0b' : COL.faint}
                              fill={pd.prioridad ? '#f59e0b' : 'none'}
                            />
                          </button>
                          <span style={{ fontWeight: 800, fontSize: 16 }}>{fmt(pd.total)}</span>
                          <div style={{ color: COL.faint }}>
                            {open ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                          </div>
                        </div>
                      </div>
                      {open && (
                        <div
                          style={{
                            overflow: 'hidden',
                            maxHeight: open ? '800px' : '0px',
                            transition: 'max-height 0.3s ease',
                            borderTop: `1px solid ${COL.border}`,
                          }}
                        >
                          <div style={{ padding: '0 16px 16px' }}>
                            <div style={{ padding: '10px 0' }}>
                              {productos.map((item, i) => {
                                const totalUnid = item._total != null ? item._total : 0;
                                const entrUnid = item._entregados != null ? item._entregados : 0;
                                const salioCocina = item._salio_cocina != null ? item._salio_cocina : 0;
                                const entregado = totalUnid > 0 && entrUnid >= totalUnid;
                                const enCocina = !entregado && salioCocina > 0;
                                const parcial = !entregado && !enCocina && entrUnid > 0;
                                return (
                                <div
                                  key={i}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '7px 0',
                                    borderBottom:
                                      i < productos.length - 1 ? `1px solid ${COL.border}` : 'none',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 10,
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {item.imagen_url ? (
                                      <img
                                        src={getImagenUrl(item.imagen_url)}
                                        alt=""
                                        style={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 8,
                                          objectFit: 'cover',
                                          flexShrink: 0,
                                          opacity: entregado ? 0.55 : 1,
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 8,
                                          background: COL.bg,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                          opacity: entregado ? 0.55 : 1,
                                        }}
                                      >
                                        <FiPackage size={14} color={COL.faint} />
                                      </div>
                                    )}
                                    <div style={{ minWidth: 0 }}>
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          fontSize: 13,
                                          textDecoration: entregado ? 'line-through' : 'none',
                                          color: entregado ? COL.faint : COL.text,
                                        }}
                                      >
                                        {item.nombre || item.codigo}
                                      </span>
                                      <span
                                        style={{ fontSize: 13, color: COL.muted, marginLeft: 8 }}
                                      >
                                        x{item.cantidad}
                                      </span>
                                      {totalUnid > 0 && (
                                        <span
                                          style={{
                                            marginLeft: 8,
                                            fontSize: 10.5,
                                            fontWeight: 700,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 3,
                                            color: entregado
                                              ? COL.green
                                              : enCocina
                                                ? COL.pri2
                                                : parcial
                                                  ? COL.amber
                                                  : COL.faint,
                                          }}
                                          title={
                                            entregado
                                              ? 'Entregado'
                                              : enCocina
                                                ? 'Salió de cocina, pendiente de entrega'
                                                : parcial
                                                  ? `Entregados ${entrUnid} de ${totalUnid}`
                                                  : 'Pendiente'
                                          }
                                        >
                                          {entregado ? (
                                            <FiCheckCircle size={12} />
                                          ) : enCocina ? (
                                            <FiCoffee size={12} />
                                          ) : (
                                            <FiClock size={12} />
                                          )}
                                          {entregado
                                            ? 'Entregado'
                                            : enCocina
                                              ? 'Salió de cocina'
                                              : parcial
                                                ? `${entrUnid}/${totalUnid}`
                                                : 'Pendiente'}
                                        </span>
                                      )}
                                      {(item.etiquetas || []).length > 0 && (
                                        <div
                                          style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 3,
                                            marginTop: 2,
                                          }}
                                        >
                                          {item.etiquetas.map((et, ei) => (
                                            <span
                                              key={ei}
                                              style={{
                                                fontSize: 9,
                                                fontWeight: 500,
                                                background: COL.tagOrangeBg,
                                                color: COL.tagOrangeText,
                                                padding: '1px 4px',
                                                borderRadius: 3,
                                              }}
                                            >
                                              {et.nombre}
                                              {et.precio > 0 ? ` +${fmt(et.precio)}` : ''}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      fontSize: 13,
                                      color: entregado ? COL.faint : COL.text,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {fmt((item.precio_unitario || 0) * (item.cantidad || 1))}
                                  </span>
                                </div>
                                );
                              })}
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {has('espera_modificar') && (
                                <button
                                  onClick={() => modificarPedido(pd)}
                                  style={S.btnSm(COL.pri2)}
                                >
                                  <FiEdit2 size={13} /> Modificar
                                </button>
                              )}
                              {has('traspasar_mesa') && (
                                <button
                                  onClick={() => abrirTraspasar(pd)}
                                  style={S.btnSm(COL.pri2)}
                                >
                                  <FiArrowRight size={13} /> Traspasar
                                </button>
                              )}
                              {has('pedido_cobrar') && (
                                <button
                                  onClick={() => completarPedido(pd)}
                                  style={S.btnSm(COL.green)}
                                >
                                  <FiCreditCard size={13} /> Cobrar
                                </button>
                              )}
                              {has('espera_eliminar') && (
                                <button
                                  onClick={() => eliminarPedido(pd.id)}
                                  style={{
                                    ...S.btnSm(COL.red),
                                    marginLeft: has('pedido_cobrar') ? undefined : 'auto',
                                  }}
                                >
                                  <FiTrash2 size={13} /> Eliminar
                                </button>
                              )}
                              {tieneRestaurante && has('pedido_ver') && pd.estado === 'creado' && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await api.put(`/pedidos/tienda/${pd.id}/enviar-comanda`);
                                      cargar();
                                    } catch (e) {
                                      alert(e.response?.data?.detail || 'Error al procesar el pedido');
                                    }
                                  }}
                                  style={S.btnSm(COL.orange)}
                                >
                                  <FiSend size={13} /> Procesar
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      {selIds.length > 0 && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 24,
            background: COL.pri,
            color: '#fff',
            borderRadius: 16,
            padding: '12px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
            zIndex: 900,
          }}
        >
          <div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              {selIds.length} {selIds.length === 1 ? 'pedido' : 'pedidos'} seleccionado{selIds.length === 1 ? '' : 's'}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{fmt(totalLote)}</div>
          </div>
          <button
            onClick={abrirPagoLote}
            disabled={selIds.length === 0}
            style={{
              border: 'none',
              background: '#fff',
              color: COL.pri,
              borderRadius: 12,
              padding: '10px 16px',
              fontWeight: 800,
              fontSize: 14,
              cursor: selIds.length === 0 ? 'default' : 'pointer',
              opacity: selIds.length === 0 ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
            }}
          >
            <FiCreditCard size={16} /> {selIds.length > 1 ? 'Cobrar combinado' : 'Cobrar'}
          </button>
          <button
            onClick={imprimirCuenta}
            style={{
              border: '1px solid rgba(255,255,255,0.6)',
              background: 'transparent',
              color: '#fff',
              borderRadius: 12,
              padding: '10px 14px',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
            }}
          >
            <FiPrinter size={15} /> Imprimir cuenta
          </button>
          <button
            onClick={limpiarSeleccion}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#fff',
              cursor: 'pointer',
              padding: 6,
              display: 'flex',
            }}
            title="Limpiar seleccion"
          >
            <FiX size={18} />
          </button>
        </div>
      )}

      {showPagoLote && (
        <div
          onClick={(e) => e.target === e.currentTarget && setShowPagoLote(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: COL.overlay,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              background: COL.surface,
              borderRadius: 20,
              width: 460,
              maxWidth: '92%',
              maxHeight: '92vh',
              overflow: 'auto',
              padding: 28,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <PagoSteps
              pagoPaso={pagoPaso}
              setPagoPaso={setPagoPaso}
              total={totalLote}
              subtotal={subtotalLote}
              impuesto={impuestoLote}
              cobrarImpuesto={cobrarImpuesto}
              setCobrarImpuesto={setCobrarImpuesto}
              pagosLista={pagosLista}
              setPagosLista={setPagosLista}
              metodosPago={metodosPago}
              clienteNombre={loteCliente.nombre}
              setClienteNombre={(v) => setLoteCliente((p) => ({ ...p, nombre: v }))}
              clienteCedula={loteCliente.cedula}
              setClienteCedula={(v) => setLoteCliente((p) => ({ ...p, cedula: v }))}
              clienteTelefono={loteCliente.telefono}
              setClienteTelefono={(v) => setLoteCliente((p) => ({ ...p, telefono: v }))}
              onConfirm={confirmarPagoLote}
              onClose={() => setShowPagoLote(false)}
            />
          </div>
        </div>
      )}

      {traspasarPedido && (
        <div
          onClick={(e) => e.target === e.currentTarget && setTraspasarPedido(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: COL.overlay,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              background: COL.surface,
              borderRadius: 20,
              width: 420,
              maxWidth: '92%',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${COL.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
              <FiArrowRight size={18} color={COL.pri2} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: COL.text }}>
                  Traspasar pedido #{traspasarPedido.ticket_numero || traspasarPedido.id}
                </div>
                <div style={{ fontSize: 12, color: COL.muted }}>
                  {traspasarPedido.mesa_nombre || traspasarPedido.mesa_codigo
                    ? `Desde Mesa ${traspasarPedido.mesa_nombre || traspasarPedido.mesa_codigo}`
                    : 'Desde esta ubicacion'}
                </div>
              </div>
              <button onClick={() => setTraspasarPedido(null)} style={{ border: 'none', background: COL.bg, borderRadius: 10, cursor: 'pointer', padding: 8, color: COL.muted }}>
                <FiX size={18} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
              {mesasDestino.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: COL.muted, fontSize: 13 }}>
                  No hay otras mesas disponibles en esta sede.
                </div>
              ) : (
                mesasDestino.map((m) => (
                  <button
                    key={m.id}
                    disabled={traspasando}
                    onClick={() => confirmarTraspasar(m.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: 12,
                      border: `1px solid ${COL.border}`,
                      background: COL.white,
                      cursor: traspasando ? 'default' : 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      marginBottom: 8,
                    }}
                  >
                    <FiHome size={16} color={COL.pri2} />
                    <span style={{ fontWeight: 700, color: COL.text, fontSize: 14 }}>
                      Mesa {m.nombre || m.codigo}
                    </span>
                    {m.capacidad > 0 && (
                      <span style={{ fontSize: 12, color: COL.muted }}>· {m.capacidad} pax</span>
                    )}
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 999,
                        color: m.estado === 'ocupada' ? COL.red : m.estado === 'sucia' ? COL.amber : COL.green,
                        background: m.estado === 'ocupada' ? COL.redBg : m.estado === 'sucia' ? COL.amberBg : COL.greenBg,
                      }}
                    >
                      {m.estado === 'ocupada' ? 'Ocupada' : m.estado === 'sucia' ? 'Sucia' : 'Libre'}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// ═══════════ HISTORIAL ═══════════
function HistorialPedidos({ sede, has, isMobile, abrirTicket }) {
  const { COL } = usePTDColors();
  const { agregarNotificacion } = useNotificaciones();
  const S = useMemo(() => buildStyle(COL), [COL]);
  const [pedidos, setPedidos] = useState([]);
  const [resumen, setResumen] = useState({
    count_total: 0, count_cobrado: 0, count_anulado: 0,
    total_cobrado: 0, total_anulado: 0, ticket_promedio: 0,
  });
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState('');
  const [orden, setOrden] = useState('reciente');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaDebounced, setBusquedaDebounced] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [detalleGrupo, setDetalleGrupo] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [comprobante, setComprobante] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const busquedaTimer = useRef(null);
  useEffect(() => {
    if (busquedaTimer.current) clearTimeout(busquedaTimer.current);
    busquedaTimer.current = setTimeout(() => setBusquedaDebounced(busqueda.trim()), 300);
    return () => { if (busquedaTimer.current) clearTimeout(busquedaTimer.current); };
  }, [busqueda]);

  const ESTADO_META = {
    creado: { label: 'Creado', color: '#64748b', variant: 'neutral' },
    comanda_enviada: { label: 'En cocina', color: '#ea580c', variant: 'warning' },
    armado_cocina: { label: 'Cocina', color: '#ea580c', variant: 'warning' },
    armado: { label: 'Armado', color: '#2563eb', variant: 'info' },
    listo: { label: 'Listo', color: '#0891b2', variant: 'info' },
    servido: { label: 'Servido', color: '#059669', variant: 'info' },
    cobrado: { label: 'Cobrado', color: '#16a34a', variant: 'success' },
    anulado: { label: 'Anulado', color: '#dc2626', variant: 'danger' },
  };
  const metaEstado = (e) => ESTADO_META[e] || { label: e, color: '#64748b', variant: 'neutral' };

  const localISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const buildParams = (extra) => {
    const p = new URLSearchParams();
    p.append('sede', sede);
    p.append('limite', '30');
    if (filtroEstado) p.append('estado', filtroEstado);
    if (busquedaDebounced) p.append('q', busquedaDebounced);
    if (filtroMetodo) p.append('metodo_pago', filtroMetodo);
    if (filtroFecha) {
      const now = new Date();
      if (filtroFecha === 'hoy') {
        p.append('fecha_desde', localISO(now));
        p.append('fecha_hasta', localISO(now) + ' 23:59:59');
      } else if (filtroFecha === 'ayer') {
        const y = new Date(now); y.setDate(y.getDate() - 1);
        p.append('fecha_desde', localISO(y));
        p.append('fecha_hasta', localISO(y) + ' 23:59:59');
      } else if (filtroFecha === '7dias') {
        const y = new Date(now); y.setDate(y.getDate() - 6);
        p.append('fecha_desde', localISO(y));
        p.append('fecha_hasta', localISO(now) + ' 23:59:59');
      } else if (filtroFecha === '30dias') {
        const y = new Date(now); y.setDate(y.getDate() - 29);
        p.append('fecha_desde', localISO(y));
        p.append('fecha_hasta', localISO(now) + ' 23:59:59');
      } else if (filtroFecha === 'personalizado') {
        if (fechaDesde) p.append('fecha_desde', fechaDesde);
        if (fechaHasta) p.append('fecha_hasta', fechaHasta + ' 23:59:59');
      }
    }
    if (extra?.ultimo_id) p.append('ultimo_id', extra.ultimo_id);
    return p;
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get(`/pedidos/tienda/listar?${buildParams().toString()}`);
      setPedidos(r.data?.data || []);
      setResumen(r.data?.resumen || {
        count_total: 0, count_cobrado: 0, count_anulado: 0,
        total_cobrado: 0, total_anulado: 0, ticket_promedio: 0,
      });
      setHasMore(!!r.data?.has_more);
      setLoading(false);
    } catch {
      setLoading(false);
      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sede, filtroEstado, filtroFecha, fechaDesde, fechaHasta, filtroMetodo, busquedaDebounced]);
  useEffect(() => {
    cargar();
  }, [cargar]);

  const cargarMas = async () => {
    if (!pedidos.length || loadingMore) return;
    const last = pedidos[pedidos.length - 1];
    setLoadingMore(true);
    try {
      const r = await api.get(`/pedidos/tienda/listar?${buildParams({ ultimo_id: last.id }).toString()}`);
      const more = r.data?.data || [];
      setPedidos((prev) => [...prev, ...more]);
      setHasMore(!!r.data?.has_more);
    } catch {
      /* ignore */
    } finally {
      setLoadingMore(false);
    }
  };

  const pedidosFiltrados = useMemo(() => {
    const arr = [...pedidos];
    if (orden === 'reciente') {
      arr.sort((a, b) => new Date(b.fechapedido || 0) - new Date(a.fechapedido || 0));
    } else if (orden === 'antiguo') {
      arr.sort((a, b) => new Date(a.fechapedido || 0) - new Date(b.fechapedido || 0));
    } else if (orden === 'monto_mayor') {
      arr.sort((a, b) => parseFloat(b.total || 0) - parseFloat(a.total || 0));
    } else if (orden === 'monto_menor') {
      arr.sort((a, b) => parseFloat(a.total || 0) - parseFloat(b.total || 0));
    }
    return arr;
  }, [pedidos, orden]);

  const hayFiltrosActivos =
    !!filtroEstado || !!filtroFecha || !!fechaDesde || !!fechaHasta || !!filtroMetodo || !!busquedaDebounced || orden !== 'reciente';

  // Agrupa pedidos combinados por grupo_cobro en una sola tarjeta (label "Pedido 2 + 3").
  const pedidosAgrupados = useMemo(() => {
    const result = [];
    const groupIndex = new Map();
    for (const pd of pedidosFiltrados) {
      if (pd.grupo_cobro && groupIndex.has(pd.grupo_cobro)) {
        result[groupIndex.get(pd.grupo_cobro)].pedidos.push(pd);
      } else if (pd.grupo_cobro) {
        groupIndex.set(pd.grupo_cobro, result.length);
        result.push({ key: 'g' + pd.grupo_cobro, pedidos: [pd] });
      } else {
        result.push({ key: 'p' + pd.id, pedidos: [pd] });
      }
    }
    return result;
  }, [pedidosFiltrados]);

  const limpiarFiltros = () => {
    setFiltroEstado('');
    setFiltroFecha('');
    setFechaDesde('');
    setFechaHasta('');
    setFiltroMetodo('');
    setOrden('reciente');
    setBusqueda('');
    setBusquedaDebounced('');
  };

  const metodos = useMemo(() => {
    const set = new Set();
    pedidos.forEach((pd) => {
      (Array.isArray(pd.pagos) ? pd.pagos : []).forEach((pg) => {
        const m = pg.metodo || pg.nombre;
        if (m) set.add(m);
      });
    });
    return Array.from(set).sort();
  }, [pedidos]);

  const anularPedido = (id) => {
    setConfirm({
      title: 'Anular pedido',
      message: 'Indica el motivo de la anulación. El pedido se marcará como anulado y el stock será reintegrado.',
      confirmLabel: 'Anular',
      inputLabel: 'Motivo',
      inputPlaceholder: 'Motivo de la anulación (obligatorio)',
      onConfirm: async (motivo) => {
        setConfirm(null);
        try {
          await api.put(`/pedidos/tienda/${id}/anular`, { motivo });
          cargar();
        } catch (e) {
          agregarNotificacion({
            titulo: 'Error',
            mensaje: e?.response?.data?.detail || 'No se pudo anular el pedido',
            tipo: 'error',
          });
        }
      },
    });
  };

  const anularGrupo = (pedidosArr) => {
    const ids = pedidosArr.map((p) => p.id);
    setConfirm({
      title: `Anular ${ids.length} pedidos combinados`,
      message: `Indica el motivo de la anulación. Se anularán ${ids.length} pedidos y el stock será reintegrado en cada uno.`,
      confirmLabel: 'Anular todo',
      inputLabel: 'Motivo',
      inputPlaceholder: 'Motivo de la anulación (obligatorio)',
      onConfirm: async (motivo) => {
        setConfirm(null);
        try {
          await Promise.all(
            ids.map((id) =>
              api.put(`/pedidos/tienda/${id}/anular`, { motivo })
            )
          );
          setDetalleGrupo(null);
          cargar();
          agregarNotificacion({
            titulo: 'Pedidos anulados',
            mensaje: `${ids.length} pedidos combinados anulados`,
            tipo: 'success',
          });
        } catch (e) {
          agregarNotificacion({
            titulo: 'Error',
            mensaje: e?.response?.data?.detail || 'No se pudo anular el grupo',
            tipo: 'error',
          });
        }
      },
    });
  };

  const abrirDetalle = async (pd) => {
    setDetalle(pd);
    setDetalleLoading(true);
    try {
      const r = await api.get(`/pedidos/tienda/${pd.id}`);
      if (r.data?.status === 'success' && r.data.data) {
        setDetalle({ ...pd, ...r.data.data });
      }
    } catch (e) {
      console.error('Error cargando detalle tienda:', e);
    } finally {
      setDetalleLoading(false);
    }
  };

  const reimprimirPedido = async (pd) => {
    try {
      const r = await api.get(`/pedidos/tienda/${pd.id}/recibo`);
      if (r.data?.data) abrirTicket(r.data.data, 'entrega', false);
    } catch (e) {
      agregarNotificacion({
        titulo: 'Error',
        mensaje: e?.response?.data?.detail || 'No se pudo generar el ticket',
        tipo: 'error',
      });
    }
  };

  const reimprimirGrupo = async (pedidosArr) => {
    const grupo = pedidosArr.find((d) => d.grupo_cobro)?.grupo_cobro;
    try {
      const params = grupo
        ? `grupo_cobro=${encodeURIComponent(grupo)}`
        : `pedido_ids=${pedidosArr.map((d) => d.id).join(',')}`;
      const r = await api.get(`/pedidos/tienda/recibo?${params}`);
      if (r.data?.data) abrirTicket(r.data.data, 'entrega', false);
    } catch (e) {
      agregarNotificacion({
        titulo: 'Error',
        mensaje: e?.response?.data?.detail || 'No se pudo generar el ticket',
        tipo: 'error',
      });
    }
  };

  const abrirDetalleRef = useRef(abrirDetalle);
  useEffect(() => { abrirDetalleRef.current = abrirDetalle; });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('agorax_pedido_abrir');
      if (!raw) return;
      localStorage.removeItem('agorax_pedido_abrir');
      const data = JSON.parse(raw);
      if (data && data.vista === 'tienda' && data.id) {
        abrirDetalleRef.current({ id: data.id });
      }
    } catch {}
  }, []);

  const abrirGrupo = async (pedidosArr) => {
    setDetalleGrupo(null);
    setDetalleLoading(true);
    try {
      const detalles = await Promise.all(
        pedidosArr.map((pd) =>
          api
            .get(`/pedidos/tienda/${pd.id}`)
            .then((r) => (r.data?.status === 'success' && r.data.data ? { ...pd, ...r.data.data } : pd))
            .catch(() => pd)
        )
      );
      detalles.sort((a, b) => (a.id || 0) - (b.id || 0));
      setDetalleGrupo(detalles);
    } catch (e) {
      console.error('Error cargando grupo de pedidos:', e);
    } finally {
      setDetalleLoading(false);
    }
  };

  const countItems = (pd) =>
    (pd.productos || []).reduce((s, i) => s + (Number(i.cantidad) || 1), 0);

  const duracionEntre = (a, b) => {
    if (!a || !b) return null;
    const diff = new Date(b).getTime() - new Date(a).getTime();
    if (isNaN(diff)) return null;
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'menos de 1 min';
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const estadoTabs = [
    { id: '', label: 'Todos' },
    { id: 'cobrado', label: 'Cobrados' },
    { id: 'anulado', label: 'Anulados' },
  ];

  const kpis = [
    { label: 'Total cobrado', value: fmt(resumen.total_cobrado), icon: FiDollarSign, color: COL.green },
    { label: 'Cobrados', value: resumen.count_cobrado, icon: FiCheckCircle, color: COL.green },
    { label: 'Anulados', value: resumen.count_anulado, icon: FiXCircle, color: COL.red },
    { label: 'Ticket promedio', value: fmt(resumen.ticket_promedio), icon: FiTrendingUp, color: COL.pri2 },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header con filtros */}
      <div
        style={{
          padding: '12px 16px',
          background: COL.white,
          borderBottom: `1px solid ${COL.border}`,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: COL.text }}>
            Historial de Pedidos
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COL.muted,
              background: COL.bg,
              padding: '3px 10px',
              borderRadius: 999,
            }}
          >
            {resumen.count_total} en este filtro
          </span>
        </div>

        <HistorialFiltros
          filtros={{
            estado: filtroEstado,
            tipo: '',
            fecha: filtroFecha,
            desde: fechaDesde,
            hasta: fechaHasta,
            metodo: filtroMetodo,
            orden,
            busqueda,
          }}
          set={{
            estado: setFiltroEstado,
            tipo: () => {},
            fecha: setFiltroFecha,
            desde: setFechaDesde,
            hasta: setFechaHasta,
            metodo: setFiltroMetodo,
            orden: setOrden,
            busqueda: setBusqueda,
          }}
          opciones={{ estados: estadoTabs, tipos: null, metodos }}
          onLimpiar={limpiarFiltros}
          hayActivos={hayFiltrosActivos}
          modoModal
        />
      </div>

      {/* KPIs */}
      {!loading && !error && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${isMobile ? 2 : 4}, 1fr)`,
            gap: 10,
            padding: '12px 16px',
            flexShrink: 0,
          }}
        >
          {kpis.map((k, i) => {
            const Icon = k.icon;
            return (
              <div
                key={i}
                style={{
                  background: COL.white,
                  borderRadius: 14,
                  border: `1px solid ${COL.border}`,
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    background: `${k.color}18`,
                    color: k.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={19} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: COL.muted, fontWeight: 600 }}>{k.label}</div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: COL.text,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {k.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
            <SkeletonLoader />
          </div>
        ) : error ? (
          <ErrorBanner message="No se pudo cargar el historial." onRetry={cargar} />
        ) : pedidosFiltrados.length === 0 ? (
          <EmptyState
            icon={FiClock}
            title="Sin historial"
            description={
              busqueda
                ? `Sin resultados para "${busqueda}"`
                : filtroEstado || filtroFecha
                  ? 'No hay pedidos con estos filtros'
                  : 'Los pedidos completados apareceran aqui'
            }
          />
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {pedidosAgrupados.map((g) => {
                const pd = g.pedidos[0];
                const esGrupo = g.pedidos.length > 1;
                const meta = metaEstado(pd.estado);
                const esAnulado = pd.estado === 'anulado';
                const itemsCount = g.pedidos.reduce((s, p) => s + countItems(p), 0);
                const totalGrupo = g.pedidos.reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
                const totalBs = g.pedidos.reduce(
                  (s, p) => s + (parseFloat(p.total) || 0) * tasaBsDe(p),
                  0,
                );
                const tieneBs = g.pedidos.some((p) => tasaBsDe(p) > 0);
                const diferenciaGrupo = g.pedidos.reduce((s, p) => s + (parseFloat(p.diferencia_cobro) || 0), 0);
                const ticketsLabel = g.pedidos.map((p) => p.ticket_numero || p.id).join(' + ');
                const abrir = () =>
                  has('historial_detalle') && (esGrupo ? abrirGrupo(g.pedidos) : abrirDetalle(pd));
                return (
                  <div
                    key={g.key}
                    onClick={abrir}
                    style={{
                      background: COL.white,
                      borderRadius: 14,
                      border: `1px solid ${COL.border}`,
                      borderLeft: `5px solid ${esGrupo ? COL.pri2 : meta.color}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      cursor: has('historial_detalle') ? 'pointer' : 'default',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ padding: '10px 14px 8px' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontSize: 15, fontWeight: 800, color: COL.pri }}>
                          #{esGrupo ? ticketsLabel : pd.ticket_numero || pd.id}
                        </span>
                        <Badge label={meta.label} variant={meta.variant} dot />
                        {esGrupo && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 11,
                              fontWeight: 800,
                              color: COL.pri2,
                              background: COL.blueBg,
                              padding: '2px 9px',
                              borderRadius: 999,
                            }}
                          >
                            <FiLayers size={11} /> {g.pedidos.length} pedidos combinados
                          </span>
                        )}
                        {pd.mesa_nombre && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 11,
                              fontWeight: 700,
                              color: COL.amber,
                              background: COL.amberBg,
                              padding: '2px 9px',
                              borderRadius: 999,
                            }}
                          >
                            <FiMapPin size={11} /> Mesa {pd.mesa_nombre}
                          </span>
                        )}
                        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: esAnulado ? COL.faint : COL.text }}>
                            {fmt(esGrupo ? totalGrupo : pd.total)}
                          </div>
                          {tieneBs && totalBs > 0 && (
                            <div style={{ fontSize: 11, fontWeight: 600, color: COL.muted }}>
                              ≈ Bs {fmtMonto(totalBs)}
                            </div>
                          )}
                          {diferenciaGrupo < -0.005 && (
                            <div style={{ fontSize: 11, fontWeight: 700, color: COL.red, marginTop: 1 }}>
                              Falta {fmt(Math.abs(diferenciaGrupo))}
                            </div>
                          )}
                          {diferenciaGrupo > 0.005 && (
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', marginTop: 1 }}>
                              Sobrante {fmt(diferenciaGrupo)}
                            </div>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flexWrap: 'wrap',
                          marginTop: 5,
                          fontSize: 11,
                          color: COL.muted,
                        }}
                      >
                        {pd.cliente_nombre && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FiUser size={11} /> {pd.cliente_nombre}
                          </span>
                        )}
                        {pd.cliente_telefono && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FiPhone size={11} /> {pd.cliente_telefono}
                          </span>
                        )}
                        {pd.cliente_cedula && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FiHash size={11} /> {pd.cliente_cedula}
                          </span>
                        )}
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <FiPackage size={11} /> {itemsCount} items
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <FiCalendar size={11} /> {formatearFecha(pd.fechapedido, true)}
                        </span>
                        {pd.hora_cobro && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FiClock size={11} /> {new Date(pd.hora_cobro).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>

                      {(() => {
                        const pagosChip = esGrupo
                          ? g.pedidos.find((p) => (p.pagos || []).length > 0)?.pagos || []
                          : pd.pagos || [];
                        return pagosChip.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                            {pagosChip.map((pg, i) => (
                              <span
                                key={i}
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: COL.muted,
                                  background: COL.bg,
                                  border: `1px solid ${COL.border}`,
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                }}
                              >
                                {pg.metodo || 'Pago'} · {fmt(pg.monto)}
                              </span>
                            ))}
                          </div>
                        ) : null;
                      })()}
                    </div>

                    {(has('historial_detalle') || has('historial_anular')) && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          padding: '5px 14px',
                          borderTop: `1px solid ${COL.line || COL.border}`,
                          background: COL.bg,
                        }}
                      >
                        {has('historial_detalle') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              abrir();
                            }}
                            style={{
                              ...S.btnSm(COL.pri2),
                              background: COL.white,
                            }}
                          >
                            <FiEye size={13} /> Ver detalle
                          </button>
                        )}
                        {has('historial_anular') && pd.estado !== 'anulado' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (esGrupo) anularGrupo(g.pedidos);
                              else anularPedido(pd.id);
                            }}
                            style={{
                              ...S.btnSm(COL.red),
                              background: COL.white,
                            }}
                          >
                            <FiXCircle size={13} /> {esGrupo ? `Anular (${g.pedidos.length})` : 'Anular'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 4px' }}>
                <button
                  onClick={cargarMas}
                  disabled={loadingMore}
                  style={{
                    padding: '10px 22px',
                    borderRadius: 12,
                    border: `1.5px solid ${COL.pri2}`,
                    background: 'transparent',
                    color: COL.pri2,
                    cursor: loadingMore ? 'wait' : 'pointer',
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: 'inherit',
                  }}
                >
                  {loadingMore ? 'Cargando...' : 'Cargar más'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {detalle && (
        <div
          onClick={(e) => e.target === e.currentTarget && setDetalle(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: COL.overlay,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            style={{
              background: COL.surface,
              borderRadius: 20,
              width: isMobile ? '92%' : 480,
              maxHeight: '88vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            {(() => {
              const meta = metaEstado(detalle.estado);
              return (
                <div
                  style={{
                    padding: '18px 20px',
                    background: `linear-gradient(135deg, ${meta.color}, ${meta.color}dd)`,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {detalle.mesa_nombre && (
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          opacity: 0.92,
                        }}
                      >
                        <FiMapPin size={13} /> Mesa {detalle.mesa_nombre}
                      </div>
                    )}
                    <div style={{ fontSize: 19, fontWeight: 800, marginTop: 2 }}>
                      Pedido #{detalle.ticket_numero || detalle.id}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.92, marginTop: 2 }}>
                      {detalle.cliente_nombre || 'Sin cliente'} · {formatearFecha(detalle.fechapedido, true)}
                    </div>
                    {(detalle.cliente_telefono || detalle.cliente_cedula) && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                          fontSize: 12,
                          opacity: 0.92,
                          marginTop: 4,
                          flexWrap: 'wrap',
                        }}
                      >
                        {detalle.cliente_telefono && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <FiPhone size={12} /> {detalle.cliente_telefono}
                          </span>
                        )}
                        {detalle.cliente_cedula && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <FiHash size={12} /> {detalle.cliente_cedula}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>TOTAL</div>
                    <div style={{ fontSize: 24, fontWeight: 800 }}>{fmt(detalle.total)}</div>
                  </div>
                  <button
                    onClick={() => reimprimirPedido(detalle)}
                    title="Reimprimir ticket"
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#fff',
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FiPrinter size={18} />
                  </button>
                  <button
                    onClick={() => setDetalle(null)}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#fff',
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FiX size={18} />
                  </button>
                </div>
              );
            })()}

            <div style={{ padding: '16px 20px' }}>
              {detalleLoading ? (
                <div style={{ textAlign: 'center', padding: 24, color: COL.muted }}>
                  Cargando detalle...
                </div>
              ) : (
                <>
                  {/* Responsables */}
                  {(detalle.mesero_nombre || detalle.cajero_nombre || detalle.mesa_nombre) && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        marginBottom: 14,
                      }}
                    >
                      {detalle.mesa_nombre && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: COL.amber,
                            background: COL.amberBg,
                            padding: '4px 10px',
                            borderRadius: 999,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <FiMapPin size={11} /> Mesa {detalle.mesa_nombre}
                        </span>
                      )}
                      {detalle.mesero_nombre && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: COL.pri2,
                            background: COL.blueBg,
                            padding: '4px 10px',
                            borderRadius: 999,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <FiUser size={11} /> Mesero: {detalle.mesero_nombre}
                        </span>
                      )}
                      {detalle.cajero_nombre && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: COL.green,
                            background: COL.greenBg,
                            padding: '4px 10px',
                            borderRadius: 999,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <FiCreditCard size={11} /> Cajero: {detalle.cajero_nombre}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Totales */}
                  <div
                    style={{
                      background: COL.bg,
                      borderRadius: 12,
                      padding: '12px 14px',
                      marginBottom: 14,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: COL.muted }}>
                      <span>Total</span>
                      <span style={{ fontWeight: 800, color: COL.text }}>{fmt(detalle.total)}</span>
                    </div>
                    {detalle.propina > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.green }}>
                        <span>Propina{detalle.propina_destinatario ? ` → ${detalle.propina_destinatario}` : ''}</span>
                        <span style={{ fontWeight: 700 }}>{fmt(detalle.propina)}</span>
                      </div>
                    )}
                    {(parseFloat(detalle.diferencia_cobro) || 0) < -0.005 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.red }}>
                        <span>Falta de pago</span>
                        <span style={{ fontWeight: 800 }}>-{fmt(Math.abs(detalle.diferencia_cobro))}</span>
                      </div>
                    )}
                    {(parseFloat(detalle.diferencia_cobro) || 0) > 0.005 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: '#d97706' }}>
                        <span>Sobrante (no propina)</span>
                        <span style={{ fontWeight: 800 }}>{fmt(detalle.diferencia_cobro)}</span>
                      </div>
                    )}
                    {!((parseFloat(detalle.diferencia_cobro) || 0) > 0.005) && detalle.cobrado_de_mas_sin_propina && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.amber }}>
                        <span>Exceso sin propina</span>
                        <span style={{ fontWeight: 700 }}>{fmt(detalle.exceso_cobrado)}</span>
                      </div>
                    )}
                    {detalle.monto_recibido != null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.muted }}>
                        <span>Recibido</span>
                        <span>{fmt(detalle.monto_recibido)}</span>
                      </div>
                    )}
                    {detalle.vuelto > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.muted }}>
                        <span>Vuelto</span>
                        <span>{fmt(detalle.vuelto)}</span>
                      </div>
                    )}
                    {detalle.hora_cobro && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.muted }}>
                        <span>Hora de cobro</span>
                        <span>{new Date(detalle.hora_cobro).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    )}
                  </div>

                  {/* Alerta: falta de pago */}
                  {(parseFloat(detalle.diferencia_cobro) || 0) < -0.005 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: COL.red,
                        borderRadius: 10,
                        padding: '10px 12px',
                        marginBottom: 14,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <FiAlertTriangle size={15} style={{ flexShrink: 0 }} />
                      <span>
                        Falta por pagar {fmt(Math.abs(detalle.diferencia_cobro))}. El pedido no se cobró en su totalidad.
                      </span>
                    </div>
                  )}

                  {/* Alerta: cobrado de más sin registrar propina */}
                  {detalle.cobrado_de_mas_sin_propina && (parseFloat(detalle.diferencia_cobro) || 0) <= 0.005 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: COL.amberBg,
                        border: `1px solid ${COL.amber}`,
                        color: COL.amber,
                        borderRadius: 10,
                        padding: '10px 12px',
                        marginBottom: 14,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <FiAlertCircle size={15} style={{ flexShrink: 0 }} />
                      <span>
                        Se cobró {fmt(detalle.exceso_cobrado)} de más y no se registró como propina.
                        Quedó como vuelto.
                      </span>
                    </div>
                  )}

                  {/* Cobro combinado: pedidos vinculados por el mismo grupo */}
                  {(detalle.pedidos_relacionados || []).length > 0 && (
                    <div
                      style={{
                        background: COL.blueBg,
                        border: `1px solid ${COL.pri2}33`,
                        borderRadius: 12,
                        padding: '12px 14px',
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 12,
                          fontWeight: 800,
                          color: COL.pri2,
                          marginBottom: 8,
                        }}
                      >
                        <FiLayers size={13} /> Cobro combinado con{' '}
                        {detalle.pedidos_relacionados.length}{' '}
                        {detalle.pedidos_relacionados.length === 1 ? 'pedido' : 'pedidos'}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {detalle.pedidos_relacionados.map((rp) => (
                          <button
                            key={rp.id}
                            onClick={() => {
                              setDetalle(null);
                              setTimeout(() => abrirDetalle({ id: rp.id, ticket_numero: rp.ticket_numero, total: rp.total, mesa_nombre: rp.mesa_nombre }), 50);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                              background: COL.white,
                              border: `1px solid ${COL.border}`,
                              borderRadius: 10,
                              padding: '9px 12px',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              textAlign: 'left',
                              width: '100%',
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                              <FiHash size={13} color={COL.pri2} />
                              <span style={{ fontWeight: 700, fontSize: 13, color: COL.text }}>
                                Pedido #{rp.ticket_numero || rp.id}
                              </span>
                              {rp.mesa_nombre && (
                                <span style={{ fontSize: 11, color: COL.muted }}>
                                  · Mesa {rp.mesa_nombre}
                                </span>
                              )}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                              <span style={{ fontWeight: 800, fontSize: 13, color: COL.text }}>{fmt(rp.total)}</span>
                              <FiChevronRight size={14} color={COL.faint} />
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pagos con comprobantes */}
                  {(detalle.pagos || []).length > 0 && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted, marginBottom: 8 }}>
                        Pagos
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                        {(detalle.pagos || []).map((p, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 8,
                              padding: '9px 12px',
                              borderRadius: 10,
                              background: COL.greenBg,
                              fontSize: 13,
                              color: COL.text,
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <span style={{ fontWeight: 700 }}>{p.metodo || p.nombre || 'Pago'}</span>
                              {p.referencia && (
                                <span style={{ fontSize: 11, color: COL.muted }}>
                                  Ref: {p.referencia}
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              <span style={{ fontWeight: 700 }}>{fmt(p.monto)}</span>
                              {p.comprobante_url && (
                                <button
                                  onClick={() => setComprobante({ url: p.comprobante_url, id: detalle.id })}
                                  style={{
                                    border: 'none',
                                    background: COL.white,
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    padding: 5,
                                    color: COL.pri2,
                                    display: 'flex',
                                  }}
                                  title="Ver comprobante"
                                >
                                  <FiImage size={15} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Productos cobrados */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted, marginBottom: 8 }}>
                    Artículos cobrados ({countItems(detalle)})
                  </div>
                  {(detalle.productos || []).map((item, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        padding: '8px 0',
                        borderBottom: `1px solid ${COL.border}`,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: COL.text, wordBreak: 'break-word' }}>
                          {item.nombre || item.codigo}
                        </span>
                        <span style={{ fontSize: 13, color: COL.muted, marginLeft: 6 }}>
                          x{item.cantidad}
                        </span>
                        {(item.etiquetas || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 3 }}>
                            {item.etiquetas.map((et, ei) => (
                              <span
                                key={ei}
                                style={{
                                  fontSize: 9,
                                  fontWeight: 600,
                                  background: COL.tagOrangeBg,
                                  color: COL.tagOrangeText,
                                  padding: '1px 5px',
                                  borderRadius: 3,
                                }}
                              >
                                {et.nombre}
                                {et.precio > 0 ? ` +${fmt(et.precio)}` : ''}
                              </span>
                            ))}
                          </div>
                        )}
                        {item.combo_id &&
                          Array.isArray(item.componentes) &&
                          item.componentes.length > 0 && (
                            <div style={{ marginTop: 4, paddingLeft: 2 }}>
                              {item.componentes.map((comp, ci) => {
                                const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
                                return (
                                  <div
                                    key={ci}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 5,
                                      fontSize: 11,
                                      color: COL.muted,
                                      padding: '1px 0',
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        width: 4,
                                        height: 4,
                                        borderRadius: '50%',
                                        backgroundColor: '#f0b400',
                                        flexShrink: 0,
                                      }}
                                    />
                                    <span style={{ color: COL.text }}>{comp.nombre}</span>
                                    {compEtiq.length > 0 && (
                                      <span style={{ color: COL.faint }}>
                                        ({compEtiq.map((e) => e.nombre).join(', ')})
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                      </div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: COL.text, flexShrink: 0 }}>
                        {fmt((item.precio_unitario || 0) * (item.cantidad || 1))}
                      </span>
                    </div>
                  ))}

                  {/* Línea de tiempo con responsable y tiempos entre fases */}
                  {Array.isArray(detalle.historial) && detalle.historial.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted, marginTop: 16, marginBottom: 8 }}>
                        Línea de tiempo
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {detalle.historial.map((h, i) => {
                          const meta = metaEstado(h.estado_nuevo);
                          const prev = detalle.historial[i - 1];
                          const dur = prev ? duracionEntre(prev.creado_en, h.creado_en) : null;
                          return (
                            <div key={h.id || i} style={{ display: 'flex', gap: 10 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: meta.color, marginTop: 3 }} />
                                {i < detalle.historial.length - 1 && (
                                  <span style={{ width: 2, flex: 1, backgroundColor: COL.border, marginTop: 2 }} />
                                )}
                              </div>
                              <div style={{ paddingBottom: 14, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: COL.text }}>
                                  {meta.label}
                                  {h.nota ? <span style={{ fontWeight: 400, color: COL.muted }}> — {h.nota}</span> : null}
                                </div>
                                <div style={{ fontSize: 11, color: COL.faint, marginTop: 1 }}>
                                  {formatearFecha(h.creado_en, true)}
                                  {h.actor_nombre ? ` · ${h.actor_nombre}` : h.actor_cedula ? ` · ${h.actor_cedula}` : ''}
                                  {h.actor_rol ? ` (${h.actor_rol})` : ''}
                                </div>
                                {dur && (
                                  <div style={{ fontSize: 10, color: COL.pri2, fontWeight: 600, marginTop: 1 }}>
                                    ⏱ {dur}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de cobro combinado: varios pedidos en la misma ventana */}
      {detalleGrupo && Array.isArray(detalleGrupo) && detalleGrupo.length > 0 && (
        <div
          onClick={(e) => e.target === e.currentTarget && setDetalleGrupo(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: COL.overlay,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            style={{
              background: COL.surface,
              borderRadius: 20,
              width: isMobile ? '92%' : 480,
              maxHeight: '88vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            {(() => {
              const tickets = detalleGrupo.map((d) => d.ticket_numero || d.id).join(' + ');
              const totalG = detalleGrupo.reduce((s, d) => s + (parseFloat(d.total) || 0), 0);
              const pagosG = detalleGrupo.find((d) => (d.pagos || []).length > 0)?.pagos || [];
              const principal = detalleGrupo.find((d) => (d.pagos || []).length > 0) || detalleGrupo[0];
              const mesaG = detalleGrupo.map((d) => d.mesa_nombre).filter(Boolean)[0];
              const clienteG = detalleGrupo.map((d) => d.cliente_nombre).filter(Boolean)[0];
              const telG = detalleGrupo.map((d) => d.cliente_telefono).filter(Boolean)[0];
              const cedG = detalleGrupo.map((d) => d.cliente_cedula).filter(Boolean)[0];
              const meseroG = detalleGrupo.map((d) => d.mesero_nombre).filter(Boolean)[0];
              const cajeroG = detalleGrupo.map((d) => d.cajero_nombre).filter(Boolean)[0];
              const itemsTotal = detalleGrupo.reduce((s, d) => s + countItems(d), 0);
              return (
                <>
                  <div
                    style={{
                      padding: '18px 20px',
                      background: `linear-gradient(135deg, ${COL.pri2}, ${COL.pri2}dd)`,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                        <FiLayers size={13} /> Cobro combinado
                        {mesaG && <span> · Mesa {mesaG}</span>}
                      </div>
                      <div style={{ fontSize: 19, fontWeight: 800, marginTop: 2 }}>Pedido {tickets}</div>
                      <div style={{ fontSize: 12, opacity: 0.92, marginTop: 2 }}>
                        {detalleGrupo.length} pedidos · {itemsTotal} artículos · {formatearFecha(detalleGrupo[0].fechapedido, true)}
                      </div>
                      {clienteG && (
                        <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <FiUser size={13} /> {clienteG}
                          {telG && <span style={{ fontWeight: 500, opacity: 0.9 }}>· {telG}</span>}
                          {cedG && <span style={{ fontWeight: 500, opacity: 0.9 }}>· {cedG}</span>}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>TOTAL</div>
                      <div style={{ fontSize: 24, fontWeight: 800 }}>{fmt(totalG)}</div>
                    </div>
                    <button
                      onClick={() => reimprimirGrupo(detalleGrupo)}
                      title="Reimprimir ticket"
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#fff',
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FiPrinter size={18} />
                    </button>
                    <button
                      onClick={() => setDetalleGrupo(null)}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#fff',
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FiX size={18} />
                    </button>
                  </div>

                  <div style={{ padding: '16px 20px' }}>
                    {detalleLoading ? (
                      <div style={{ textAlign: 'center', padding: 24, color: COL.muted }}>Cargando detalle...</div>
                    ) : (
                      <>
                        {(meseroG || cajeroG) && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                            {meseroG && (
                              <span style={{ fontSize: 11, fontWeight: 600, color: COL.pri2, background: COL.blueBg, padding: '4px 10px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <FiUser size={11} /> Mesero: {meseroG}
                              </span>
                            )}
                            {cajeroG && (
                              <span style={{ fontSize: 11, fontWeight: 600, color: COL.green, background: COL.greenBg, padding: '4px 10px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <FiCreditCard size={11} /> Cajero: {cajeroG}
                              </span>
                            )}
                          </div>
                        )}

                        {detalleGrupo.map((d, gi) => (
                          <div
                            key={d.id}
                            style={{
                              marginBottom: gi < detalleGrupo.length - 1 ? 16 : 0,
                              border: `1px solid ${COL.border}`,
                              borderRadius: 12,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                padding: '10px 14px',
                                background: COL.bg,
                                borderBottom: `1px solid ${COL.border}`,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
                                <FiHash size={13} color={COL.pri2} />
                                <span style={{ fontWeight: 800, fontSize: 14, color: COL.text }}>
                                  Pedido #{d.ticket_numero || d.id}
                                </span>
                                {d.mesa_nombre && (
                                  <span style={{ fontSize: 11, color: COL.muted }}>· Mesa {d.mesa_nombre}</span>
                                )}
                                {(() => {
                                  const m = metaEstado(d.estado);
                                  return (
                                    <Badge label={m.label} variant={m.variant} dot />
                                  );
                                })()}
                              </div>
                              <span style={{ fontWeight: 800, fontSize: 14, color: COL.text, flexShrink: 0 }}>
                                {fmt(d.total)}
                              </span>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                flexWrap: 'wrap',
                                padding: '4px 14px 8px',
                                fontSize: 11,
                                color: COL.muted,
                              }}
                            >
                              {d.cliente_nombre && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <FiUser size={11} /> {d.cliente_nombre}
                                </span>
                              )}
                              {d.cliente_telefono && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <FiPhone size={11} /> {d.cliente_telefono}
                                </span>
                              )}
                              {d.hora_cobro && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <FiClock size={11} /> {new Date(d.hora_cobro).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <FiPackage size={11} /> {countItems(d)} artículos
                              </span>
                            </div>
                            <div style={{ padding: '6px 14px' }}>
                              {(d.productos || []).length === 0 ? (
                                <div style={{ padding: '10px 0', fontSize: 12, color: COL.faint }}>Sin artículos</div>
                              ) : (
                                (d.productos || []).map((item, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                      padding: '7px 0',
                                      borderBottom: i < (d.productos || []).length - 1 ? `1px solid ${COL.border}` : 'none',
                                    }}
                                  >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <span style={{ fontWeight: 600, fontSize: 13, color: COL.text, wordBreak: 'break-word' }}>
                                        {item.nombre || item.codigo}
                                      </span>
                                      <span style={{ fontSize: 13, color: COL.muted, marginLeft: 6 }}>x{item.cantidad}</span>
                                      {(item.etiquetas || []).length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 3 }}>
                                          {item.etiquetas.map((et, ei) => (
                                            <span
                                              key={ei}
                                              style={{
                                                fontSize: 9,
                                                fontWeight: 600,
                                                background: COL.tagOrangeBg,
                                                color: COL.tagOrangeText,
                                                padding: '1px 5px',
                                                borderRadius: 3,
                                              }}
                                            >
                                              {et.nombre}
                                              {et.precio > 0 ? ` +${fmt(et.precio)}` : ''}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <span style={{ fontWeight: 700, fontSize: 13, color: COL.text, flexShrink: 0 }}>
                                      {fmt((item.precio_unitario || 0) * (item.cantidad || 1))}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Pago del cobro combinado */}
                        <div style={{ marginTop: 18 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted, marginBottom: 8 }}>Pago</div>
                          <div
                            style={{
                              background: COL.bg,
                              borderRadius: 12,
                              padding: '12px 14px',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: COL.muted }}>
                              <span>Total</span>
                              <span style={{ fontWeight: 800, color: COL.text }}>{fmt(totalG)}</span>
                            </div>
                            {principal.monto_recibido != null && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.muted }}>
                                <span>Recibido</span>
                                <span>{fmt(principal.monto_recibido)}</span>
                              </div>
                            )}
                            {principal.vuelto > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.muted }}>
                                <span>Vuelto</span>
                                <span>{fmt(principal.vuelto)}</span>
                              </div>
                            )}
                            {principal.hora_cobro && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.muted }}>
                                <span>Hora de cobro</span>
                                <span>{new Date(principal.hora_cobro).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            )}
                          </div>

                          {(pagosG || []).length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                              {pagosG.map((p, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '9px 12px',
                                    borderRadius: 10,
                                    background: COL.greenBg,
                                    fontSize: 13,
                                    color: COL.text,
                                  }}
                                >
                                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                    <span style={{ fontWeight: 700 }}>{p.metodo || p.nombre || 'Pago'}</span>
                                    {p.referencia && (
                                      <span style={{ fontSize: 11, color: COL.muted }}>Ref: {p.referencia}</span>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    <span style={{ fontWeight: 700 }}>{fmt(p.monto)}</span>
                                    {p.comprobante_url && (
                                      <button
                                        onClick={() => setComprobante({ url: p.comprobante_url, id: principal.id })}
                                        style={{
                                          border: 'none',
                                          background: COL.white,
                                          borderRadius: 8,
                                          cursor: 'pointer',
                                          padding: 5,
                                          color: COL.pri2,
                                          display: 'flex',
                                        }}
                                        title="Ver comprobante"
                                      >
                                        <FiImage size={15} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {has('historial_anular') && detalleGrupo.some((d) => d.estado !== 'anulado') && (
                          <button
                            onClick={() => anularGrupo(detalleGrupo)}
                            style={{
                              marginTop: 18,
                              width: '100%',
                              border: `1.5px solid ${COL.red}`,
                              background: 'transparent',
                              color: COL.red,
                              borderRadius: 12,
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 800,
                              fontFamily: 'inherit',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 6,
                            }}
                          >
                            <FiXCircle size={15} /> Anular {detalleGrupo.length} pedidos combinados
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Visor de comprobante */}
      {comprobante && (
        <div
          onClick={() => setComprobante(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COL.surface,
              borderRadius: 16,
              width: '100%',
              maxWidth: 520,
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: `1px solid ${COL.border}` }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: COL.text }}>
                Comprobante #{comprobante.id}
              </span>
              <button onClick={() => setComprobante(null)} style={{ border: 'none', background: COL.bg, borderRadius: 8, cursor: 'pointer', padding: 8, color: COL.muted, display: 'flex' }}>
                <FiX size={16} />
              </button>
            </div>
            <img
              src={getImagenUrl(comprobante.url)}
              alt="Comprobante"
              style={{ width: '100%', display: 'block', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          inputLabel={confirm.inputLabel}
          inputPlaceholder={confirm.inputPlaceholder}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function ModalAbrirCaja({ onClose, onAbierta }) {
  const [baseUsd, setBaseUsd] = useState('');
  const [baseBs, setBaseBs] = useState('');
  const [abriendo, setAbriendo] = useState(false);

  const abrir = async () => {
    const usd = parseFloat(baseUsd) || 0;
    const bs = parseFloat(baseBs) || 0;
    setAbriendo(true);
    try {
      const fd = new FormData();
      fd.append('base_inicial_usd', usd);
      fd.append('base_inicial_bs', bs);
      await api.post('/cierre-caja/abrir', fd);
      onAbierta();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al abrir caja');
    } finally { setAbriendo(false); }
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 360,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <FiLock size={20} color="#f59e0b" />
          <span style={{ fontSize: 16, fontWeight: 800, color: '#1F2937' }}>Abrir Caja</span>
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 16 }}>
          Debes abrir caja antes de poder cobrar pedidos
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Base inicial USD ($)</label>
          <input type="number" value={baseUsd} onChange={(e) => setBaseUsd(e.target.value)}
            placeholder="0.00" style={{ width: '100%', padding: '10px 12px', borderRadius: 10,
              border: '1px solid #e5e7eb', fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
              boxSizing: 'border-box', outline: 'none' }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Base inicial Bs</label>
          <input type="number" value={baseBs} onChange={(e) => setBaseBs(e.target.value)}
            placeholder="0.00" style={{ width: '100%', padding: '10px 12px', borderRadius: 10,
              border: '1px solid #e5e7eb', fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
              boxSizing: 'border-box', outline: 'none' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: 10,
            border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#6B7280' }}>
            Cancelar
          </button>
          <button onClick={abrir} disabled={abriendo} style={{ flex: 1, padding: '12px', borderRadius: 10,
            border: 'none', background: '#34a853',
            color: '#fff', fontSize: 13, fontWeight: 700, cursor: abriendo ? 'wait' : 'pointer',
            fontFamily: 'inherit' }}>
            {abriendo ? 'Abriendo...' : 'Abrir Caja'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PagoComprobante({ pago, onUploaded }) {
  const [subiendo, setSubiendo] = useState(false);
  const [mostrando, setMostrando] = useState(false);
  const { COL } = usePTDColors();
  const fileRef = useRef();
  const tieneComprobanteRef = useRef(false);
  useEffect(() => { tieneComprobanteRef.current = !!pago.comprobante_url; }, [pago.comprobante_url]);

  const subirArchivo = useCallback(async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const r = await api.post('/pedidos/tienda/subir-comprobante', fd);
      onUploaded(r.data?.data?.url || '');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error al subir comprobante';
      alert(msg);
    } finally {
      setSubiendo(false);
    }
  }, [onUploaded]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) subirArchivo(file);
    e.target.value = '';
  };

  const pegarComprobante = async () => {
    if (subiendo) return;
    if (tieneComprobanteRef.current) return;
    if (!navigator.clipboard || typeof navigator.clipboard.read !== 'function') {
      alert('Tu navegador no permite leer el portapapeles. Pega con Ctrl+V o usa "Subir comprobante".');
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) {
        const imgType = (it.types || []).find((t) => t.startsWith('image/'));
        if (imgType) {
          const blob = await it.getType(imgType);
          const ext = (imgType.split('/')[1] || 'png').replace('jpeg', 'jpg');
          const file = new File([blob], `comprobante_${Date.now()}.${ext}`, { type: imgType });
          await subirArchivo(file);
          return;
        }
      }
      alert('No hay imagen en el portapapeles. Copia una imagen (Ctrl+C) y vuelve a intentarlo.');
    } catch (err) {
      if (err && err.name === 'NotAllowedError') {
        alert('Permiso de portapapeles denegado. Vuelve a intentarlo y permite el acceso, o pega con Ctrl+V.');
      } else {
        alert('No se pudo leer el portapapeles. Pega la imagen con Ctrl+V o súbela manualmente.');
      }
    }
  };

  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData && e.clipboardData.items;
      if (!items) return;
      for (const it of items) {
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f && !tieneComprobanteRef.current) {
            e.preventDefault();
            subirArchivo(f);
          }
          return;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [subirArchivo]);

  const btnBase = {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', borderRadius: 8, border: 'none',
    cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
    transition: 'all 0.15s',
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="file" accept="image/*,.pdf" capture="environment" style={{ display: 'none' }}
          ref={fileRef} onChange={handleFile} />
        {pago.comprobante_url ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: COL.green }}>
              <FiCheck size={12} />
              Subido
            </span>
            <button onClick={() => setMostrando(true)}
              style={{
                ...btnBase,
                background: `${COL.pri2}15`, color: COL.pri2,
              }}>
              <FiEye size={13} />
              Ver
            </button>
            <button onClick={() => onUploaded('')}
              style={{
                ...btnBase,
                background: 'var(--agx-red-bg)', color: COL.red,
                padding: '6px 8px',
              }}>
              <FiX size={13} />
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={pegarComprobante} disabled={subiendo}
              style={{
                ...btnBase,
                background: `${COL.pri2}15`, color: COL.pri2,
              }}>
              <FiClipboard size={13} />
              {subiendo ? 'Pegando...' : 'Pegar comprobante'}
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={subiendo}
              style={{
                ...btnBase,
                border: `1.5px dashed ${COL.border}`, background: COL.white,
                color: COL.muted,
              }}>
              <FiCamera size={13} />
              {subiendo ? 'Subiendo...' : 'Subir comprobante'}
            </button>
          </div>
        )}
      </div>

      {mostrando && (
        <div onClick={() => setMostrando(false)} style={{
          position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(4px)',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 16, overflow: 'hidden',
            maxWidth: '90vw', maxHeight: '90vh', position: 'relative',
          }}>
            <button onClick={() => setMostrando(false)} style={{
              position: 'absolute', top: 10, right: 10, zIndex: 1,
              width: 32, height: 32, borderRadius: 8,
              border: 'none', background: 'rgba(0,0,0,0.5)',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FiX size={18} />
            </button>
            <img src={getImagenUrl(pago.comprobante_url)}
              alt="Comprobante" style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════ ESTILOS ═══════════
function buildStyle(C) {
  return {
    chip: (active) => ({
      padding: '6px 14px',
      borderRadius: 20,
      border: `1.5px solid ${active ? C.pri2 : C.border}`,
      fontSize: 12,
      fontWeight: active ? 600 : 400,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      fontFamily: 'inherit',
      background: active ? C.pri2 : C.surfaceAlt,
      color: active ? '#fff' : C.muted,
      transition: 'all 0.15s',
    }),
    qtyBtn: {
      width: 32,
      height: 32,
      borderRadius: 8,
      border: `1px solid ${C.border}`,
      background: C.surfaceAlt,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: C.pri2,
    },
    zoomBtn: {
      padding: '5px 10px',
      borderRadius: 8,
      border: `1px solid ${C.border}`,
      background: C.surfaceAlt,
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: 14,
      fontWeight: 700,
      color: C.text,
    },
    formLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      color: C.muted,
      marginBottom: 6,
    },
    formInput: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 10,
      border: `1px solid ${C.border}`,
      fontSize: 14,
      fontFamily: 'inherit',
      boxSizing: 'border-box',
      outline: 'none',
      background: C.surfaceAlt,
      color: C.text,
    },
    btnFull: (c) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      width: '100%',
      padding: '13px 16px',
      borderRadius: 12,
      border: 'none',
      background: c,
      color: '#fff',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 700,
      fontFamily: 'inherit',
    }),
    btnPrimary: (disabled) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      width: '100%',
      padding: 12,
      borderRadius: 12,
      border: 'none',
      background: disabled ? C.border : C.pri,
      color: '#fff',
      fontSize: 14,
      fontWeight: 600,
      cursor: disabled ? 'default' : 'pointer',
      fontFamily: 'inherit',
      boxShadow: disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.12)',
    }),
    btnOutline: (c) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      width: '100%',
      padding: 10,
      borderRadius: 10,
      border: `1px solid ${C.border}`,
      background: 'transparent',
      color: c,
      cursor: 'pointer',
      fontSize: 12,
      fontWeight: 600,
      fontFamily: 'inherit',
    }),
    btnSm: (c) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 14px',
      borderRadius: 8,
      border: `1px solid ${C.border}`,
      background: C.surfaceAlt,
      color: c,
      cursor: 'pointer',
      fontSize: 12,
      fontWeight: 500,
      fontFamily: 'inherit',
    }),
    iconBtn: (c) => ({
      width: 32,
      height: 32,
      borderRadius: 8,
      border: `1px solid ${C.border}`,
      background: C.surfaceAlt,
      color: c,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'inherit',
    }),
    th: {
      padding: '10px 12px',
      textAlign: 'left',
      fontWeight: 600,
      color: C.muted,
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
    },
    td: { padding: '10px 12px', color: C.text, fontSize: 12 },
  };
}

